/*
================================================================================
🚨 CRITICAL ENVIRONMENT CONFIGURATION - READ FIRST! 🚨
================================================================================

📍 CURRENT DEPLOYMENT STATUS:
   - Frontend URL: https://login-tracking-hub.preview.emergentagent.com
   - Platform Admin Email: admin@hoaplatform.com
   - Platform Admin Password: j9sahdcujd$J
   - VERIFIED WORKING: Platform admin login functional as of last test

🔧 BACKEND URL CONFIGURATION GUIDE:

1. KUBERNETES/CLUSTER DEPLOYMENTS (CURRENT SETUP):
   ✅ Set: REACT_APP_BACKEND_URL= (empty)
   ✅ Result: Uses relative URLs (/api/auth/login) 
   ✅ Works with: Cluster ingress routing /api/* to backend
   ✅ Current Status: WORKING

2. LOCAL DEVELOPMENT:
   Set: REACT_APP_BACKEND_URL=http://localhost:8001
   Result: Direct connection to local backend
   
3. EXTERNAL BACKEND:
   Set: REACT_APP_BACKEND_URL=https://your-backend-domain.com
   Result: Absolute URLs to external backend
   NOTE: Requires CORS configuration on backend

🛠️ HOW THE CODE WORKS:
   - Line ~37: axios baseURL set only if REACT_APP_BACKEND_URL exists
   - Line ~42: window.performLogin tries both absolute and relative URLs
   - Line ~417: Auth context uses axios.post (respects baseURL setting)
   - Empty BACKEND_URL = relative URLs = cluster ingress routing

⚠️ TROUBLESHOOTING:
   - "Login failed" usually = wrong BACKEND_URL configuration
   - CORS errors = need relative URLs or backend CORS config
   - 404 errors = backend not accessible at configured URL
   
🎯 FOR FUTURE AGENTS:
   1. Check current public URL in environment variables
   2. Test platform admin login with above credentials  
   3. If login fails, verify REACT_APP_BACKEND_URL in /app/frontend/.env
   4. Current working config: REACT_APP_BACKEND_URL= (empty)

================================================================================
*/

import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link, useSearchParams } from 'react-router-dom';
import { api, chatApi } from './api'; // Use our centralized API instance
import { useMultiHOA } from './MultiHOAContext'; // Multi-HOA context hook

// Import marketing pages
import About from './pages/About';
import Features from './pages/Features';
import Security from './pages/Security';
import Help from './pages/Help';
import Contact from './pages/Contact';
import Training from './pages/Training';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import SignIn from './pages/SignIn';
import axios from 'axios';
import LandingPage from './LandingPage';
import EnhancedHOASetupFlow from './EnhancedHOASetupFlow';
import HOAOnboardingChecklist from './HOAOnboardingChecklist';
import HOASetupSignoffChecklist from './HOASetupSignoffChecklist';
import HOASignupSuccess from './HOASignupSuccess';

// Profile Picture Component with Two Independent Badges (Role + Committee)
const ProfilePicture = ({ user, size = 'md', profileStyles = null }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl'
  };
  
  const getProfileStyle = (role) => {
    if (profileStyles?.role_styles?.[role]) {
      return profileStyles.role_styles[role];
    }
    
    // Default styles with correct colors
    const defaultStyles = {
      'board_member': { color: '#FFD100', icon: '⭐', name: 'Board Member' },
      'president': { color: '#FFD100', icon: '👑', name: 'President' },
      'vice_president': { color: '#FFD100', icon: '⭐', name: 'Vice President' },
      'treasurer': { color: '#FFD100', icon: '💰', name: 'Treasurer' },
      'secretary': { color: '#FFD100', icon: '📋', name: 'Secretary' },
      'homeowner': { color: '#9B59B6', icon: '🏠', name: 'Homeowner' },
      'maintenance': { color: '#E74C3C', icon: '🔧', name: 'Maintenance' },
      'security': { color: '#2ECC71', icon: '🛡️', name: 'Security' },
      'platform_admin': { color: '#E67E22', icon: '👑', name: 'Platform Admin' }
    };
    
    return defaultStyles[role] || defaultStyles['homeowner'];
  };
  
  const primaryRole = user?.role || 'homeowner';
  const primaryStyle = getProfileStyle(primaryRole);
  
  // Check if user is committee member (independent of role)
  const isCommitteeMember = user?.committees && Array.isArray(user.committees) && user.committees.length > 0;
  
  // Generate initials - support multiple names
  const getDisplayInitials = (user) => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const nameParts = fullName.split(' ').filter(part => part.length > 0);
    
    if (nameParts.length >= 2) {
      return nameParts.slice(0, 2).map(name => name.charAt(0).toUpperCase()).join('');
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    } else {
      return 'U';
    }
  };
  
  const displayInitials = getDisplayInitials(user);
  
  // Badge size based on profile size
  const getBadgeSize = (size) => {
    switch (size) {
      case 'xl': return 'w-6 h-6';
      case 'lg': return 'w-5 h-5';
      case 'md': return 'w-4 h-4';
      case 'sm': return 'w-3 h-3';
      default: return 'w-4 h-4';
    }
  };
  
  const badgeSize = getBadgeSize(size);
  
  return (
    <div className={`relative ${sizeClasses[size]} flex-shrink-0`} style={{ position: 'relative' }}>
      {/* Main profile circle with user initials */}
      <div 
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-bold shadow-sm border-2 border-white`}
        style={{ 
          background: `linear-gradient(135deg, ${primaryStyle.color}, ${primaryStyle.color}CC)`,
          fontSize: size === 'xl' ? '1.5rem' : size === 'lg' ? '1.125rem' : '0.875rem'
        }}
      >
        {displayInitials}
      </div>
      
      {/* Role badge (TOP-RIGHT) */}
      {primaryStyle.icon && (
        <div 
          className={`absolute -top-1 -right-1 ${badgeSize} rounded-full flex items-center justify-center border border-white shadow-md bg-white`}
          title={primaryStyle.name}
          style={{ 
            fontSize: size === 'xl' ? '0.875rem' : size === 'lg' ? '0.75rem' : '0.625rem',
            zIndex: 10
          }}
        >
          {primaryStyle.icon}
        </div>
      )}
      
      {/* Committee badge (TOP-LEFT) - only if user is committee member */}
      {isCommitteeMember && (
        <div 
          className={`absolute -top-1 -left-1 ${badgeSize} rounded-full flex items-center justify-center border border-white shadow-md bg-white`}
          title="Committee Member"
          style={{ 
            fontSize: size === 'xl' ? '0.875rem' : size === 'lg' ? '0.75rem' : '0.625rem',
            zIndex: 10
          }}
        >
          🪄
        </div>
      )}
    </div>
  );
};
  
import PlatformAdminDashboard from './PlatformAdminDashboard';
import WelcomeToNeighborhood from './WelcomeToNeighborhood';
import PaymentSystem from './PaymentSystem';
import BudgetSystem from './BudgetSystem';
import CommunityActivityFeed from './components/CommunityActivityFeed';
import NotificationSystem from './NotificationSystem';
import SchedulerSystem from './SchedulerSystem';
import CalendarSystem from './CalendarSystem';
import CommunityEvents from './components/CommunityEvents';
import VotingTab from './components/VotingTab';
import WelcomeEmailTracker from './components/WelcomeEmailTracker';
import ProxyApprovals from './components/ProxyApprovals';
import ViolationSystem from './ViolationSystem';
import { AuthContext, useAuth } from './AuthContext';
import EnhancedDocumentsTab from './components/EnhancedDocumentsTab';
import HOAFilesPro from './components/HOAFilesPro';
import BoardRegistrationCodes from './BoardRegistrationCodes';
import CommitteeManagement from './CommitteeManagement';
import ChangePasswordPage from './ChangePasswordPage';
import SupportTicketSystem from './SupportTicketSystem';
import FormsTab from './FormsTab';
import NewsletterDashboard from './components/NewsletterDashboard';
import ElectronicConsentModal from './ElectronicConsentModal';
import ConfidentialityConsentModal from './ConfidentialityConsentModal';

import HOATrackWidget from './components/HOATrackWidget';
import { formatLocal } from './utils/timezones';
import AsyncSelect from 'react-select/async';
import TeamPermissions from './components/TeamPermissions';
import PublicHOA from './components/PublicHOA';
import ExternalPaymentForm from './components/ExternalPaymentForm';
import ExternalPaymentSuccess from './components/ExternalPaymentSuccess';

// External Payment Page Wrapper
const ExternalPaymentPage = ({ slug }) => {
  const [hoaData, setHoaData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const loadHOA = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public-profile/by-slug/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setHoaData(data);
        }
      } catch (err) {
        console.error('Error loading HOA:', err);
      } finally {
        setLoading(false);
      }
    };
    loadHOA();
  }, [slug]);
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!hoaData) {
    return <div className="min-h-screen flex items-center justify-center">HOA not found</div>;
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <ExternalPaymentForm hoaId={hoaData.hoa_id} hoaName={hoaData.name} />
    </div>
  );
};
import PollVolunteerPage from './PollVolunteerPage';
import GuestFileAccess from './GuestFileAccess';
import PublicPageSettings from './components/PublicPageSettings';
import HOATrackSettings from './components/HOATrackSettings';
import MaintenanceTeamManagement from './components/MaintenanceTeamManagement';
import SecurityTeamManagement from './components/SecurityTeamManagement';
import TeamMemberManagement from './components/TeamMemberManagement';
import AmenityManagement from './components/AmenityManagement';
import SubscriptionManagement from './components/SubscriptionManagementFinal';
import HOAPropertySelector from './components/HOAPropertySelector';
import MultiHOAInitializer from './components/MultiHOAInitializer';
import HOASelectorDropdown from './components/HOASelectorDropdown';

import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

// Create contexts for HOA data
const HOAContext = createContext();

// Custom hooks for contexts
export const useHOA = () => {
  const context = useContext(HOAContext);
  if (!context) {
    throw new Error('useHOA must be used within an HOAProvider');
  }
  return context;
};

// Configure axios defaults
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
if (BACKEND_URL) {
  axios.defaults.baseURL = BACKEND_URL;
}
// If BACKEND_URL is empty, axios will use relative URLs which work with cluster ingress

// Simple login function that works
window.performLogin = async (email, password) => {
  const payload = { email, password };
  const endpoints = [
    `${BACKEND_URL}/api/auth/login`, // absolute (uses REACT_APP_BACKEND_URL)
    `/api/auth/login`                // relative (uses cluster ingress)
  ];
  for (const url of endpoints) {
    try {
      console.log('[login] trying:', url);
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data?.access_token) {
          localStorage.setItem('hoa_token', data.access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
          console.log('[login] success via', url);
          // Don't reload immediately - let the auth system handle state updates
          // window.location.reload();
          return true;
        }
      } else {
        const t = await resp.text();
        console.warn('[login] non-200 via', url, resp.status, t.slice(0,180));
      }
    } catch (e) {
      console.warn('[login] network error via', url, e?.message || e);
const PlatformAdminInfo = ({ hoaId }) => {
  const [admin, setAdmin] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const load = async () => {
      if (!hoaId) return;
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/api/hoa/${hoaId}/platform-admin`);
        setAdmin(res.data?.platform_admin || null);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load platform admin');
        setAdmin(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hoaId]);

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!admin) return <p className="text-sm text-gray-500">No platform admin found.</p>;

  return (
    <div className="flex items-center space-x-3">
      <div className="h-8 w-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-semibold">
        {(admin.first_name?.[0] || '').toUpperCase()}{(admin.last_name?.[0] || '').toUpperCase()}
      </div>
      <div>
        <div className="text-sm font-medium text-gray-900">{admin.first_name} {admin.last_name}</div>
        <div className="text-xs text-gray-600">{admin.email} • {admin.role}</div>
      </div>
    </div>
  );
};

    }
  }
  return false;
};

// Auth Provider Component

// HOA Admin info card component
const HOAAdminInfo = ({ hoaId }) => {
  const [admin, setAdmin] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const load = async () => {
      if (!hoaId) return;
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/api/hoa/${hoaId}/admin`);
        setAdmin(res.data?.hoa_admin || null);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load HOA admin');
        setAdmin(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hoaId]);

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!admin) return <p className="text-sm text-gray-500">No HOA admin found.</p>;

  return (
    <div className="flex items-center space-x-3">
      <div className="h-8 w-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-semibold">
        {(admin.first_name?.[0] || '').toUpperCase()}{(admin.last_name?.[0] || '').toUpperCase()}
      </div>
      <div>
        <div className="text-sm font-medium text-gray-900">{admin.first_name} {admin.last_name}</div>
        <div className="text-xs text-gray-600">{admin.email} • HOA Administrator</div>
      </div>
    </div>
  );
};


// Platform Admin info card component (top-level)
const PlatformAdminInfo = ({ hoaId }) => {
  const [admin, setAdmin] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const load = async () => {
      if (!hoaId) return;
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/api/hoa/${hoaId}/platform-admin`);
        setAdmin(res.data?.platform_admin || null);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load platform admin');
        setAdmin(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hoaId]);

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!admin) return <p className="text-sm text-gray-500">No platform admin found.</p>;

  return (
    <div className="flex items-center space-x-3">
      <div className="h-8 w-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-semibold">
        {(admin.first_name?.[0] || '').toUpperCase()}{(admin.last_name?.[0] || '').toUpperCase()}
      </div>
      <div>
        <div className="text-sm font-medium text-gray-900">{admin.first_name} {admin.last_name}</div>
        <div className="text-xs text-gray-600">{admin.email} • {admin.role}</div>
      </div>
    </div>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonationMode, setImpersonationMode] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('hoa_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }

    // Listen for HOA theme updates and (re)apply when triggered
    const handleHoaThemeUpdated = (e) => {
      const { hoaId } = e.detail || {};
      if (hoaId && user?.hoa_id === hoaId) {
        loadHoaThemeFor(hoaId);
      }
    };
    window.addEventListener('hoa_theme_updated', handleHoaThemeUpdated);

    return () => {
      window.removeEventListener('hoa_theme_updated', handleHoaThemeUpdated);
    };
  }, []);

  // Apply branding from server on login/HOA change
  useEffect(() => {
    const loadBranding = async () => {
      try {
        if (!user || !user.hoa_id) return;
        // Store current HOA id globally for other components
        try { window.currentHoaId = user.hoa_id; } catch(_) {}
        // Get HOA code
        const { data: my } = await api.get('/api/hoa/my');
        const hoa = my?.hoa;
        if (!hoa?.code) return;
        const { data: branding } = await api.get(`/api/hoa/${hoa.code}/branding`);
        const primary = branding.primaryColor || '#4f46e5';
        const secondary = branding.secondaryColor || '#6366f1';
        // Set window logo url for header/overview
        if (branding.logo_url) {
          window.__serverBrandingLogoUrl = branding.logo_url;
        }
        // Persist under HOA namespace
        const themeConfig = {
          primaryColor: primary,
          secondaryColor: secondary,
          logo: branding.logo_url || null,
          appliedAt: new Date().toISOString(),
          hoaId: user.hoa_id
        };
        localStorage.setItem(`hoa_theme_${user.hoa_id}`, JSON.stringify(themeConfig));

        // Apply theme CSS
        let styleEl = document.getElementById('hoa-dynamic-theme');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'hoa-dynamic-theme';
          document.head.appendChild(styleEl);
        }
        styleEl.innerHTML = `
          /* Primary CTA buttons only (avoid global overrides on generic Tailwind classes) */
          [data-theme="primary"], .primary-cta { background-color: ${primary} !important; color: #fff !important; }
          [data-theme="primary"]:hover, .primary-cta:hover { background-color: ${secondary} !important; }
          .primary-cta *, [data-theme="primary"] * { color: #fff !important; }
          .dashboard-nav .active { background-color: ${primary} !important; color: #fff !important; }
        `;
      } catch (e) {
        console.warn('Branding load failed', e);
      }
    };
    loadBranding();
  }, [user?.hoa_id]);

  // Apply or clear theme whenever auth user changes (scope to HOA only)
  useEffect(() => {
    if (user?.hoa_id) {
      loadHoaThemeFor(user.hoa_id);
    } else {
      // Not in HOA dashboard (login, platform admin, etc.) -> clear any injected HOA theme
      clearHoaTheme();
    }
  }, [user?.hoa_id]);

  // Clear any injected HOA theme styles when not in an HOA dashboard
  const clearHoaTheme = () => {
    try {
      const styleEl = document.getElementById('hoa-global-theme');
      if (styleEl) styleEl.remove();
      const dynEl = document.getElementById('hoa-dynamic-theme');
      if (dynEl) dynEl.remove();
      const root = document.documentElement;
      root.style.removeProperty('--primary-color');
      root.style.removeProperty('--secondary-color');
    } catch (e) {
      console.debug('clearHoaTheme error', e);
    }
  };

  // Load and apply HOA-scoped theme only for the current HOA dashboard
  const loadHoaThemeFor = (hoaId) => {
    try {
      if (!hoaId) return clearHoaTheme();
      const saved = localStorage.getItem(`hoa_theme_${hoaId}`) || localStorage.getItem('branding_theme_fallback');
      if (!saved) return clearHoaTheme();
      const themeConfig = JSON.parse(saved);
      applyThemeGlobally(themeConfig);
    } catch (error) {
      console.debug('Theme loading error:', error);
    }
  };

  // Apply theme comprehensively across entire site with enhanced coverage
  const applyThemeGlobally = (themeConfig) => {
    try {
      if (!themeConfig.primaryColor && !themeConfig.primary) return;
      
      const primaryColor = themeConfig.primaryColor || themeConfig.primary;
      const secondaryColor = themeConfig.secondaryColor || themeConfig.secondary || primaryColor;
      
      const root = document.documentElement;
      
      // Update CSS variables
      root.style.setProperty('--primary-color', primaryColor);
      root.style.setProperty('--secondary-color', secondaryColor);
      
      // Create comprehensive CSS override that persists
      let styleEl = document.getElementById('hoa-global-theme');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'hoa-global-theme';
        document.head.appendChild(styleEl);
      }
      
      styleEl.innerHTML = `
        /* PERSISTENT GLOBAL THEME - WITH PROPER CONTRAST */
        .bg-indigo-600, .bg-blue-600, .bg-purple-600, 
        .hover\\:bg-indigo-700:hover, .hover\\:bg-blue-700:hover, .hover\\:bg-purple-700:hover,
        button[class*="bg-indigo"], button[class*="bg-blue"], button[class*="bg-purple"],
        .dashboard-nav button.active, .nav-tab.active,
        [data-theme="primary"] { 
          background-color: ${primaryColor} !important;
          color: white !important;
        }
        
        /* Ensure text is always readable on colored backgrounds */
        .bg-indigo-600 *, .bg-blue-600 *, .bg-purple-600 *,
        .bg-indigo-100 *, .bg-blue-100 *, .bg-purple-100 *,
        .bg-indigo-50 *, .bg-blue-50 *, .bg-purple-50 *,
        [style*="background-color: ${primaryColor}"] *,
        [style*="background-color: ${primaryColor}1A"] *,
        [style*="background-color: ${primaryColor}0D"] * {
          color: white !important;
        }
        
        /* Specific contrast fixes for Communications Tab and Overview Quick Actions */
        .communication-tab .bg-blue-50, .communication-tab .bg-green-50, .communication-tab .bg-yellow-50,
        .overview-tab .quick-action-card, .overview-tab .stat-card,
        .quick-actions .bg-indigo-100, .quick-actions .bg-blue-100, .quick-actions .bg-purple-100 {
          background-color: white !important;
          border: 2px solid ${primaryColor} !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
        }
        /* Quick Action buttons should look like primary CTAs */
        .overview-tab .quick-actions button,
        .overview-tab .quick-actions a,
        .overview-tab [data-theme="primary"],
        .overview-tab .primary-cta {
          background-color: ${primaryColor} !important;
          color: #fff !important;
          border: none !important;
        }
        .overview-tab .quick-actions button:hover,
        .overview-tab .quick-actions a:hover,
        .overview-tab [data-theme="primary"]:hover,
        .overview-tab .primary-cta:hover {
          background-color: ${secondaryColor} !important;
        }
        /* Ensure CTA text/icons are white */
        .overview-tab .quick-actions button *,
        .overview-tab [data-theme="primary"] * {
          color: #fff !important;
        }
        
        .communication-tab .bg-blue-50 *, .communication-tab .bg-green-50 *, .communication-tab .bg-yellow-50 *,
        .overview-tab .quick-action-card *, .overview-tab .stat-card *,
        .quick-actions .bg-indigo-100 *, .quick-actions .bg-blue-100 *, .quick-actions .bg-purple-100 * {
          color: ${primaryColor} !important;
          font-weight: 700 !important;
        }
        
        .communication-tab h4, .communication-tab h5,
        .overview-tab h4, .overview-tab h5 {
          color: ${primaryColor} !important;
          font-weight: 800 !important;
        }
        
        .communication-tab p, .overview-tab p {
          color: #374151 !important;
          font-weight: 600 !important;
        }
        
        .communication-tab button, .overview-tab button {
          background-color: ${primaryColor} !important;
          color: white !important;
          font-weight: 600 !important;
          border: none !important;
        }
        
        .communication-tab button:hover, .overview-tab button:hover {
          background-color: ${secondaryColor} !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 8px rgba(0,0,0,0.2) !important;
        }
        
        /* Mobile-specific improvements */
        @media (max-width: 768px) {
          /* Smooth horizontal scrolling for tabs */
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          
          /* Better mobile content spacing */
          body {
            overflow-x: hidden;
          }
          
          /* Ensure tables are scrollable on mobile */
          table {
            display: block;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          /* Better mobile card spacing */
          .bg-white.rounded-lg.shadow {
            margin-bottom: 1rem;
          }
          
          /* Mobile-friendly buttons */
          button, a.button {
            min-height: 44px;
            touch-action: manipulation;
          }
          
          /* Prevent text from being too small on mobile */
          body {
            font-size: 16px;
            line-height: 1.5;
          }
          
          /* Better modal presentation on mobile */
          .fixed.inset-0 > div:not([class*="bg-black"]) {
            max-width: calc(100vw - 2rem);
            max-height: calc(100dvh - 2rem);
            margin: 1rem;
            overflow-y: auto;
          }
        }
        
        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .max-w-7xl {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
        }
        
        /* Calendar and bubble elements */
        .calendar-event, .event-bubble, .status-bubble,
        .bg-indigo-50, .bg-blue-50, .bg-purple-50 {
          background-color: ${primaryColor}0D !important;
          border: 2px solid ${primaryColor} !important;
        }
        
        .calendar-event *, .event-bubble *, .status-bubble *,
        .bg-indigo-50 *, .bg-blue-50 *, .bg-purple-50 * {
          color: ${primaryColor} !important;
          font-weight: 700 !important;
          text-shadow: 1px 1px 2px white !important;
        }
        
        /* Text colors - always contrasting */
        .text-indigo-600, .text-blue-600, .text-purple-600,
        .text-indigo-500, .text-blue-500, .text-purple-500,
        .theme-primary-text { 
          color: ${primaryColor} !important;
          font-weight: 600 !important;
        }
        
        /* Borders and rings */
        .border-indigo-600, .border-blue-600, .border-purple-600,
        .ring-indigo-500, .ring-blue-500, .ring-purple-500,
        .theme-primary-border { 
          border-color: ${primaryColor} !important;
          --tw-ring-color: ${primaryColor} !important;
        }
        
        /* Focus states */
        .focus\\:ring-indigo-500:focus, .focus\\:ring-blue-500:focus, .focus\\:ring-purple-500:focus,
        .focus\\:border-indigo-500:focus, .focus\\:border-blue-500:focus, .focus\\:border-purple-500:focus {
          --tw-ring-color: ${primaryColor} !important;
          border-color: ${primaryColor} !important;
        }
        
        /* Status badges and indicators */
        .status-badge.primary, .badge-primary, .indicator-primary { 
          background-color: ${primaryColor} !important;
          color: white !important;
        }
        
        /* Custom theme classes for consistent application */
        .theme-primary { 
          background-color: ${primaryColor} !important;
          color: white !important;
        }
        .theme-primary-hover:hover { background-color: ${secondaryColor} !important; }
      `;
      
      // Force immediate application to existing elements with proper error handling
      const elementsToUpdate = document.querySelectorAll(`
        .bg-indigo-600, .bg-blue-600, .bg-purple-600,
        .text-indigo-600, .text-blue-600, .text-purple-600,
        .border-indigo-600, .border-blue-600, .border-purple-600,
        button[class*="bg-indigo"], button[class*="bg-blue"], button[class*="bg-purple"]
      `);
      
      elementsToUpdate.forEach(el => {
        try {
          // Safely check className - handle both string and SVGAnimatedString
          const className = el.className && typeof el.className === 'string' 
            ? el.className 
            : el.className && el.className.baseVal 
              ? el.className.baseVal 
              : '';
          
          if (className.includes && typeof className.includes === 'function') {
            if (className.includes('bg-')) {
              el.style.backgroundColor = primaryColor;
            }
            if (className.includes('text-')) {
              el.style.color = primaryColor;
            }
            if (className.includes('border-')) {
              el.style.borderColor = primaryColor;
            }
          }
        } catch (error) {
          // Silently ignore elements that can't be processed
          console.debug('Theme application skipped for element:', el);
        }
      });
      
      // Store theme in multiple locations for maximum persistence
      try {
        // Store as HOA-scoped theme only
        try {
          const currentHoaId = (user && user.hoa_id) || (window.currentHoaId) || 'unknown';
          if (currentHoaId && currentHoaId !== 'unknown') {
            localStorage.setItem(`hoa_theme_${currentHoaId}`, JSON.stringify({ primaryColor, secondaryColor }));
          }
        } catch(e) { /* ignore storage errors */ }
      } catch (storageError) {
        console.debug('Theme storage error:', storageError);
      }
      
    } catch (error) {
      console.debug('Theme application error:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      console.log('🔑 Fetching user info from /api/auth/me...');
      const response = await api.get('/api/auth/me');
      const u = response.data.user || {};
      console.log('🔑 Raw user data from API:', u);
      
      // Normalize shapes coming from backend
      const normalized = {
        ...u,
        id: u.id ?? u._id,  // tolerate either id or _id
        hoa_id: u.hoa_id ?? u.current_hoa_id ?? u.selected_hoa_id
      };
      
      // Fallback to last selected HOA if server didn't include it
      if (!normalized.hoa_id) {
        const last = localStorage.getItem('last_selected_hoa');
        if (last) normalized.hoa_id = last;
        console.log('🔑 Using fallback HOA from localStorage:', last);
      }
      
      console.log('🔑 Normalized user object:', normalized);
      setUser(normalized);
      
      try {
        if (u?.must_change_password) setForcePasswordChange(true);
      } catch(_){}
    } catch (error) {
      console.error('❌ Failed to fetch user info:', error);
      
      // Only clear user if it's an authentication error (401/403)
      // For other errors (network issues, server errors), keep the user logged in
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('🔑 Authentication failed, clearing user and token');
        setUser(null);
        localStorage.removeItem('hoa_token');
        delete axios.defaults.headers.common['Authorization'];
      } else {
        console.log('🔑 Network/server error, keeping user logged in');
        // Don't clear user for temporary network issues
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch user info on app load if token exists
  React.useEffect(() => {
    const token = localStorage.getItem('hoa_token');
    if (token && !user) {
      console.log('🔑 Token found on mount, fetching user info...');
      setLoading(true); // Keep loading state active during token restoration
      fetchUserInfo();
    } else if (!token) {
      console.log('🔑 No token found on mount');
      setLoading(false);
    }
  }, []); // Remove user dependency to prevent infinite loop

  const login = async (email, password) => {
    console.log('🔑 AuthProvider login function called with:', email);
    try {
      console.log('🔑 Making API call to /api/auth/login...');
      const response = await api.post('/api/auth/login', { email, password });
      console.log('🔑 API response received:', response.data);
      
      // Check if HOA selection is required
      if (response.data.requires_hoa_selection) {
        console.log('🏠 HOA selection required');
        return {
          success: true,
          requiresHoaSelection: true,
          hoaOptions: response.data.hoa_options,
          tempAuthToken: response.data.temp_auth_token,
          email: email
        };
      }
      
      // Single HOA - proceed directly with login
      console.log('✅ Direct login successful, setting user');
      const { access_token } = response.data;
      
      localStorage.setItem('hoa_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Fetch complete user info (including permissions) after setting token
      await fetchUserInfo();
      
      return { success: true };
    } catch (error) {
      console.error('❌ Login API call failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const selectHoa = async (email, hoaId, tempAuthToken) => {
    try {
      const response = await api.post('/api/auth/select-hoa', {
        email: email,
        hoa_id: hoaId,
        temp_auth_token: tempAuthToken
      });
      
      const { access_token } = response.data;
      
      localStorage.setItem('hoa_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Fetch complete user info (including permissions) after setting token
      await fetchUserInfo();
      
      // Note: Server automatically stores last selected HOA via the select-hoa endpoint
      // No need to manually store it client-side anymore
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'HOA selection failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/api/auth/register', userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('hoa_token');
    localStorage.removeItem('impersonation_token');
    // Clear multi-HOA context
    localStorage.removeItem('active_hoa_id');
    localStorage.removeItem('active_property_id');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setImpersonationMode(false);
    setOriginalUser(null);
  };

  const startImpersonation = async (hoaId, role, reason, targetUserId = null) => {
    try {
      console.log('Attempting to start impersonation:', { hoaId, role, reason, targetUserId });
      console.log('Auth token exists:', !!localStorage.getItem('hoa_token'));
      
      // If no targetUserId provided, try to get a user from the HOA first
      let actualTargetUserId = targetUserId;
      
      if (!actualTargetUserId) {
        // Fetch the HOA's users and select the first approved board/homeowner
        const { data: cfg } = await api.get(`/api/platform-admin/hoa/${hoaId}/configuration`);
        const board = (cfg?.board_members || []);
        if (board.length > 0) {
          actualTargetUserId = board[0].id;
        } else {
          // Fallback: try homeowners endpoint
          const { data: res } = await api.get('/api/users/homeowners');
          const list = (res || []);
          actualTargetUserId = list.length > 0 ? list[0].id : null;
        }
      }
      
      // Build query parameters for the impersonation API
      const queryParams = new URLSearchParams({
        hoa_id: hoaId,
        target_role: role,
        target_user_id: actualTargetUserId,
        reason: reason
      });
      
      const { data: response } = await api.post(`/api/platform-admin/impersonate?${queryParams.toString()}`);
      
      console.log('Impersonation response:', response);
      
      if (response.impersonation_token) {
        // Store original user data
        setOriginalUser(user);
        
        // Store impersonation token
        localStorage.setItem('impersonation_token', response.impersonation_token);
        
        // Update axios headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.impersonation_token}`;
        
        // Update user state with impersonated user
        setUser(response.impersonated_user);
        setImpersonationMode(true);
        
        return { success: true };
      } else {
        console.error('No impersonation token received');
        return { success: false, error: 'No impersonation token received' };
      }
    } catch (error) {
      console.error('Impersonation failed:', error);
      const errorMsg = error.response?.data?.detail || 'Impersonation failed';
      return { 
        success: false, 
        error: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg)
      };
    }
  };

  const endImpersonation = () => {
    // Remove impersonation token
    localStorage.removeItem('impersonation_token');
    
    // Restore original token
    const originalToken = localStorage.getItem('hoa_token');
    if (originalToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
    }
    
    // Restore original user
    setUser(originalUser);
    setOriginalUser(null);
    setImpersonationMode(false);
  };

  // Change password function for first-login and normal updates
  const changePassword = async ({ newPassword, oldPassword }) => {
    try {
      const payload = { new_password: newPassword };
      if (oldPassword) payload.old_password = oldPassword;
      const res = await api.post('/api/auth/password/change', payload);
      await fetchUserInfo();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to change password' };
    }
  };

  const getUserPreferences = async () => {
    try {
      const { data } = await api.get('/api/user/preferences');
      return { 
        success: true, 
        preferences: data.preferences 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to get preferences' 
      };
    }
  };

  const updateUserPreferences = async (preferences) => {
    try {
      await api.put('/api/user/preferences', preferences);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to update preferences' 
      };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      selectHoa,
      register,
      logout,
      changePassword,
      getUserPreferences,
      updateUserPreferences,
      impersonationMode,
      originalUser,
      startImpersonation,
      endImpersonation,
      forcePasswordChange,
      setForcePasswordChange
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// HOA Provider Component
const HOAProvider = ({ children }) => {
  const [hoaData, setHoaData] = useState(null);
  const [homeowners, setHomeowners] = useState([]);
  const [archivedHomeowners, setArchivedHomeowners] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [hoaSettings, setHoaSettings] = useState({ property_identifier: { enabled: false, label: 'Property Identifier', required: false } });
  const [subscriptionTier, setSubscriptionTier] = useState('basic'); // basic, standard, premium
  const [subscriptionData, setSubscriptionData] = useState(null); // Full subscription data with add-ons and features
  const [accountUsage, setAccountUsage] = useState({ 
    storage: { used_gb: 0, limit_gb: 0, percentage: 0 },
    users: { registered: 0, limit: 0, percentage: 0 },
    properties: { actual: 0, available: 0, display: 'N/A' }
  });
  const { user } = useAuth();
  const { activeHoaId } = useMultiHOA();

  // Fetch account usage
  useEffect(() => {
    const fetchAccountUsage = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!user?.id || !currentHoaId) return;
        const { data } = await api.get('/api/billing/usage');
        if (data.success) {
          setAccountUsage({
            storage: data.storage,
            users: data.users,
            properties: data.properties || { actual: 0, available: 0, display: 'N/A' }
          });
        }
      } catch (error) {
        console.warn('Failed to load account usage:', error);
        // Keep default values
      }
    };

    const currentHoaId = activeHoaId || user?.hoa_id;
    if (user?.id && currentHoaId) {
      fetchAccountUsage();
    }
  }, [user?.id, activeHoaId, user?.hoa_id]);

  // Fetch subscription tier
  useEffect(() => {
    const fetchSubscriptionTier = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!user?.id || !currentHoaId) return;
        const { data } = await api.get('/api/subscription/current');
        if (data && data.plan) {
          setSubscriptionTier(data.plan.tier || 'basic');
        }
      } catch (error) {
        console.warn('Failed to load subscription tier:', error);
        setSubscriptionTier('basic'); // Default to basic on error
      }
    };

    const currentHoaId = activeHoaId || user?.hoa_id;
    if (user?.id && currentHoaId) {
      fetchSubscriptionTier();
    }
  }, [user?.id, activeHoaId, user?.hoa_id]);

  useEffect(() => {
    if (user && user.hoa_id && user.role !== 'platform_admin') {
      fetchHOAData();
      fetchHOASettings();
      fetchHomeowners();
      fetchArchivedHomeowners();
      fetchPendingApprovals();
    }
  }, [user]);

  const fetchHOAData = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data: response } = await api.get(`/api/hoa/${currentHoaId}`);
      let hoaDataResult;
      
      // Extract the hoa data from the response structure
      if (response.success && response.hoa) {
        hoaDataResult = response.hoa;
      } else {
        hoaDataResult = response;
      }
      
      console.log('HOA Data fetched:', hoaDataResult);
      
      // Fetch team members for permission checking
      try {
        const { data: teamMembersResponse } = await api.get('/api/team-members');
        if (teamMembersResponse.team_members) {
          hoaDataResult.team_members = teamMembersResponse.team_members;
          console.log('Team members loaded:', hoaDataResult.team_members.length);
        }
      } catch (tmError) {
        console.log('Could not load team members:', tmError.message);
      }
      
      // Get HOA code - check both possible locations
      const hoaCode = hoaDataResult?.code || hoaDataResult?.hoa_code;
      console.log('HOA Code for branding:', hoaCode);
      
      // Fetch branding data separately using HOA code
      if (hoaCode) {
        try {
          console.log(`Fetching branding data from: /api/hoa/${hoaCode}/branding`);
          const { data: brandingResponse } = await api.get(`/api/hoa/${hoaCode}/branding`);
          console.log('Branding response:', brandingResponse);
          if (brandingResponse) {
            hoaDataResult.branding = brandingResponse;
            console.log('Branding data set:', hoaDataResult.branding);
          }
        } catch (brandingError) {
          console.log('Branding data not available:', brandingError.message);
          console.error('Full branding error:', brandingError);
        }
      } else {
        console.log('No HOA code found in hoaData, cannot fetch branding');
        console.log('Available fields in hoaData:', Object.keys(hoaDataResult || {}));
      }
      
      setHoaData(hoaDataResult);
      // Set current HOA id globally for theme scoping
      window.currentHoaId = user.hoa_id;
    } catch (error) {
      console.error('Error fetching HOA data:', error);
    }
  };

  const fetchHOASettings = async () => {
    try {
      const { data } = await api.get('/api/board/hoa/settings');
      if (data?.settings?.property_identifier) {
        setHoaSettings({ property_identifier: data.settings.property_identifier });
      }
    } catch (e) {
      console.error('Error fetching HOA settings:', e);
    }
  };

  const fetchHomeowners = async () => {
    try {
      const { data } = await api.get('/api/users/homeowners');
      setHomeowners(data.homeowners || []);
    } catch (error) {
      console.error('Error fetching homeowners:', error);
    }
  };

  const fetchArchivedHomeowners = async () => {
    try {
      const { data } = await api.get('/api/users/archived-homeowners');
      setArchivedHomeowners(data.archived_homeowners || []);
    } catch (error) {
      console.error('Error fetching archived homeowners:', error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const { data } = await api.get('/api/board/pending-approvals');
      setPendingApprovals(data.pending_users || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  // Make refresh functions globally available for CSV import
  useEffect(() => {
    window.hoaRefreshFunctions = {
      fetchHomeowners,
      fetchPendingApprovals
    };
    
    // Cleanup on unmount
    return () => {
      delete window.hoaRefreshFunctions;
    };
  }, [fetchHomeowners, fetchPendingApprovals]);

  const approveUser = async (userId) => {
    try {
      const response = await api.post(`/api/board/approve-user/${userId}`);
      // Backend returns {message: "User approved successfully"}
      if (response.status === 200) {
        await fetchPendingApprovals();
        await fetchHomeowners();
        return { success: true };
      }
      return { success: false, error: 'Approval failed' };
    } catch (error) {
      console.error('Error approving user:', error);
      return { success: false, error: error.response?.data?.detail || 'Approval failed' };
    }
  };

  const approvePropertyMembership = async (membershipId) => {
    try {
      const response = await api.post(`/api/board/approve-property-membership/${membershipId}`);
      if (response.status === 200) {
        await fetchPendingApprovals();
        return { success: true };
      }
      return { success: false, error: 'Approval failed' };
    } catch (error) {
      console.error('Error approving property membership:', error);
      return { success: false, error: error.response?.data?.detail || 'Approval failed' };
    }
  };

  const rejectUser = async (userId, reason) => {
    try {
      // Backend expects form-encoded reason
      const form = new URLSearchParams();
      form.append('reason', reason || 'No reason provided');
      const response = await api.post(`/api/board/reject-user/${userId}` , form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      if (response.status === 200) {
        await fetchPendingApprovals();
        return { success: true };
      }
      return { success: false, error: 'Rejection failed' };
    } catch (error) {
      console.error('Error rejecting user:', error);
      return { success: false, error: error.response?.data?.detail || 'Rejection failed' };
    }
  };

  // Action button functions for homeowner management
  const approveHomeowner = async (homeownerId) => {
    try {
      const res = await approveUser(homeownerId);
      if (res?.success) {
        const homeowner = homeowners.find(h => h.id === homeownerId) || {};
        const fullName = `${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim();
        alert(`✅ Homeowner Approved!\n\n👤 Name: ${fullName}\n📧 Email: ${homeowner.email || ''}\n\n✅ Status: Active\n\n📧 EMAIL NOTIFICATION:\n• User will receive approval email shortly\n• Email includes login instructions\n• For password setup, direct them to:\n  1. Contact HOA board members\n  2. Board can generate a temporary password\n  3. User will be required to change password on first login\n\n📅 Approved: ${new Date().toLocaleDateString()}`);
        
        // Refresh data
        fetchHomeowners();
        fetchPendingApprovals();
      } else {
        alert('Approval failed');
      }
    } catch (e) {
      alert('Approval failed');
    }
  };

  const suspendHomeowner = async (homeownerId) => {
    const confirmed = confirm('Suspend this homeowner? They will lose portal access until reactivated.');
    if (confirmed) {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) return;
        
        const response = await api.put(`/api/hoa/${currentHoaId}/suspend-homeowner/${homeownerId}`);
        const result = response.data;
        
        if (result.success) {
          const homeowner = homeowners.find(h => h.id === homeownerId) || {};
          alert(`⚠️ Homeowner Suspended!\n\n👤 Name: ${homeowner.first_name} ${homeowner.last_name}\n📧 Email: ${homeowner.email}\n\n🚫 Access: Portal access revoked\n📧 Notification: User will be notified\n🔄 Note: Can be reactivated at any time`);
          
          // Refresh homeowner list
          fetchHomeowners();
        } else {
          alert('❌ Failed to suspend homeowner: ' + (result.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error suspending homeowner:', error);
        alert('❌ Error suspending homeowner. Please try again.');
      }
    }
  };

  return (
    <HOAContext.Provider value={{
      hoaData,
      homeowners,
      archivedHomeowners,
      pendingApprovals,
      accountUsage,
      hoaSettings,
      subscriptionTier,
      fetchHOAData,
      fetchHOASettings,
      fetchHomeowners,
      fetchArchivedHomeowners,
      fetchPendingApprovals,
      approveUser,
      rejectUser,
      approveHomeowner,
      suspendHomeowner,
      approvePropertyMembership
    }}>
      {children}
    </HOAContext.Provider>
  );
};

// HOA Selection Component
const HOASelectionForm = ({ hoaOptions, email, tempAuthToken, onSelect, onBack }) => {
  const [selectedHoa, setSelectedHoa] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { selectHoa, getUserPreferences } = useAuth();

  // Auto-select if user has a last selected HOA preference
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        // First check if only one HOA - auto-select it
        if (hoaOptions.length === 1) {
          setSelectedHoa(hoaOptions[0].hoa_id);
          return;
        }
        
        // For multiple HOAs, try to get user's last selected preference
        // Note: We can't call the API here since user isn't authenticated yet
        // So we fall back to localStorage for this specific case
        const lastSelected = localStorage.getItem('last_selected_hoa');
        if (lastSelected && hoaOptions.find(hoa => hoa.hoa_id === lastSelected)) {
          setSelectedHoa(lastSelected);
        }
      } catch (error) {
        console.warn('Could not load user preferences during HOA selection:', error);
        // Fallback to single HOA auto-selection if applicable
        if (hoaOptions.length === 1) {
          setSelectedHoa(hoaOptions[0].hoa_id);
        }
      }
    };
    
    loadPreferences();
  }, [hoaOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedHoa) {
      setError('Please select an HOA');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await selectHoa(email, selectedHoa, tempAuthToken);
      if (result.success) {
        // Store selection temporarily in localStorage as backup
        // (Server already stores it via the select-hoa endpoint)
        localStorage.setItem('last_selected_hoa', selectedHoa);
        onSelect();
      } else {
        setError(result.error || 'HOA selection failed');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-5a2 2 0 012-2h2a2 2 0 012 2v5m-4 0h4" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Select Your HOA
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            You have access to multiple HOA communities. Please select which one you'd like to access.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="hoa-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select HOA Community
            </label>
            <select
              id="hoa-select"
              required
              className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              value={selectedHoa}
              onChange={(e) => setSelectedHoa(e.target.value)}
            >
              <option value="">Choose your HOA...</option>
              {hoaOptions.map((hoa) => (
                <option key={hoa.hoa_id} value={hoa.hoa_id}>
                  {hoa.display_name} - {hoa.user_role.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !selectedHoa}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Continue to Dashboard'}
            </button>
          </div>

          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={onBack}
              className="text-gray-600 hover:text-gray-500 font-medium text-sm"
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
const LoginForm = ({ onBack, onSuccess, showRegistrationOption = true, mustChangePassword = false, onPasswordChanged = () => {} }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showHoaSelection, setShowHoaSelection] = useState(false);
  const [hoaSelectionData, setHoaSelectionData] = useState(null);
  const { login, register, changePassword } = useAuth();

  // Registration form state
  const [regForm, setRegForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    propertyAddress: '',
    phoneNumber: '',
    registrationCode: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('🔍 Login attempt started for:', email);

    try {
      console.log('🔍 Calling login function...');
      const result = await login(email, password);
      console.log('🔍 Login result:', result);
      
      if (result.success) {
        console.log('✅ Login successful!');
        if (result.requiresHoaSelection) {
          console.log('🏠 HOA selection required');
          // User has multiple HOAs - show selection screen
          setHoaSelectionData({
            hoaOptions: result.hoaOptions,
            email: result.email,
            tempAuthToken: result.tempAuthToken
          });
          setShowHoaSelection(true);
        } else {
          console.log('🎯 Direct login successful - proceeding to dashboard');
          // Single HOA or successful login - proceed to dashboard
          if (onSuccess) onSuccess();
          // Page will reload automatically via the auth system
        }
      } else {
        console.error('❌ Login failed:', result.error);
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('❌ Login exception:', error);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
      console.log('🔍 Login attempt completed');
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (!regForm.registrationCode.trim()) {
      setError('Registration code is required');
      return;
    }

    try {
      setLoading(true);
      
      // First, validate the registration code
      const { data: codeValidation } = await api.post('/api/registration/validate-code', {
        code: regForm.registrationCode,
        email: regForm.email,
        address: regForm.propertyAddress
      });

      if (!codeValidation.valid) {
        setError(codeValidation.error);
        return;
      }

      // If code is valid, proceed with registration
      const result = await register({
        email: regForm.email,
        first_name: regForm.firstName,
        last_name: regForm.lastName,
        property_address: regForm.propertyAddress,
        phone: regForm.phoneNumber,
        registration_code: regForm.registrationCode
      });

      if (result.success) {
        setShowRegistration(false);
        setError('');
        alert('Thank you for signing up! Once your registration is approved by the board, you will receive an email with login instructions and your temporary password.');
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (showRegistration) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-indigo-100">
              <svg className="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Join Your HOA Community
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Register as a homeowner to access community features
            </p>
          </div>
          
          {/* Important info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">When to use this form</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>This registration is <strong>only</strong> for HOAs that have set up a registration code.</p>
                  <ul className="mt-2 list-disc list-inside space-y-1">
                    <li>If you already received a username and password, simply <button type="button" onClick={() => setShowRegistration(false)} className="text-blue-600 underline hover:text-blue-800 font-medium">login here</button></li>
                    <li>If your password isn't working, use <button type="button" onClick={() => setShowRegistration(false)} className="text-blue-600 underline hover:text-blue-800 font-medium">Forgot Password</button></li>
                    <li>Or contact your HOA board for assistance</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleRegistration}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="sr-only">First Name</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                    placeholder="First Name"
                    value={regForm.firstName}
                    onChange={(e) => setRegForm({...regForm, firstName: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="sr-only">Last Name</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                    placeholder="Last Name"
                    value={regForm.lastName}
                    onChange={(e) => setRegForm({...regForm, lastName: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="reg-email" className="sr-only">Email address</label>
                <input
                  id="reg-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={regForm.email}
                  onChange={(e) => setRegForm({...regForm, email: e.target.value})}
                />
              </div>
              
              <div>
                <label htmlFor="propertyAddress" className="sr-only">Property Address</label>
                <input
                  id="propertyAddress"
                  name="propertyAddress"
                  type="text"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Property Address"
                  value={regForm.propertyAddress}
                  onChange={(e) => setRegForm({...regForm, propertyAddress: e.target.value})}
                />
                <p className="mt-1 text-xs text-gray-500">Write out full address with no commas (e.g., 123 Main St Apt 4 City State 12345)</p>
              </div>
              
              <div>
                <label htmlFor="phoneNumber" className="sr-only">Phone Number</label>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Phone Number (optional)"
                  value={regForm.phoneNumber}
                  onChange={(e) => setRegForm({...regForm, phoneNumber: e.target.value})}
                />
              </div>
              
              <div>
                <label htmlFor="registrationCode" className="sr-only">Registration Code</label>
                <input
                  id="registrationCode"
                  name="registrationCode"
                  type="text"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm font-mono uppercase"
                  placeholder="Registration Code (e.g., ABC123XY)"
                  value={regForm.registrationCode}
                  onChange={(e) => setRegForm({...regForm, registrationCode: e.target.value.toUpperCase()})}
                />
                <p className="mt-1 text-xs text-gray-600">
                  Enter the registration code provided by your HOA board
                </p>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Registering...' : 'Register'}
              </button>
            </div>
            
            <div className="flex items-center justify-center space-x-4 text-sm">
              <button
                type="button"
                onClick={() => setShowRegistration(false)}
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Back to Login
              </button>
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="text-gray-600 hover:text-gray-500 font-medium"
                >
                  Back to Home
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  }

  // HOA selection handlers
  const handleHoaSelection = () => {
    // HOA selection successful - proceed to dashboard
    if (onSuccess) onSuccess();
    // Page will reload automatically via the auth system
  };

  const handleBackFromHoaSelection = () => {
    setShowHoaSelection(false);
    setHoaSelectionData(null);
  };

  // If showing HOA selection, render that instead
  if (showHoaSelection && hoaSelectionData) {
    return (
      <HOASelectionForm
        hoaOptions={hoaSelectionData.hoaOptions}
        email={hoaSelectionData.email}
        tempAuthToken={hoaSelectionData.tempAuthToken}
        onSelect={handleHoaSelection}
        onBack={handleBackFromHoaSelection}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            HOA Management Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to your community account
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={async () => {
                  const info = '🔐 PASSWORD RESET OPTIONS\n\n1. You can contact your HOA board or community manager and they can reset your password from their dashboard.\n\n2. Or you can request a temporary password to be emailed to you now.';
                  
                  alert(info);
                  
                  const userEmail = window.prompt("Enter the email associated with your HOA account to request a temporary password:");
                  if (!userEmail) return;
                  
                  try {
                    await api.post('/api/auth/self-reset-password', { email: userEmail });
                    alert("If an account exists for that email, a temporary password has been emailed to you. Please check your inbox (and spam folder).");
                  } catch (error) {
                    console.error('Self reset failed', error);
                    alert("We couldn't start the reset. Please contact your HOA board or platform support.");
                  }
                }}
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              onClick={() => console.log('🔴 SUBMIT BUTTON CLICKED!')}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
          
          <div className="flex items-center justify-center space-x-4 text-sm">
            {showRegistrationOption && (
              <button
                type="button"
                onClick={() => setShowRegistration(true)}
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Register as Homeowner
              </button>
            )}
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="text-gray-600 hover:text-gray-500 font-medium"
              >
                Back to Home
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper function to format dates in HOA timezone using Luxon
const formatDateInTimezone = (utcDateString, timezone = 'America/Chicago') => {
  if (!utcDateString) return { dateStr: 'N/A', timeStr: 'N/A', fullStr: 'N/A' };
  
  try {
    // Use Luxon for proper timezone handling
    const formatted = formatLocal(utcDateString, timezone, 'MMM d, yyyy h:mm a ZZZZ');
    
    if (!formatted) {
      return { dateStr: 'Invalid Date', timeStr: 'Invalid Date', fullStr: 'Invalid Date' };
    }
    
    // Split into date and time parts for compatibility
    const parts = formatted.split(' ');
    const dateStr = `${parts[0]} ${parts[1]} ${parts[2]}`; // "MMM d, yyyy"
    const timeStr = `${parts[3]} ${parts[4]} ${parts[5]}`; // "h:mm a ZZZZ"
    
    return {
      dateStr,
      timeStr,
      fullStr: formatted
    };
  } catch (error) {
    console.error('Error formatting date:', error, utcDateString);
    return { dateStr: 'Error', timeStr: 'Error', fullStr: 'Error' };
  }
};

// Dashboard Component
const Dashboard = ({ subscriptionTier = 'basic' }) => {
  const authUserGuard = true;
  const { user, logout } = useAuth();
  // Defensive guard to avoid null/undefined user during logout or session change
  const safeUser = user || { role: 'homeowner' };
  const { hoaData, homeowners, pendingApprovals, subscriptionTier: contextSubscriptionTier } = useHOA();
  const { activeHoaId } = useMultiHOA();
  
  // Use context subscription tier if no prop provided
  const effectiveSubscriptionTier = subscriptionTier !== 'basic' ? subscriptionTier : (contextSubscriptionTier || 'basic');
  // Use URL search params for main tab persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('view') || 'overview';
  
  const setActiveTab = (tab) => {
    // When changing main tab, preserve other params but update view
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', tab);
    // If switching away from settings, clear the settings tab param
    if (tab !== 'settings') {
      newParams.delete('tab');
    }
    setSearchParams(newParams);
  };
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [hoaSettings, setHoaSettings] = useState(null);
  const [boardMembers, setBoardMembers] = useState([]);

  // Fetch board members for overview tab
  useEffect(() => {
    const fetchBoardMembers = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!user?.id || !currentHoaId) return;
        const { data } = await api.get('/api/board/members');
        setBoardMembers(data?.board_members || []);
      } catch (e) {
        console.warn('Failed to load board members', e);
        setBoardMembers([]);
      }
    };
    
    const currentHoaId = activeHoaId || user?.hoa_id;
    if (user?.id && currentHoaId) {
      fetchBoardMembers();
    }
  }, [user?.id, activeHoaId, user?.hoa_id]);

  // Fetch HOA settings
  useEffect(() => {
    const fetchHoaSettings = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) return;
        
        const { data } = await api.get(`/api/hoa/${currentHoaId}/settings`);
        if (data.success) {
          setHoaSettings(data.settings);
        }
      } catch (error) {
        console.warn('Could not fetch HOA settings:', error);
        // Use default settings if fetch fails
        setHoaSettings({
          homeowner_support_tickets_enabled: false,
          support_ticket_expiry_date: null
        });
      }
    };

    const currentHoaId = activeHoaId || user?.hoa_id;
    if (currentHoaId) {
      fetchHoaSettings();
    }
  }, [activeHoaId, user?.hoa_id]);

  // Listen for tab switch events from Contact Board functionality
  useEffect(() => {
    const handleSwitchTab = (event) => {
      const { tab, chatId, submissionId } = event.detail;
      setActiveTab(tab);
      // Store chatId for CommunicationSystem to use
      if (chatId) {
        localStorage.setItem('selectedChatId', chatId);
      }
      
      if (submissionId) {
        setSelectedSubmissionId(submissionId);
      }
    };

    window.addEventListener('switchToTab', handleSwitchTab);
    return () => window.removeEventListener('switchToTab', handleSwitchTab);
  }, []);

  // Listen for document removal requests to navigate to support tickets
  useEffect(() => {
    const handleNavigateToSupport = (event) => {
      const { documentId, documentName } = event.detail;
      // Store document info for pre-filling support ticket
      sessionStorage.setItem('supportTicketDocId', documentId);
      sessionStorage.setItem('supportTicketDocName', documentName);
      // Navigate to support tickets tab
      setActiveTab('support_tickets');
    };

    window.addEventListener('navigate-to-support', handleNavigateToSupport);
    return () => window.removeEventListener('navigate-to-support', handleNavigateToSupport);
  }, []);

  const isBoardMember = (role) => {
    return ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(role);
  };

  // Check if support tickets should be enabled for homeowners
  const shouldShowSupportTickets = () => {
    // Board members always see support tickets
    if (isBoardMember(user?.role)) {
      return true;
    }
    
    // For homeowners, check HOA settings
    if (!hoaSettings) {
      return false; // Default to hidden if settings not loaded
    }
    
    const isEnabled = hoaSettings.homeowner_support_tickets_enabled;
    const expiryDate = hoaSettings.support_ticket_expiry_date;
    
    // If no expiry date set, just use enabled flag
    if (!expiryDate) {
      return isEnabled;
    }
    
    // Check if not expired
    const now = new Date();
    const expiry = new Date(expiryDate);
    return isEnabled && now < expiry;
  };

  // Helper to check if feature is available for current subscription tier
  const isFeatureAvailable = (minTier) => {
    if (!minTier) return true; // No restriction
    const tierOrder = ['basic', 'standard', 'premium'];
    const currentTierIndex = tierOrder.indexOf(effectiveSubscriptionTier.toLowerCase());
    const requiredTierIndex = tierOrder.indexOf(minTier.toLowerCase());
    return currentTierIndex >= requiredTierIndex;
  };

  const allTabs = [
    { id: 'overview', name: 'Overview', boardOnly: false, requiredPermission: null },
    { id: 'neighborhood', name: 'Neighborhood', boardOnly: false, requiredPermission: null },
    { id: 'documents', name: 'Documents', boardOnly: false, requiredPermission: 'documents' },
    { id: 'hoa-files-pro', name: 'HOA Files Pro', boardOnly: false, requiredPermission: null, minTier: 'standard' },
    { id: 'payments', name: 'Payments', boardOnly: false, requiredPermission: null },
    { id: 'budget', name: 'Budget & Finance', boardOnly: false, requiredPermission: 'financial_reports', reportsSubTabRestricted: true },
    { id: 'calendar', name: 'Community Calendar', boardOnly: false, requiredPermission: null },  // Everyone can see calendar
    { id: 'voting', name: 'Voting', boardOnly: false, requiredPermission: null },  // Everyone can see, but team members limited to polls
    { id: 'newsletter', name: 'Newsletter', boardOnly: false, requiredPermission: 'newsletter' },
    { id: 'violations', name: 'Violations', boardOnly: false, requiredPermission: null },
    { id: 'communication', name: 'Communications', boardOnly: false, requiredPermission: null },
    { id: 'hoa_track', name: 'HOA Track', boardOnly: false, requiredPermission: 'hoa_track' },
    { id: 'approvals', name: 'Approvals', boardOnly: true, requiredPermission: null },
    { id: 'settings', name: 'Settings', boardOnly: false, requiredPermission: null },
    { id: 'support_tickets', name: 'Support Tickets', boardOnly: false, customVisibility: shouldShowSupportTickets, requiredPermission: null }
  ];

  const visibleTabs = allTabs.filter(tab => {
    // Handle board-only tabs
    if (tab.boardOnly && !isBoardMember((user && user.role) || 'homeowner')) {
      return false;
    }
    
    // Handle subscription tier restrictions - grey out but still show
    // (greying out happens in rendering, not here)
    
    // Handle team member permission-based access (team_member, maintenance, security, staff)
    const teamRoles = ['team_member', 'maintenance', 'security', 'staff', 'attorney', 'accountant'];
    if (user && teamRoles.includes(user.role) && tab.requiredPermission) {
      // Load team member permissions from localStorage or user object
      const teamMember = hoaData?.team_members?.find(tm => tm.email === user.email);
      if (teamMember && teamMember.permissions) {
        if (!teamMember.permissions.includes(tab.requiredPermission)) {
          return false;
        }
      } else {
        // If no team member data found, hide tabs with required permissions
        return false;
      }
    }
    
    // Handle custom visibility logic (like support tickets)
    if (tab.customVisibility && typeof tab.customVisibility === 'function') {
      return tab.customVisibility();
    }
    
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              {/* HOA Logo small */}
              {(() => { try {
                const themeRaw = localStorage.getItem(`hoa_theme_${user?.hoa_id}`) || localStorage.getItem('branding_theme_fallback');
                if (!themeRaw) return null; const theme = JSON.parse(themeRaw); if (!theme.logo && !window.__serverBrandingLogoUrl) return null;
                const src = window.__serverBrandingLogoUrl || theme.logo; return <img src={src} alt="Logo" className="h-8 w-auto object-contain rounded" />;
              } catch(_) { return null; } })()}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  HOA Dashboard
                </h1>
                {hoaData?.name && (
                  <p className="text-lg text-gray-600 mt-1">
                    {hoaData.name}
                  </p>
                )}
              </div>
            </div>
            {/* Desktop: Full layout | Mobile: Condensed layout */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* HOA Switcher Dropdown - Shows if user has multiple HOAs */}
              <HOASelectorDropdown />
              
              {/* HOA/Property Selector - Commented out for now, less commonly used
              <div className="hidden sm:block">
                <HOAPropertySelector />
              </div>
              */}
              
              {/* User info - Hidden on mobile, visible on tablet+ */}
              <div className="hidden md:flex md:items-center md:space-x-2">
                <ProfilePicture user={user} size="md" />
                <span className="text-gray-700">
                  Welcome, {user?.first_name} {user?.last_name}
                </span>
              </div>
              
              {/* Mobile: Show only profile picture and logout */}
              <div className="flex md:hidden items-center space-x-2">
                <ProfilePicture user={user} size="sm" />
                <button
                  onClick={logout}
                  className="bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700 text-sm"
                >
                  Logout
                </button>
              </div>
              
              {/* Desktop: Regular logout button */}
              <button
                onClick={logout}
                className="hidden md:block bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Navigation */}
      <nav className="bg-white shadow hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center lg:justify-start space-x-2 lg:space-x-8 py-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-3 my-1 border-b-2 font-medium text-sm whitespace-nowrap rounded-t-md transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600 bg-indigo-50 !bg-indigo-50 !text-indigo-600 !border-indigo-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 !border-transparent'
                }`}
              >
                {tab.name}
                {tab.id === 'approvals' && pendingApprovals.length > 0 && (
                  <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {pendingApprovals.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Header with Menu */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">
          {visibleTabs.find(tab => tab.id === activeTab)?.name || 'Dashboard'}
        </h2>
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Mobile Navigation Sidebar */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-[99999999] md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowMobileMenu(false)}></div>
          <div className="fixed right-0 top-0 h-full w-64 bg-white shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* HOA Switcher in Mobile Menu */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <HOAPropertySelector />
            </div>
            
            <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 160px)' }}>
              <div className="space-y-2">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-left ${
                      activeTab === tab.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>{tab.name}</span>
                    {tab.id === 'approvals' && pendingApprovals.length > 0 && (
                      <span className="ml-auto bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                        {pendingApprovals.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content - MOBILE OPTIMIZED: Proper padding and overflow handling */}
      <main className="max-w-7xl mx-auto py-3 sm:py-6 px-2 sm:px-6 lg:px-8">
        <div className="sm:px-0">
          {activeTab === 'overview' && <OverviewTab user={user} hoaData={hoaData} homeowners={homeowners} boardMembers={boardMembers} pendingApprovals={pendingApprovals} setActiveTab={setActiveTab} />}
          {activeTab === 'neighborhood' && <WelcomeToNeighborhood user={user} isAdmin={isBoardMember(((user && user.role) || 'homeowner'))} />}
          {activeTab === 'documents' && <EnhancedDocumentsTab />}
          {activeTab === 'hoa-files-pro' && (
            effectiveSubscriptionTier === 'basic' ? (
              <div className="p-8 text-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg mx-4 my-8">
                <div className="max-w-2xl mx-auto">
                  <div className="text-6xl mb-4">📁</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">HOA Files Pro</h3>
                  <p className="text-gray-600 mb-6">
                    Advanced document management and organization features are available in Standard and Premium plans.
                  </p>
                  <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-3">Premium Features Include:</h4>
                    <ul className="text-left space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        Advanced folder organization and tagging
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        Document templates and automated workflows
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        Version control and audit trails
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        Enhanced security and permissions
                      </li>
                    </ul>
                  </div>
                  <button 
                    onClick={() => setActiveTab('settings')}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    Upgrade to Standard Plan
                  </button>
                </div>
              </div>
            ) : (
              <HOAFilesPro />
            )
          )}
          {activeTab === 'payments' && (
            <PaymentSystem 
              user={user}
              isBoard={isBoardMember(((user && user.role) || 'homeowner'))}
            />
          )}
          {activeTab === 'budget' && (
            <BudgetSystem 
              user={user}
              isBoard={isBoardMember(((user && user.role) || 'homeowner'))}
              subscriptionTier={effectiveSubscriptionTier}
            />
          )}
          {activeTab === 'calendar' && <CommunityEvents />}
          {activeTab === 'voting' && <VotingTab hoaName={hoaData?.name || 'HOA'} />}
          {activeTab === 'newsletter' && <NewsletterDashboard user={user} hoaData={hoaData} />}
          {activeTab === 'violations' && <ViolationSystem />}
          {activeTab === 'communication' && (
            <div className="communication-tab">
              <SchedulerSystem />
            </div>
          )}
          {activeTab === 'hoa_track' && <FormsTab selectedSubmissionId={selectedSubmissionId} subscriptionTier={effectiveSubscriptionTier} hoaSettings={hoaSettings} />}
          {activeTab === 'approvals' && <ApprovalsTab hoaData={hoaData} />}
          {activeTab === 'settings' && <SettingsTab />}
          
          {activeTab === 'support_tickets' && (
            <SupportTicketSystem />
          )}
        </div>
      </main>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ user, hoaData, homeowners = [], boardMembers = [], pendingApprovals = [], setActiveTab }) => {
  const { accountUsage } = useHOA();
  const { activeHoaId } = useMultiHOA();
  const storagePercentage = accountUsage.storage.limit_gb > 0 ? Math.round((accountUsage.storage.used_gb / accountUsage.storage.limit_gb) * 100) : 0;
  const userPercentage = accountUsage.users.limit > 0 ? Math.round((accountUsage.users.registered / accountUsage.users.limit) * 100) : 0;
  const isStorageNearLimit = storagePercentage >= 80;
  const isUserNearLimit = userPercentage >= 80;
  const isBoardMemberUser = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(user?.role);

  // Helper function to check if team member has access to a specific tab
  const hasTabAccess = (requiredPermission) => {
    // Board members have access to all tabs
    if (isBoardMemberUser) return true;
    
    // Regular homeowners don't need permission checks
    const teamRoles = ['team_member', 'maintenance', 'security', 'staff', 'attorney', 'accountant'];
    if (!teamRoles.includes(user?.role)) return true;
    
    // Check team member permissions
    const teamMember = hoaData?.team_members?.find(tm => tm.email === user?.email);
    if (!teamMember || !teamMember.permissions) return false;
    
    return teamMember.permissions.includes(requiredPermission);
  };

  // Committee memberships state and loading
  const [userCommittees, setUserCommittees] = useState([]);
  
  // User violations state and loading
  const [userViolations, setUserViolations] = useState([]);
  const [loadingViolations, setLoadingViolations] = useState(false);
  
  // Document storage usage state
  const [documentStorageUsage, setDocumentStorageUsage] = useState(null);
  const [loadingDocStorage, setLoadingDocStorage] = useState(false);
  
  // Pending approvals count - use the prop passed from parent
  const pendingApprovalsCount = pendingApprovals?.length || 0;
  
  // Load user's committee memberships using the correct API
  const loadUserCommittees = async () => {
    const currentHoaId = activeHoaId || user?.hoa_id;
    if (!currentHoaId) return;
    
    try {
      const { data } = await api.get(`/api/hoa/${currentHoaId}/committees`);
      const committees = Array.isArray(data.committees) ? data.committees : [];
      
      // Find committees where current user is a member
      const userMemberships = committees.filter(committee => 
        committee.members && committee.members.some(member => 
          member.email === user.email || member.id === user.id
        )
      );
      
      setUserCommittees(userMemberships);
    } catch (error) {
      console.error('Error loading user committees:', error);
      setUserCommittees([]);
    }
  };
  
  // Mark violation as viewed
  const markViolationViewed = async (violationId) => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      await api.put(`/api/violations/${currentHoaId}/${violationId}/mark-viewed`);
      // Refresh violations to remove notification
      loadUserViolations();
    } catch (error) {
      console.error('Error marking violation as viewed:', error);
    }
  };

  // Load contextually relevant violations for overview
  const loadUserViolations = async () => {
    const currentHoaId = activeHoaId || user?.hoa_id;
    if (!currentHoaId) return;
    
    setLoadingViolations(true);
    try {
      // Use new overview endpoint for contextually filtered violations
      const { data } = await api.get(`/api/violations/${currentHoaId}/overview`);
      const violations = Array.isArray(data.violations) ? data.violations : [];
      
      setUserViolations(violations);
    } catch (error) {
      console.error('Error loading user violations:', error);
      setUserViolations([]);
    } finally {
      setLoadingViolations(false);
    }
  };

  // Load document storage usage
  const loadDocumentStorageUsage = async () => {
    const currentHoaId = activeHoaId || user?.hoa_id;
    if (!currentHoaId) return;
    
    setLoadingDocStorage(true);
    try {
      const { data } = await api.get('/api/documents/storage-stats');
      // Transform to match expected format
      setDocumentStorageUsage({
        used_gb: data.total_gb,
        limit_gb: data.max_gb,
        percentage_used: (data.total_gb / data.max_gb) * 100,
        is_over_limit: data.total_gb > data.max_gb,
        is_approaching_limit: data.total_gb > (data.max_gb * 0.8),
        document_count: data.final_documents
      });
    } catch (error) {
      console.error('Error loading document storage usage:', error);
      setDocumentStorageUsage(null);
    } finally {
      setLoadingDocStorage(false);
    }
  };

  // Pending approvals count is now derived from the prop, no API call needed

  // Load committee memberships, violations, and storage when component mounts
  useEffect(() => {
    if (user?.hoa_id) {
      loadUserCommittees();
      loadUserViolations();
      loadDocumentStorageUsage();
      // Pending approvals count is now derived from the prop
    }
  }, [user?.hoa_id, activeHoaId, isBoardMemberUser]);

  // Get full role description including committee memberships
  const getFullRoleDescription = () => {
    const baseRole = user?.role?.replace('_', ' ') || 'Homeowner';
    const capitalizedBaseRole = baseRole.charAt(0).toUpperCase() + baseRole.slice(1);
    
    if (userCommittees.length === 0) {
      return capitalizedBaseRole;
    }
    
    const committeeMemberships = userCommittees.map(committee => 
      `${committee.name} member`
    );
    
    return [capitalizedBaseRole, ...committeeMemberships].join(', ');
  };

  // Quick Actions for homeowners (Contact Board)
  const contactBoard = async (message, isUrgent = false) => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.post(`/api/hoa/${currentHoaId}/contact-board`, {
        message: message,
        is_urgent: isUrgent,
        sender_id: user.id
      });

      if (data.success) {
        alert('Your message has been sent to the board successfully!');
        
        // If a chat was created, navigate to communications tab
        if (data.chat_id) {
          // Dispatch custom event to switch tabs
          window.dispatchEvent(new CustomEvent('switchToTab', {
            detail: { tab: 'communication', chatId: data.chat_id }
          }));
        }
      } else {
        alert('Failed to send message: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error contacting board:', error);
      alert('Unable to contact board at this time. Please try again later or contact your property management company directly.');
    }
  };
  
  // Read HOA-scoped theme or server branding for logo
  let logoUrl = null;
  try {
    const themeRaw = localStorage.getItem(`hoa_theme_${user?.hoa_id}`) || localStorage.getItem('branding_theme_fallback');
    if (themeRaw) {
      const theme = JSON.parse(themeRaw);
      logoUrl = theme.logo || null;
    }
    // If server branding is fetched, prefer it
    if (window.__serverBrandingLogoUrl) logoUrl = window.__serverBrandingLogoUrl;
  } catch (_) {}

  return (
    <div className="overview-tab space-y-6">
      {/* Welcome Message */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center space-x-4">
          {logoUrl && (
            <img src={logoUrl} alt="Community Logo" className="h-16 w-auto object-contain rounded-lg bg-white p-2" />
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{color: '#7c3aed'}}>
              Welcome to your community{hoaData?.name ? ` - ${hoaData.name}` : ''}
            </h1>
            <p className="text-sm mt-2" style={{color: '#a855f7'}}>
              Hello {user?.first_name} {user?.last_name}! Welcome to your community dashboard.
            </p>
            {hoaData?.tagline && (
              <p className="text-sm mt-1 italic" style={{color: '#a855f7'}}>"{hoaData.tagline}"</p>
            )}
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${isBoardMemberUser ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'} gap-6`}>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Community Members
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {(() => {
                      // Combine homeowners and board members, avoiding duplicates
                      const allMembers = new Set();
                      homeowners.forEach(h => allMembers.add(h.email || h.id));
                      boardMembers.forEach(b => allMembers.add(b.email || b.id));
                      return allMembers.size;
                    })()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {isBoardMemberUser && (
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 1v6m8-6v6" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Properties
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {accountUsage?.properties?.display || 'N/A'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}

        {isBoardMemberUser && (
          <>
            {/* Storage Usage Card - Now showing document storage (7-year compliance) */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-purple-500 p-2 rounded-md">
                      <span className="text-white text-sm">💾</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Document Storage
                        {documentStorageUsage && (
                          <span className="block text-xs text-gray-400 mt-0.5">7-year compliance</span>
                        )}
                      </dt>
                      {documentStorageUsage ? (
                        <dd className={`text-lg font-medium ${
                          documentStorageUsage.is_over_limit ? 'text-red-600' 
                          : documentStorageUsage.is_approaching_limit ? 'text-orange-600' 
                          : 'text-gray-900'
                        }`}>
                          {documentStorageUsage.used_gb.toFixed(2)} GB / {documentStorageUsage.limit_gb} GB
                        </dd>
                      ) : (
                        <dd className={`text-lg font-medium ${isStorageNearLimit ? 'text-red-600' : 'text-gray-900'}`}>
                          {accountUsage.storage.used_gb.toFixed(1)} GB / {accountUsage.storage.limit_gb} GB
                        </dd>
                      )}
                    </dl>
                  </div>
                </div>
                {/* Storage Progress Bar */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Used</span>
                    <span>
                      {documentStorageUsage 
                        ? `${Math.round(documentStorageUsage.percentage_used)}%` 
                        : `${storagePercentage}%`
                      }
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        documentStorageUsage 
                          ? (documentStorageUsage.is_over_limit ? 'bg-red-500' 
                            : documentStorageUsage.is_approaching_limit ? 'bg-orange-400' 
                            : 'bg-emerald-500')
                          : (isStorageNearLimit ? 'bg-red-500' 
                            : storagePercentage > 60 ? 'bg-yellow-500' 
                            : 'bg-green-500')
                      }`}
                      style={{ 
                        width: `${Math.min(
                          documentStorageUsage 
                            ? documentStorageUsage.percentage_used 
                            : storagePercentage, 
                          100
                        )}%` 
                      }}
                    ></div>
                  </div>
                  {documentStorageUsage && documentStorageUsage.document_count > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      {documentStorageUsage.document_count} archived documents
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* User Account Usage Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-blue-500 p-2 rounded-md">
                      <span className="text-white text-sm">👥</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">User Accounts</dt>
                      <dd className={`text-lg font-medium ${isUserNearLimit ? 'text-red-600' : 'text-gray-900'}`}>
                        {accountUsage.users.registered} / {accountUsage.users.limit}
                      </dd>
                    </dl>
                  </div>
                </div>
                {/* User Progress Bar */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Registered</span>
                    <span>{userPercentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        isUserNearLimit ? 'bg-red-500' : userPercentage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(userPercentage, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Welcome Card for Homeowners */}
        {!isBoardMemberUser && (
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm">🏠</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Your Role</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {getFullRoleDescription()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 quick-actions">
            {/* Make Payment - Available to all users */}
            <button
              onClick={() => {
                // Switch to payments tab
                window.dispatchEvent(new CustomEvent('switchToTab', {
                  detail: { tab: 'payments' }
                }));
              }}
              className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
              data-theme="primary"
            >
              <svg className="h-8 w-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              <div className="text-left">
                <p className="font-medium text-gray-900">Make Payment</p>
                <p className="text-sm text-gray-600">Pay HOA fees online</p>
              </div>
            </button>

            {/* Contact Board - Homeowners only - Now redirects to HOA Track General Request */}
            {!isBoardMemberUser && (
              <button
                onClick={() => {
                  // Switch to HOA Track tab and open Contact Board form
                  window.dispatchEvent(new CustomEvent('switchToTab', {
                    detail: { tab: 'hoa_track', formType: 'general_request' }
                  }));
                }}
                className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
                data-theme="primary"
              >
                <svg className="h-8 w-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-white">Contact Board</p>
                  <p className="text-sm text-white/90">Ask questions or report issues</p>
                </div>
              </button>
            )}

            {/* Meeting - Board Members ONLY (not homeowners, not team members) */}
            {isBoardMemberUser && (
              <button
                onClick={() => {
                  // Switch to voting tab and open Live Voting - BOD specifically
                  window.dispatchEvent(new CustomEvent('switchToTab', {
                    detail: { tab: 'voting' }
                  }));
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('switchToLiveVotingBOD'));
                  }, 200);
                }}
                className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
                data-theme="secondary"
              >
                <svg className="h-8 w-8 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Meeting</p>
                  <p className="text-sm text-gray-600">Start BOD live voting</p>
                </div>
              </button>
            )}

            {/* User Approvals - Board Members ONLY */}
            {isBoardMemberUser && (
              <button
                onClick={() => {
                  setActiveTab('approvals');
                }}
                className={`primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold relative ${
                  pendingApprovalsCount > 0 ? 'ring-2 ring-orange-500 ring-offset-2' : ''
                }`}
                data-theme={pendingApprovalsCount > 0 ? 'warning' : 'secondary'}
              >
                {pendingApprovalsCount > 0 && (
                  <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                    {pendingApprovalsCount}
                  </span>
                )}
                <svg className="h-8 w-8 text-orange-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-gray-900">User Approvals</p>
                  <p className="text-sm text-gray-600">
                    {pendingApprovalsCount > 0 ? `${pendingApprovalsCount} pending` : 'No pending'}
                  </p>
                </div>
              </button>
            )}

            {/* HOA Files Pro - Board Members only */}
            {isBoardMemberUser && (
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('switchToTab', {
                    detail: { tab: 'hoa-files-pro' }
                  }));
                }}
                className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
                data-theme="primary"
              >
                <svg className="h-8 w-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-white">HOA Files Pro</p>
                  <p className="text-sm text-white/90">Manage board documents</p>
                </div>
              </button>
            )}

            {/* Events - Available to all users + Team members with calendar_management */}
            {(isBoardMemberUser || !['team_member', 'maintenance', 'security', 'staff', 'attorney', 'accountant'].includes(user?.role) || hasTabAccess('calendar_management')) && (
              <button
                onClick={() => {
                  // Switch to calendar tab
                  window.dispatchEvent(new CustomEvent('switchToTab', {
                    detail: { tab: 'calendar' }
                  }));
                }}
                className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
                data-theme="primary"
              >
                <svg className="h-8 w-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-white">Events</p>
                  <p className="text-sm text-white/90">View community calendar</p>
                </div>
              </button>
            )}

            {/* View Documents - Available to all users + Team members with documents permission */}
            {(isBoardMemberUser || !['team_member', 'maintenance', 'security', 'staff', 'attorney', 'accountant'].includes(user?.role) || hasTabAccess('documents')) && (
              <button 
                onClick={() => {
                  // Switch to documents tab
                  window.dispatchEvent(new CustomEvent('switchToTab', {
                    detail: { tab: 'documents' }
                  }));
                }}
                className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
                data-theme="primary"
              >
                <svg className="h-8 w-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-white">View Documents</p>
                  <p className="text-sm text-white/90">CC&Rs, bylaws, and notices</p>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* HOA Track Widget - User's Recent Requests */}
      <HOATrackWidget />

      {/* Community Activity */}
      <CommunityActivityFeed user={user} />

      {/* User Violations Section - Only show if active violations exist */}
      {userViolations.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">
                {isBoardMemberUser ? 'Your Personal Violations' : 'Your Violations'}
              </h3>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {userViolations.length} Active
            </span>
          </div>
          
          <div className="space-y-4">
            {userViolations.map((violation) => (
              <div key={violation.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h4 className="font-medium text-gray-900 capitalize">
                        {violation.violation_type?.replace('_', ' ')} Violation
                      </h4>
                      {violation.has_new_activity && (
                        <span className="ml-2 animate-pulse" title="New activity">
                          🔔
                        </span>
                      )}
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        violation.severity === 'minor' ? 'bg-yellow-100 text-yellow-800' :
                        violation.severity === 'moderate' ? 'bg-orange-100 text-orange-800' :
                        violation.severity === 'severe' ? 'bg-red-100 text-red-800' :
                        'bg-red-200 text-red-900'
                      }`}>
                        {violation.severity?.toUpperCase()}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 mb-2">{violation.description}</p>
                    
                    {violation.property_address && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Property:</strong> {violation.property_address}
                      </p>
                    )}
                    
                    {violation.fine_amount && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-red-700">
                          <strong>Fine Amount:</strong> ${violation.fine_amount}
                          {violation.fine_due_date && (
                            <span className="text-gray-600 ml-2">
                              (Due: {new Date(violation.fine_due_date).toLocaleDateString()})
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                    
                    {(violation.hearing_requested || violation.hearing_scheduled) && (
                      <div className="mb-2">
                        {violation.hearing_scheduled ? (
                          <div className="text-sm text-blue-700">
                            <p className="font-medium mb-1">📅 Hearing Scheduled</p>
                            <p className="text-xs text-gray-600">
                              {violation.hearing_date ? new Date(violation.hearing_date).toLocaleDateString() : 'Date TBD'}
                              {violation.hearing_time && ` at ${violation.hearing_time}`}
                            </p>
                            {violation.hearing_location && (
                              <p className="text-xs text-gray-600">📍 {violation.hearing_location}</p>
                            )}
                            {violation.payments_paused && (
                              <p className="text-xs text-orange-600 italic">⏸️ Payments paused</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-orange-700">
                            <strong>Status:</strong> Hearing Requested
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4 flex flex-col gap-2">
                    <button
                      onClick={() => {
                        markViolationViewed(violation.id);
                        setActiveTab('violations');
                      }}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      View Details
                    </button>
                    
                    {violation.fine_amount && violation.status !== 'fine_paid' && (
                      <button
                        onClick={() => setActiveTab('payments')}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                      >
                        Pay Fine
                      </button>
                    )}
                    
                    {!violation.hearing_requested && violation.status !== 'resolved' && (
                      <button
                        onClick={async () => {
                          try {
                            const currentHoaId = activeHoaId || user?.hoa_id;
                            if (!currentHoaId) return;
                            
                            await api.put(`/api/violations/${currentHoaId}/${violation.id}/request-hearing`);
                            alert('Hearing request submitted successfully!');
                            loadUserViolations(); // Refresh violations
                          } catch (error) {
                            console.error('Error requesting hearing:', error);
                            alert('Error requesting hearing. Please try again.');
                          }
                        }}
                        className="inline-flex items-center px-3 py-1 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                      >
                        Request Hearing
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              {isBoardMemberUser 
                ? 'These are your personal violations as a homeowner, separate from your board responsibilities.'
                : 'Please review these violations and take appropriate action. Contact the board if you have questions.'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Documents Tab Component
const DocumentsTab = () => {
  const { user } = useAuth(); // FIXED: Use useAuth hook instead of useContext
  // FIXED: Add defensive guard like other components
  const safeUser = user || { role: 'homeowner' };
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('governing');
  const [selectedVisibility, setSelectedVisibility] = useState('all'); // NEW: Visibility state
  
  // Load documents from localStorage on component mount
  useEffect(() => {
    const savedDocs = JSON.parse(localStorage.getItem('hoa_documents') || '[]');
    setDocuments(savedDocs);
  }, []);

  // Filter documents based on user role - FIXED visibility logic and user guard
  const visibleDocuments = documents.filter(doc => {
    // FIXED: Use safeUser instead of direct user check
    
    // Board members can see all documents
    if (safeUser.role === 'board_member' || safeUser.role === 'president' || 
        safeUser.role === 'vice_president' || safeUser.role === 'treasurer' || 
        safeUser.role === 'secretary') {
      return true;
    }
    
    // Homeowners can only see documents with 'all' permission
    return doc.viewPermission === 'all';
  });

  const documentCategories = [
    { id: 'governing', name: 'Governing Documents', description: 'CC&Rs, Bylaws, Articles of Incorporation' },
    { id: 'financial', name: 'Financial Documents', description: 'Budgets, Financial Statements, Reserve Studies' },
    { id: 'policies', name: 'Policies & Rules', description: 'Rules & Regulations, Architectural Guidelines' },
    { id: 'legal', name: 'Legal Documents', description: 'Contracts, Legal Notices, Insurance Policies' },
    { id: 'minutes', name: 'Meeting Minutes', description: 'Board Meeting Minutes, Annual Meeting Minutes' },
    { id: 'maintenance', name: 'Maintenance Records', description: 'Maintenance Contracts, Service Records' }
  ];

  const handleDocumentUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingDocument(true);
    
    try {
      for (const file of files) {
        // Convert file to base64 for storage - FIXED async handling
        const fileData = await new Promise((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.onload = (e) => resolve(e.target.result);
          fileReader.onerror = (e) => reject(e);
          fileReader.readAsDataURL(file);
        });
        
        const newDocument = {
          id: Date.now() + Math.random(),
          name: file.name,
          category: selectedCategory,
          size: file.size,
          uploadDate: new Date().toISOString(),
          type: file.type,
          viewPermission: selectedVisibility, // FIXED: Use selected visibility
          fileData: fileData // Store file data for viewing/downloading
        };
        
        const updatedDocs = [...documents, newDocument];
        setDocuments(updatedDocs);
        localStorage.setItem('hoa_documents', JSON.stringify(updatedDocs));
      }
      
      alert(`✅ ${files.length} document(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('❌ Error uploading documents. Please try again.');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = (docId) => {
    const updatedDocs = documents.filter(doc => doc.id !== docId);
    setDocuments(updatedDocs);
    localStorage.setItem('hoa_documents', JSON.stringify(updatedDocs));
    alert('Document deleted successfully');
  };

  // NEW: Handle document viewing - FIXED parameter naming
  const handleViewDocument = (documentItem) => {
    if (documentItem.fileData) {
      // Open document in new tab
      const newWindow = window.open();
      if (documentItem.type.startsWith('image/')) {
        newWindow.document.write(`<img src="${documentItem.fileData}" style="max-width: 100%; height: auto;" alt="${documentItem.name}">`);
      } else if (documentItem.type === 'application/pdf') {
        newWindow.location.href = documentItem.fileData;
      } else {
        // For other file types, trigger download
        handleDownloadDocument(documentItem);
        newWindow.close();
      }
    } else {
      alert('Document data not available for viewing');
    }
  };

  // NEW: Handle document downloading - FIXED createElement issue
  const handleDownloadDocument = (documentItem) => {
    if (documentItem.fileData) {
      const link = window.document.createElement('a'); // Use window.document instead of document
      link.href = documentItem.fileData;
      link.download = documentItem.name;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } else {
      alert('Document data not available for download');
    }
  };

  return (
    <div className="space-y-6">
      {/* Document Upload */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">📤 Upload Documents</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {documentCategories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
              {/* Only show visibility selector to board members */}
              {(safeUser?.role === 'board_member' || safeUser?.role === 'president' || 
                safeUser?.role === 'vice_president' || safeUser?.role === 'treasurer' || 
                safeUser?.role === 'secretary') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visibility Permission</label>
                  <select
                    value={selectedVisibility}
                    onChange={(e) => setSelectedVisibility(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">All Homeowners</option>
                    <option value="board">Board Members Only</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedVisibility === 'board' ? 'Only board members can view' : 'All homeowners can view'}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Choose Files</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onChange={handleDocumentUpload}
                  disabled={uploadingDocument}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>
            </div>
            {uploadingDocument && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-3"></div>
                <span className="text-indigo-700">Uploading documents...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documentCategories.map(category => (
          <div key={category.id} className="bg-white shadow rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-900 mb-2">{category.name}</h5>
            <p className="text-xs text-gray-600 mb-3">{category.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {documents.filter(doc => doc.category === category.id).length} documents
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* All Documents */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">All Documents</h3>
          <div className="divide-y divide-gray-200">
            {visibleDocuments.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2">No documents available</p>
                <p className="text-sm text-gray-400">
                  {safeUser?.role === 'homeowner' 
                    ? 'No documents have been shared with homeowners yet.'
                    : 'Upload your first document to get started.'
                  }
                </p>
              </div>
            ) : (
              visibleDocuments.map(document => (
                <div key={document.id} className="py-4 flex items-center justify-between">
                  <div className="flex items-center cursor-pointer" onClick={() => handleViewDocument(document)}>
                    <div className="flex-shrink-0">
                      <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900 hover:text-indigo-600 transition-colors">
                        {document.name} 
                        <span className="ml-2 text-xs text-indigo-600">Click to view</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        {documentCategories.find(cat => cat.id === document.category)?.name} • 
                        {Math.round(document.size / 1024)} KB • 
                        {new Date(document.uploadDate).toLocaleDateString()} •
                        <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                          document.viewPermission === 'board' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {document.viewPermission === 'board' ? 'Board Only' : 'All Homeowners'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDocument(document);
                      }}
                      className="text-indigo-600 hover:text-indigo-800 text-sm px-3 py-1 border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors"
                    >
                      View
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadDocument(document);
                      }}
                      className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-300 rounded-md hover:bg-green-50 transition-colors"
                    >
                      Download
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDocument(document.id);
                      }}
                      className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Approvals Tab Component  
const ApprovalsTab = ({ hoaData }) => {
  const { pendingApprovals, approveUser, rejectUser } = useHOA();
  const { user } = useContext(AuthContext);
  const [activeApprovalTab, setActiveApprovalTab] = useState('homeowners');
  const [pendingTeamUsers, setPendingTeamUsers] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [archivedHomeowners, setArchivedHomeowners] = useState([]);
  const [archivedTeamUsers, setArchivedTeamUsers] = useState([]);
  const [archivedDocuments, setArchivedDocuments] = useState([]);
  const [rejectingUserId, setRejectingUserId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [conditionalApprovalUserId, setConditionalApprovalUserId] = useState(null);
  const [conditionalReasons, setConditionalReasons] = useState({
    nameMatch: false,
    addressVerification: false,
    expiredCode: false
  });
  const [visHelpOpen, setVisHelpOpen] = useState({}); // tooltip state per approval id

  // Approvals Document Details cache and inline edit/comment states
  const [docDetails, setDocDetails] = useState({}); // { [id]: approval }
  const [commentInputs, setCommentInputs] = useState({}); // { [id]: string }
  const [editNotes, setEditNotes] = useState({}); // { [id]: string }
  const [editActionItems, setEditActionItems] = useState({}); // { [id]: string multiline }
  const [editVisibility, setEditVisibility] = useState({}); // { [id]: { attendance, motions, discussions, notes, actionItems } }
  const [editStartTime, setEditStartTime] = useState({}); // { [id]: string }
  const [editEndTime, setEditEndTime] = useState({}); // { [id]: string }
  
  // HOA settings for meeting minutes (same as VotingTab)
  const [hoaSettings, setHoaSettings] = useState({
    hoa_name: '',
    mailing_address: '',
    hoa_email: '',
    phone: '',
    website: ''
  });

  const loadApprovalDetails = async (approvalId) => {
    try {
      const r = await api.get(`/api/approvals/meeting-minutes/${approvalId}`);
      const approval = r.data?.approval || {};
      setDocDetails(prev => ({ ...prev, [approvalId]: approval }));
      // prime edit states
      setEditNotes(prev => ({ ...prev, [approvalId]: approval.meeting_notes || '' }));
      setEditActionItems(prev => ({ 
        ...prev, 
        [approvalId]: (approval.action_items || []).map(item => 
          typeof item === 'string' ? item : (item.text || item.title || item.description || String(item))
        ).join('\n') 
      }));
      setEditVisibility(prev => ({ ...prev, [approvalId]: approval.section_visibility || { attendance: true, proofOfNotice: true, rulesOfConduct: true, priorBusiness: true, manualPriorMinutes: true, motions: true, discussions: true, notes: true, actionItems: true } }));
      setEditStartTime(prev => ({ ...prev, [approvalId]: approval.start_time || '' }));
      setEditEndTime(prev => ({ ...prev, [approvalId]: approval.end_time || '' }));
    } catch (e) {
      console.error('Failed to load approval details', e);
    }
  };

  // Load HOA settings for meeting minutes (same as VotingTab)
  const loadHoaSettings = async () => {
    try {
      const response = await api.get(`/api/hoa/${user?.hoa_id}/settings`);
      if (response.data?.success && response.data?.settings) {
        setHoaSettings(response.data.settings);
        console.log('[HOA SETTINGS] Loaded:', response.data.settings);
        return response.data.settings;
      }
    } catch (error) {
      console.error('Error loading HOA settings for meeting minutes:', error);
    }
  };

  const postApprovalComment = async (approvalId) => {
    const text = (commentInputs[approvalId] || '').trim();
    if (!text) return;
    try {
      const r = await api.post(`/api/approvals/meeting-minutes/${approvalId}/comment`, { text });
      const comment = r.data?.comment;
      if (comment) {
        setDocDetails(prev => {
          const cur = prev[approvalId] || {};
          const comments = cur.comments ? [...cur.comments, comment] : [comment];
          return { ...prev, [approvalId]: { ...cur, comments } };
        });
        setCommentInputs(prev => ({ ...prev, [approvalId]: '' }));
      }
    } catch (e) {
      console.error('Failed to post comment', e);
      alert('Failed to add comment');
    }
  };

  const saveApprovalEdits = async (approvalId) => {
    try {
      const visibility = editVisibility[approvalId] || {};
      const payload = {
        meeting_notes: editNotes[approvalId] || '',
        action_items: (editActionItems[approvalId] || '').split('\n').map(s => s.trim()).filter(Boolean),
        section_visibility: visibility,
        start_time: editStartTime[approvalId] || null,
        end_time: editEndTime[approvalId] || null
      };
      const r = await api.patch(`/api/approvals/meeting-minutes/${approvalId}`, payload);
      const updated = r.data?.approval;
      if (updated) {
        setDocDetails(prev => ({ ...prev, [approvalId]: updated }));
        alert('Changes saved to this approval.');
      }
    } catch (e) {
      console.error('Failed to save approval edits', e);
      alert('Failed to save changes.');
    }
  };

  const finalizeApprovedMinutes = async (approvalId) => {
    try {
      // Ensure HOA settings are loaded before generating HTML
      if (!hoaSettings || !hoaSettings.hoa_name) {
        console.log('[FINALIZE] HOA settings not loaded, loading now...');
        await loadHoaSettings();
      }
      
      // Prefer canonical finalize; build HTML from details if available, else from basic doc
      const approval = docDetails[approvalId] || pendingDocuments.find(d => d.id === approvalId) || {};
      console.log('[FINALIZE] Approval data:', {
        id: approvalId,
        has_attendance: !!approval.attendance,
        attendance_count: approval.attendance?.length,
        has_homeowners: !!approval.homeowners,
        homeowners_count: approval.homeowners?.length,
        has_boardMembers: !!approval.boardMembers,
        boardMembers_count: approval.boardMembers?.length,
        has_meeting_location: !!approval.meeting_location,
        section_visibility: approval.section_visibility
      });
      console.log('[FINALIZE] Full approval object keys:', Object.keys(approval));
      console.log('[FINALIZE] Homeowners data:', approval.homeowners);
      console.log('[FINALIZE] BoardMembers data:', approval.boardMembers);
      const htmlBody = generateFullMeetingMinutesPreview(approval);
      const htmlPage = `<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>${(approval.meeting_type || 'Board')} Minutes</title></head><body>${htmlBody}</body></html>`;
      
      // CRITICAL FIX: Pass combined packet flags from approval to finalize endpoint
      // This ensures the backend knows to merge the minutes PDF with the combined attachments packet
      const finalizePayload = {
        title: `${approval.meeting_type || 'Board'} Meeting Minutes - ${approval.meeting_date || new Date().toISOString().split('T')[0]}`,
        html_content: htmlPage,
        is_board_only: false,
        // FIX: Include combined packet information from the approval record
        use_combined_packet: approval.use_combined_packet || false,
        combined_packet_id: approval.combined_packet_id || null
      };
      
      console.log('[APPROVALS] Finalizing minutes with payload:', {
        approval_id: approvalId,
        has_html: !!htmlPage,
        use_combined_packet: finalizePayload.use_combined_packet,
        combined_packet_id: finalizePayload.combined_packet_id
      });
      
      const res = await api.post(`/api/approvals/meeting-minutes/${approvalId}/finalize`, finalizePayload);
      if (res.data?.success) {
        // Ensure it's indexed for Documents tab visibility regardless of backend collection
        try {
          await api.post('/api/documents/index', {
            document_id: res.data.document_id,
            category: 'meeting_minutes',
            hoa_id: user.hoa_id,
            source: 'minutes_approvals'
          });
        } catch (e) {
          console.warn('Index API call failed (non-fatal):', e);
        }
        await loadPendingDocuments();
        return true;
      }
    } catch (e) {
      console.warn('Canonical finalize failed, will fallback to legacy if available.', e);
    }
    return false;
  };


  // Load pending team users and archived items
  useEffect(() => {
    if (user?.hoa_id) {
      loadPendingTeamUsers();
      loadPendingDocuments();
      loadHoaSettings(); // Load HOA settings for meeting minutes
      if (activeApprovalTab === 'archive') {
        loadArchivedHomeowners();
        loadArchivedTeamUsers();
        loadArchivedDocuments();
      }
    }
  }, [user?.hoa_id, activeApprovalTab]);

  const loadPendingTeamUsers = async () => {
    try {
      const response = await api.get('/api/team-users/pending', {
        params: { hoa_id: user.hoa_id }
      });
      setPendingTeamUsers(response.data.pending_users || []);
    } catch (error) {
      console.error('Failed to load pending team users:', error);
    }
  };

  const loadPendingDocuments = async () => {
    try {
      // Load ONLY from document_approvals - the canonical source
      let response = await api.get('/api/vote/pending-approvals');
      let list = response.data?.approvals || [];
      
      // CRITICAL: Filter out any approved/rejected documents on frontend side too
      list = list.filter(doc => 
        doc.status === 'pending_approval' || 
        doc.status === 'pending' || 
        !doc.status // Include if status is missing
      );
      
      console.log(`Loaded ${list.length} pending documents from backend`);
      console.log('First document data:', list[0]); // DEBUG: See what fields we actually have
      
      setPendingDocuments(list);
    } catch (error) {
      console.error('Failed to load pending documents:', error);
      setPendingDocuments([]);
    }
  };

  const loadArchivedHomeowners = async () => {
    try {
      // Fetch archived (removed) homeowners from archived_users collection
      const response = await api.get('/api/users/archived-homeowners');
      setArchivedHomeowners(response.data.archived_homeowners || []);
    } catch (error) {
      console.error('Failed to load archived homeowners:', error);
    }
  };

  const loadArchivedTeamUsers = async () => {
    try {
      const response = await api.get('/api/team-users/archived');
      setArchivedTeamUsers(response.data.archived_users || []);
    } catch (error) {
      console.error('Failed to load archived team users:', error);
    }
  };

  const loadArchivedDocuments = async () => {
    try {
      // Fetch approved/rejected/signed documents
      let response = await api.get('/api/vote/archived-approvals');
      let list = response.data?.approvals || [];
      if (!list || list.length === 0) {
        const r2 = await api.get('/api/approvals/archived');
        list = r2.data?.approvals || [];
      }
      setArchivedDocuments(list);
    } catch (error) {
      console.error('Failed to load archived documents:', error);
    }
  };

  const approveTeamUser = async (userId) => {
    try {
      await api.post(`/api/team-users/${userId}/approve`, {}, {
        params: { hoa_id: user.hoa_id }
      });
      await loadPendingTeamUsers();
      alert('Team member approved successfully!');
    } catch (error) {
      console.error('Failed to approve team user:', error);
      alert('Failed to approve team member. Please try again.');
    }
  };

  const rejectTeamUser = async (userId, reason) => {
    try {
      await api.post(`/api/team-users/${userId}/reject`, {
        reason: reason
      }, {
        params: { hoa_id: user.hoa_id }
      });
      await loadPendingTeamUsers();
      alert('Team member rejected successfully.');
    } catch (error) {
      console.error('Failed to reject team user:', error);
      alert('Failed to reject team member. Please try again.');
    }
  };

  const approveDocument = async (approvalId, userSignature) => {
    try {
      // Get feedback from textarea
      const feedbackElement = document.getElementById(`feedback-${approvalId}`);
      const feedback = feedbackElement ? feedbackElement.value : '';
      
      const response = await api.post(`/api/vote/approve-minutes/${approvalId}`, {
        decision: "approved",
        comments: feedback ? `${feedback}\n\nElectronically signed by: ${userSignature}` : `Electronically signed by: ${userSignature}`,
        signature: userSignature
      });
      
      // IMMEDIATELY remove from pending list in UI
      console.log('Removing approval ID:', approvalId, 'from pending documents');
      setPendingDocuments(prev => {
        const filtered = prev.filter(doc => doc.id !== approvalId && doc._id !== approvalId);
        console.log('Before filter:', prev.length, 'After filter:', filtered.length);
        return filtered;
      });
      
      // If fully approved, generate and save the signed PDF
      if (response.data.status === 'approved') {
        await generateAndSaveSignedPDF(approvalId, userSignature);
      }
      
      // Reload to sync with backend
      await loadPendingDocuments();
      alert(`Document approved and signed successfully! ${response.data.status === 'approved' ? 'All required signatures collected. PDF has been generated and saved to Documents.' : 'Waiting for additional signatures.'}`);
    } catch (error) {
      console.error('Failed to approve document:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to approve document: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Helper function to generate file URL for supporting documents
  const getDocumentFileUrl = (docId) => {
    const token = localStorage.getItem('hoa_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001');
    return `${backendUrl}/api/vote/supporting-document-file/${docId}?token=${encodeURIComponent(token)}`;
  };

  const generateAndSaveSignedPDF = async (approvalId, signature) => {
    try {
      // Generate the signed PDF and save it to documents with proper indexing
      const response = await api.post(`/api/vote/generate-signed-minutes/${approvalId}`, {
        signature: signature,
        generate_pdf: true,
        save_to_documents: true
      });
      
      if (response.data.success) {
        const currentHoaId = user?.hoa_id;
        // Ensure it's properly indexed in the documents system
        try {
          await api.post('/api/documents/index', {
            document_id: response.data.document_id,
            category: 'meeting_minutes',
            hoa_id: currentHoaId,
            source: 'minutes_archive'
          });
        } catch (indexErr) {
          console.warn('Failed to index document (non-fatal):', indexErr);
        }
        
        console.log('Signed PDF generated, saved, and indexed in documents successfully');
      }
    } catch (error) {
      console.error('Failed to generate signed PDF:', error);
    }
  };

  // Generate full meeting minutes preview for approval (includes Called to Order, Rules, Adjournment)
  const generateFullMeetingMinutesPreview = (doc) => {
    // Parse date without timezone shift - treat as local date
    const dateStr = doc.meeting_date || new Date().toISOString().split('T')[0];
    const [year, month, day] = dateStr.split('-');
    const currentDate = new Date(year, month - 1, day).toLocaleDateString();
    
    // Meeting type label - expand abbreviations
    const meetingTypeRaw = doc.meeting_type || 'Board Meeting';
    let meetingTypeLabel = meetingTypeRaw;
    
    // Expand common abbreviations
    if (meetingTypeRaw === 'BOD' || meetingTypeRaw === 'bod') {
      meetingTypeLabel = 'Board of Directors Meeting';
    } else if (meetingTypeRaw === 'ARC' || meetingTypeRaw === 'arc') {
      meetingTypeLabel = 'Architectural Review Committee Meeting';
    } else if (meetingTypeRaw === 'Regular') {
      meetingTypeLabel = 'Regular Board Meeting';
    } else if (meetingTypeRaw === 'Special') {
      meetingTypeLabel = 'Special Board Meeting';
    }
    
    // Use hoaSettings (same as VotingTab) instead of hoaData
    console.log('[PREVIEW DEBUG] hoaSettings:', hoaSettings);
    console.log('[PREVIEW DEBUG] doc.meeting_location:', doc.meeting_location);
    console.log('[PREVIEW DEBUG] doc.attendance:', doc.attendance);
    console.log('[PREVIEW DEBUG] doc.homeowners:', doc.homeowners);
    console.log('[PREVIEW DEBUG] doc.boardMembers:', doc.boardMembers);
    console.log('[PREVIEW DEBUG] doc.section_visibility:', doc.section_visibility);
    const startTime = doc.start_time || 'Not recorded';
    const endTime = doc.end_time || 'Not recorded';
    const vis = doc.section_visibility || { attendance: true, proofOfNotice: true, rulesOfConduct: true, priorBusiness: true, manualPriorMinutes: true, motions: true, discussions: true, notes: true, actionItems: true };
    
    // CRITICAL FIX: ALWAYS include attendance/roll call in finalized documents regardless of visibility settings
    // This ensures roll call appears in the final approved document
    const forceShowAttendance = true;
    
    // Fallback for old approval records without new fields
    const rules = doc.rules_of_conduct || [];
    const proofOfNotice = doc.proof_of_notice || {};
    const manualPriorMinutes = doc.manual_prior_minutes || '';
    const attendance = doc.attendance || [];
    
    return `
      <div style="font-family: 'Times New Roman', serif; line-height: 1.8; font-size: 16px;">
        <div style="text-align: center; border-bottom: 3px double #333; padding-bottom: 25px; margin-bottom: 35px;">
          <h1 style="font-size: 28px; margin: 0; font-weight: bold;">${meetingTypeLabel.toUpperCase()}</h1>
          <h2 style="font-size: 22px; margin: 15px 0 10px 0; font-weight: bold;">${hoaSettings?.hoa_name || hoaSettings?.name || 'HOMEOWNERS ASSOCIATION'}</h2>
          ${hoaSettings?.mailing_address ? `<p style=\"margin: 6px 0; font-size: 14px;\"><strong>Address:</strong> ${hoaSettings.mailing_address}</p>` : ''}
          ${hoaSettings?.hoa_email ? `<p style=\"margin: 6px 0; font-size: 14px;\"><strong>Email:</strong> ${hoaSettings.hoa_email}</p>` : ''}
          <p style="margin: 8px 0; font-size: 16px;"><strong>Date:</strong> ${currentDate}</p>
          <p style="margin: 8px 0; font-size: 16px;"><strong>Meeting Type:</strong> ${meetingTypeLabel}</p>
        </div>

        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">MEETING CALLED TO ORDER</h3>
          <div style="margin-left: 25px;">
            <p style="margin: 8px 0; font-size: 16px;"><strong>Called to Order:</strong> ${startTime}</p>
          </div>
        </div>

        ${doc.meeting_location && (vis.meetingLocation !== false) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">MEETING LOCATION</h3>
          <div style="margin-left: 25px;">
            <p style="margin: 8px 0; font-size: 16px;">${doc.meeting_location}</p>
          </div>
        </div>
        ` : ''}

        ${proofOfNotice && (vis.proofOfNotice !== false) && (proofOfNotice.postedOnEvents || proofOfNotice.emailSent || proofOfNotice.mailPacket || proofOfNotice.other) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">PROOF OF NOTICE</h3>
          <div style="margin-left: 25px;">
            ${proofOfNotice.postedOnEvents || proofOfNotice.emailSent ? `<p style="margin: 8px 0; font-size: 16px;">✓ Posted on events calendar and emailed to all homeowners</p>` : ''}
            ${proofOfNotice.mailPacket ? `<p style="margin: 8px 0; font-size: 16px;">✓ Mail packet ${proofOfNotice.mailPacketDate ? `(Date: ${proofOfNotice.mailPacketDate})` : ''}</p>` : ''}
            ${proofOfNotice.other && proofOfNotice.otherNotes ? `<p style="margin: 8px 0; font-size: 16px;">✓ Other: ${proofOfNotice.otherNotes}</p>` : ''}
          </div>
        </div>
        ` : ''}

        ${((forceShowAttendance || vis.attendance !== false) && (attendance && attendance.length > 0 || doc.homeowners && doc.homeowners.length > 0)) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">ROLL CALL</h3>
          <div style="margin-left: 25px;">
            <p style="margin: 8px 0; font-size: 16px; font-weight: bold;">Members Present:</p>
            <ol style="margin-left: 20px; font-size: 16px;">
              ${(() => {
                let members = [];
                
                // 1. Add board members from attendance array
                if (attendance && attendance.length > 0) {
                  members = members.concat(attendance.filter(m => {
                    const isPresent = m.present === true || m.present === 'true' || m.present === 'True';
                    const isBoard = m.role && ['president', 'vice_president', 'secretary', 'treasurer', 'board_member'].includes(m.role.toLowerCase());
                    return isPresent && isBoard;
                  }).map(member => {
                    const memberName = member.name || member.user_id || 'Unknown';
                    const role = member.role ? member.role.replace(/_/g, ' ').toUpperCase() : 'BOARD MEMBER';
                    return '<li style="margin: 6px 0;">' + memberName + ' - ' + role + '</li>';
                  }));
                }
                
                // 2. Add homeowners from homeowners array (if available)
                if (doc.homeowners && doc.homeowners.length > 0) {
                  const presentHomeowners = doc.homeowners.filter(h => {
                    const isPresent = h.present === true || h.present === 'true' || h.present === 'True' || h.attendance_status === true;
                    const isRegularHomeowner = !h.type || h.type === 'homeowner';
                    return isPresent && isRegularHomeowner;
                  });
                  
                  members = members.concat(presentHomeowners.map(h => {
                    const memberName = h.name || 'Unknown';
                    const propertyId = h.property_identifier || h.unit || h.lot || h.property_number || '';
                    const displayLabel = propertyId ? ` (Lot ${propertyId})` : '';
                    return '<li style="margin: 6px 0;">' + memberName + displayLabel + ' - Homeowner</li>';
                  }));
                  
                  // 3. Add external attendees
                  const presentExternal = doc.homeowners.filter(h => {
                    const isPresent = h.present === true || h.present === 'true' || h.present === 'True' || h.attendance_status === true;
                    return isPresent && h.type === 'external';
                  });
                  
                  members = members.concat(presentExternal.map(h => {
                    const memberName = h.name || 'Unknown';
                    const roleLabels = {
                      attorney: 'Attorney',
                      accountant: 'Accountant',
                      staff: 'Staff',
                      maintenance: 'Maintenance',
                      security: 'Security',
                      other: 'Other'
                    };
                    const roleLabel = roleLabels[h.externalRole] || 'External Attendee';
                    return '<li style="margin: 6px 0;">' + memberName + ' - ' + roleLabel + '</li>';
                  }));
                  
                  // 4. Add team members
                  const presentTeam = doc.homeowners.filter(h => {
                    const isPresent = h.present === true || h.present === 'true' || h.present === 'True' || h.attendance_status === true;
                    return isPresent && h.type === 'team_member';
                  });
                  
                  members = members.concat(presentTeam.map(h => {
                    const memberName = h.name || 'Unknown';
                    const roleLabels = {
                      attorney: 'Attorney',
                      accountant: 'Accountant',
                      staff: 'Staff',
                      other: 'Team Member'
                    };
                    const roleLabel = roleLabels[h.role] || 'Team Member';
                    return '<li style="margin: 6px 0;">' + memberName + ' - ' + roleLabel + '</li>';
                  }));
                }
                
                // Fallback: if no homeowners array, use attendance array for all members
                if ((!doc.homeowners || doc.homeowners.length === 0) && attendance && attendance.length > 0) {
                  const nonBoardMembers = attendance.filter(m => {
                    const isPresent = m.present === true || m.present === 'true' || m.present === 'True';
                    const isBoard = m.role && ['president', 'vice_president', 'secretary', 'treasurer', 'board_member'].includes(m.role.toLowerCase());
                    return isPresent && !isBoard;
                  }).map(member => {
                    const memberName = member.name || member.user_id || 'Unknown';
                    if (memberName.startsWith('external_') && /\d{10,}/.test(memberName)) {
                      return '';
                    }
                    if (member.type === 'external') {
                      const roleLabels = {
                        attorney: 'Attorney',
                        accountant: 'Accountant',
                        staff: 'Staff',
                        maintenance: 'Maintenance',
                        security: 'Security',
                        other: 'Other'
                      };
                      const roleLabel = roleLabels[member.externalRole] || 'External Attendee';
                      return '<li style="margin: 6px 0;">' + memberName + ' - ' + roleLabel + '</li>';
                    }
                    const propertyId = member.property_identifier || member.unit || member.lot || member.property_number || '';
                    const displayLabel = propertyId ? ` (Lot ${propertyId})` : '';
                    return '<li style="margin: 6px 0;">' + memberName + displayLabel + ' - Homeowner</li>';
                  }).filter(line => line !== '');
                  members = members.concat(nonBoardMembers);
                }
                
                return members.length > 0 ? members.join('') : '<li style="margin: 6px 0;">No members present</li>';
              })()}
            </ol>
          </div>
        </div>
        ` : ''}

        ${(vis.rulesOfConduct !== false) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">RULES OF THE MEETING</h3>
          <ol style="margin-left: 35px; font-size: 16px;">
            ${(rules && rules.length > 0 
               ? rules 
               : [
                 "Members allotted 3 minutes to speak on agenda items during 'Open to the Floor'.",
                 "Board members have no time restriction for responses.",
                 "Speakers state name and address/identifier; speak calmly; no yelling/profanity/smearing.",
                 "Outbursts or speaking out of turn may lead to removal; legal means will be used if necessary.",
                 `To transact such other business as may properly come before the ${meetingType === 'ARC' ? 'ARC' : meetingType === 'Grievance Committee' ? 'Grievance Committee' : 'Board'} or any adjournment or postponement thereof.`
               ]).map(rule => `<li style="margin: 6px 0;">${rule}</li>`).join('')}
          </ol>
        </div>
        ` : ''}

        ${((vis.priorBusiness !== false || vis.manualPriorMinutes !== false) && (doc.prior_minutes_approval || manualPriorMinutes)) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">PRIOR BUSINESS</h3>
          <div style="margin-left: 25px;">
            ${(vis.priorBusiness !== false && doc.prior_minutes_approval) ? `
            <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
              <h4 style="font-weight: bold; margin-bottom: 10px; font-size: 18px;">Approval of Prior Meeting Minutes</h4>
              <p style="margin-bottom: 10px; font-size: 16px;"><strong>Prior Minutes:</strong> ${doc.prior_minutes_approval.title || 'Prior Meeting Minutes'}</p>
              <p style="margin-bottom: 10px; font-size: 16px;"><strong>Date:</strong> ${doc.prior_minutes_approval.date || 'Unknown'}</p>
              <p style="margin-bottom: 10px; font-size: 16px;"><em>Motion to approve the prior meeting minutes as presented.</em></p>
              <p style="font-weight: bold; font-size: 16px;">Status: <span style="color: ${doc.prior_minutes_approval.status === 'approved' ? 'green' : doc.prior_minutes_approval.status === 'rejected' ? 'red' : 'orange'};">${(doc.prior_minutes_approval.status || 'PENDING').toUpperCase()}</span></p>
              ${doc.prior_minutes_approval.votes ? `<p style="font-size: 16px; margin-top: 8px;">Votes - Approve: ${doc.prior_minutes_approval.votes.approve || 0}, Oppose: ${doc.prior_minutes_approval.votes.oppose || 0}, Abstain: ${doc.prior_minutes_approval.votes.abstain || 0}</p>` : ''}
            </div>
            ` : ''}
            ${(vis.manualPriorMinutes !== false && manualPriorMinutes) ? `
            <div style="margin-top: 15px; padding: 15px; background-color: #f0f8ff; border-left: 4px solid #007cba; border-radius: 5px;">
              <p style="font-weight: bold; margin-bottom: 10px; font-size: 16px;">Additional Prior Meeting Minutes Notes:</p>
              <p style="font-size: 16px; line-height: 1.6; white-space: pre-wrap;">${manualPriorMinutes}</p>
            </div>
            ` : ''}
          </div>
        </div>
        ` : ''}

        ${(vis.motions !== false && doc.motions && doc.motions.length > 0) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">CURRENT BUSINESS</h3>
          <div style="margin-left: 25px;">
            ${doc.motions.map((motion, idx) => `
              <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
                <h4 style="font-weight: bold; margin-bottom: 10px; font-size: 18px;">Motion ${idx + 1}: ${motion.title}</h4>
                <p style="margin-bottom: 10px; font-size: 16px;">${motion.description || ''}</p>
                ${motion.discussion ? `<p style="margin-bottom: 10px; font-size: 16px;"><strong>Discussion:</strong> ${motion.discussion}</p>` : ''}
                ${(motion.voting_rule === 'propose_second' || motion.proposer_name || motion.seconder_name) ? `
                  <p style="margin-bottom: 8px; font-size: 16px; color: #6b21a8;"><strong>Proposed by:</strong> ${motion.proposer_name || 'Not set'}</p>
                  <p style="margin-bottom: 10px; font-size: 16px; color: #6b21a8;"><strong>Seconded by:</strong> ${motion.seconder_name || 'Not set'}</p>
                ` : ''}
                <p style="font-weight: bold; font-size: 16px;">Status: <span style="color: ${motion.status === 'approved' ? 'green' : motion.status === 'rejected' ? 'red' : 'orange'};">${(motion.status || 'PENDING').toUpperCase()}</span></p>
                ${motion.votes ? `<p style="font-size: 16px; margin-top: 8px;">Votes - Approve: ${motion.votes.approve || 0}, Oppose: ${motion.votes.oppose || 0}, Abstain: ${motion.votes.abstain || 0}</p>` : ''}
                ${motion.individual_votes ? `
                  <div style="margin-top: 12px; padding: 10px; background-color: #f9fafb; border-radius: 4px; font-size: 14px;">
                    ${motion.individual_votes.approve && motion.individual_votes.approve.length > 0 ? `
                      <p style="margin: 5px 0;"><strong>Voted For:</strong> ${motion.individual_votes.approve.map(v => v.name).join(', ')}</p>
                    ` : ''}
                    ${motion.individual_votes.oppose && motion.individual_votes.oppose.length > 0 ? `
                      <p style="margin: 5px 0;"><strong>Voted Against:</strong> ${motion.individual_votes.oppose.map(v => v.name).join(', ')}</p>
                    ` : ''}
                    ${motion.individual_votes.abstain && motion.individual_votes.abstain.length > 0 ? `
                      <p style="margin: 5px 0;"><strong>Abstained:</strong> ${motion.individual_votes.abstain.map(v => v.name).join(', ')}</p>
                    ` : ''}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>
        </div>
        ` : ''}

        ${(vis.discussions !== false && doc.discussions && doc.discussions.length > 0) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">OPEN DISCUSSION</h3>
          <div style="margin-left: 25px;">
            ${doc.discussions.map((discussion) => `
              <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
                <h4 style="font-weight: bold; margin-bottom: 10px; font-size: 18px;">${discussion.topic}</h4>
                <p style="margin-bottom: 8px; font-size: 16px; color: #666;">Submitted by: ${discussion.member_name || 'Unknown'}</p>
                ${discussion.notes ? `<p style=\"margin-bottom: 10px; font-size: 16px;\">${discussion.notes}</p>` : ''}
                <p style="font-weight: bold; font-size: 16px;">Status: <span style="color: ${discussion.status === 'resolved' ? 'green' : 'orange'};">${((discussion.status || 'Open').toUpperCase())}</span></p>
              </div>
            `).join('')}
          </div>
        </div>
        ` : ''}

        ${(vis.notes !== false && doc.meeting_notes) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">NOTES FROM SECRETARY</h3>
          <div style="margin-left: 25px; padding: 15px; background-color: #f9f9f9; border-radius: 5px;">
            <p style="font-size: 16px; line-height: 1.6;">${doc.meeting_notes}</p>
          </div>
        </div>
        ` : ''}

        ${(vis.actionItems !== false && doc.action_items && doc.action_items.length > 0) ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">ACTION ITEMS</h3>
          <div style="margin-left: 25px;">
            ${doc.action_items.map((item, idx) => {
              const itemText = typeof item === 'string' ? item : (item.text || item.title || item.description || String(item));
              return `<p style="margin: 10px 0; font-size: 16px; padding: 8px; background-color: #f9f9f9; border-left: 4px solid #007cba;">${idx + 1}. ${itemText}</p>`;
            }).join('')}
          </div>
        </div>
        ` : ''}

        ${doc.supporting_documents && doc.supporting_documents.length > 0 ? `
        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">📎 SUPPORTING DOCUMENTS</h3>
          <div style="margin-left: 25px;">
            ${doc.use_combined_packet && doc.combined_packet_id ? `
              <p style="font-style: italic; color: #666; margin-bottom: 15px; font-size: 14px;">All supporting documents have been combined into a single packet for convenience:</p>
              <div style="margin-bottom: 30px; page-break-inside: avoid;">
                <div style="padding: 10px; background: #f0fdf4; border: 2px solid #10b981; border-radius: 4px; margin-bottom: 10px;">
                  <div style="display: flex; align-items: center;">
                    <span style="font-size: 20px; margin-right: 10px;">📦</span>
                    <div style="flex: 1;">
                      <strong>Combined Supporting Documents Packet</strong><br>
                      <span style="font-size: 12px; color: #6b7280;">
                        ${doc.supporting_documents.length} documents merged into single PDF
                      </span>
                    </div>
                  </div>
                </div>
                <div style="border: 2px solid #ddd; padding: 10px; background: #fff; min-height: 600px;">
                  <iframe src="${process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001')}/api/packets/download/${doc.combined_packet_id}?token=${localStorage.getItem('hoa_token')}" width="100%" height="800px" style="border: none;"></iframe>
                  <p style="text-align: center; margin-top: 10px; font-size: 12px; color: #666;">
                    Combined Packet - ${doc.supporting_documents.length} documents
                  </p>
                </div>
              </div>
            ` : `
              <p style="font-style: italic; color: #666; margin-bottom: 15px; font-size: 14px;">The following documents are attached as supporting materials for this meeting:</p>
              ${doc.supporting_documents.map((docFile, index) => {
              const isImage = docFile.mime_type?.startsWith('image/');
              const isPDF = docFile.mime_type === 'application/pdf';
              const fileUrl = getDocumentFileUrl(docFile.id);
              
              return `
                <div style="margin-bottom: 30px; page-break-inside: avoid;">
                  <div style="padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 10px;">
                    <div style="display: flex; align-items: center;">
                      <span style="font-size: 20px; margin-right: 10px;">📄</span>
                      <div style="flex: 1;">
                        <strong>${index + 1}. ${docFile.file_name}</strong><br>
                        <span style="font-size: 12px; color: #6b7280;">
                          Size: ${(docFile.file_size / 1024).toFixed(1)} KB | 
                          Uploaded: ${new Date(docFile.uploaded_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  ${isImage ? `
                    <div style="text-align: center; padding: 10px; background: #fff; border: 1px solid #ddd;">
                      <img src="${fileUrl}" style="max-width: 100%; height: auto; max-height: 800px;" alt="${docFile.file_name}" onerror="this.parentElement.innerHTML='<p style=color:red;>Image failed to load. URL: ${fileUrl}</p>'" />
                    </div>
                  ` : isPDF ? `
                    <div style="border: 2px solid #ddd; padding: 10px; background: #fff; min-height: 600px;">
                      <iframe src="${fileUrl}" width="100%" height="800px" style="border: none;"></iframe>
                      <p style="text-align: center; margin-top: 10px; font-size: 12px; color: #666;">
                        PDF Document: ${docFile.file_name}
                      </p>
                    </div>
                  ` : `
                    <div style="padding: 20px; background: #fff; border: 1px solid #ddd; text-align: center; color: #666;">
                      <p>📄 Document content not displayable in preview</p>
                      <p style="font-size: 12px; margin-top: 10px;">This document (${docFile.file_name}) is available for download</p>
                      <a href="${fileUrl}" download="${docFile.file_name}" style="display: inline-block; margin-top: 15px; padding: 10px 20px; background: #6366f1; color: white; text-decoration: none; border-radius: 6px; font-size: 14px;">
                        📥 Download ${docFile.file_name}
                      </a>
                    </div>
                  `}
                </div>
              `;
              }).join('')}
              <p style="font-size: 12px; color: #666; font-style: italic; margin-top: 10px;">
                Total: ${doc.supporting_documents.length} supporting document${doc.supporting_documents.length !== 1 ? 's' : ''}
              </p>
            `}
          </div>
        </div>
        ` : ''}

        <div style="margin-bottom: 35px;">
          <h3 style="font-weight: bold; font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">MEETING ADJOURNED</h3>
          <div style="margin-left: 25px;">
            <p style="margin: 8px 0; font-size: 16px;"><strong>Meeting adjourned:</strong> ${endTime}</p>
          </div>
        </div>

        <div style="margin-top: 50px; text-align: center; font-size: 14px; color: #666; border-top: 1px solid #ccc; padding-top: 20px;">
          <p>Generated for Board Review and Approval</p>
        </div>
      </div>
    `;
  };

  const rejectDocument = async (approvalId, reason) => {
    try {
      await api.post(`/api/vote/approve-minutes/${approvalId}`, {
        decision: "rejected",
        comments: reason
      });
      
      // IMMEDIATELY remove from pending list in UI
      setPendingDocuments(prev => prev.filter(doc => doc.id !== approvalId && doc._id !== approvalId));
      
      await loadPendingDocuments();
      alert('Document rejected successfully.');
    } catch (error) {
      console.error('Failed to reject document:', error);
      alert('Failed to reject document. Please try again.');
    }
  };

  const deleteDocument = async (approvalId) => {
    // Find the document to get its meeting_type
    const doc = pendingDocuments.find(d => d.id === approvalId || d._id === approvalId);
    if (!doc) {
      alert('Document not found');
      return;
    }
    
    // Show reason options in a dialog
    const reasons = [
      'Created in error',
      'Need to remake', 
      'No longer needed'
    ];
    
    const reasonChoice = prompt(
      'Select reason for deletion:\n' +
      '1. Created in error\n' +
      '2. Need to remake\n' +
      '3. No longer needed\n\n' +
      'Enter 1, 2, or 3:'
    );
    
    if (!reasonChoice || !['1', '2', '3'].includes(reasonChoice)) {
      return;
    }
    
    const reason = reasons[parseInt(reasonChoice) - 1];
    const confirmDelete = confirm(`Delete this document?\nReason: ${reason}\n\nThis will move it to the Archive tab.`);
    
    if (!confirmDelete) {
      return;
    }
    
    try {
      // Mark as deleted by setting status to "deleted" with reason
      await api.post(`/api/vote/approve-minutes/${approvalId}`, {
        decision: "deleted",
        comments: `Deleted: ${reason}`,
        meeting_type: doc.meeting_type || 'BOD',
        signature: `${user.first_name} ${user.last_name}`
      });
      
      // IMMEDIATELY remove from pending list in UI
      setPendingDocuments(prev => prev.filter(doc => doc.id !== approvalId && doc._id !== approvalId));
      
      await loadPendingDocuments();
      alert('✅ Document deleted and moved to archive.');
    } catch (error) {
      console.error('Failed to delete document:', error);
      console.error('Error details:', error.response?.data);
      alert(`❌ Failed to delete document: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSelectUser = (userId, checked) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(new Set(pendingApprovals.map(user => user.id)));
    } else {
      setSelectedUsers(new Set());
    }
    setShowBulkActions(checked && pendingApprovals.length > 0);
  };

  const handleBulkApprove = async () => {
    if (!window.confirm(`Are you sure you want to approve ${selectedUsers.size} selected users to your site?`)) {
      return;
    }

    for (const userId of selectedUsers) {
      await approveUser(userId);
    }
    setSelectedUsers(new Set());
    setShowBulkActions(false);
    alert(`✅ Successfully approved ${selectedUsers.size} homeowners!\n\n📧 EMAIL NOTIFICATIONS:\n• All approved users will receive welcome emails\n• Each email contains login instructions\n• New users should contact their HOA board for password setup\n\n📅 Approval Date: ${new Date().toLocaleDateString()}\n\n💡 TIP: Share this guidance with new homeowners:\n"Contact your HOA board members to get your initial login password"`);
  };

  const handleBulkReject = async () => {
    const reason = prompt('Please provide a reason for rejecting the selected users:');
    if (!reason) return;

    if (!window.confirm(`Are you sure you want to reject ${selectedUsers.size} selected users?`)) {
      return;
    }

    for (const userId of selectedUsers) {
      await rejectUser(userId, reason);
    }
    setSelectedUsers(new Set());
    setShowBulkActions(false);
    alert(`✅ Successfully rejected ${selectedUsers.size} users.`);
  };

  const handleDownloadCSV = () => {
    const csvHeaders = ['Name', 'Email', 'Property Address', 'Registration Date', 'Phone', 'Status'];
    const csvData = pendingApprovals.map(user => [
      `"${user.first_name} ${user.last_name}"`,
      `"${user.email}"`,
      `"${user.property_address}"`,
      `"${new Date(user.created_at).toLocaleDateString()}"`,
      `"${user.phone_number || ''}"`,
      'Pending Approval'
    ]);

    const csvContent = [csvHeaders.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `pending_approvals_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleConditionalApproval = async (userId) => {
    const selectedReasons = Object.entries(conditionalReasons)
      .filter(([key, value]) => value)
      .map(([key, value]) => {
        switch(key) {
          case 'nameMatch': return 'Name needs to match title';
          case 'addressVerification': return 'Address/lot/unit needs verification';
          case 'expiredCode': return 'Expired code - please request new code from HOA';
          default: return key;
        }
      });

    if (selectedReasons.length === 0) {
      alert('Please select at least one reason for conditional approval');
      return;
    }

    const emailMessage = `Your registration requires additional information:\n\n${selectedReasons.map(reason => `• ${reason}`).join('\n')}\n\nPlease contact the HOA board to resolve these issues.`;
    
    // In a real application, this would send an email
    console.log('Conditional approval email would be sent:', emailMessage);
    
    alert(`✅ Conditional approval sent to user with required actions:\n${selectedReasons.map(reason => `• ${reason}`).join('\n')}`);
    setConditionalApprovalUserId(null);
    setConditionalReasons({ nameMatch: false, addressVerification: false, expiredCode: false });
  };

  const handleApprove = async (userId) => {
    const result = await approveUser(userId);
    if (!result.success) {
      alert('Failed to approve user: ' + result.error);
    }
  };

  const handleReject = async (userId) => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    const result = await rejectUser(userId, rejectReason);
    if (result.success) {
      setRejectingUserId(null);
      setRejectReason('');
    } else {
      alert('Failed to reject user: ' + result.error);
    }
  };

  const renderTeamUserApprovals = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg leading-6 font-medium text-gray-900">
          Pending Team Members ({pendingTeamUsers.length})
        </h4>
      </div>

      {pendingTeamUsers.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">👥</div>
          <p className="text-sm text-gray-500">No pending team member approvals</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingTeamUsers.map((teamUser) => (
            <div key={teamUser.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">
                    {teamUser.role === 'maintenance' ? '🔧' : 
                     teamUser.role === 'security' ? '🛡️' : '👤'}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {teamUser.first_name} {teamUser.last_name}
                    </h4>
                    <p className="text-sm text-gray-500">{teamUser.email}</p>
                    <p className="text-sm text-blue-600 font-medium capitalize">
                      {teamUser.role.replace('_', ' ')} Team
                    </p>
                    <p className="text-xs text-gray-400">
                      Requested: {new Date(teamUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => approveTeamUser(teamUser.id)}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                  >
                    ✅ Approve
                  </button>
                  
                  <button
                    onClick={() => setRejectingUserId(teamUser.id)}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                  >
                    ❌ Reject
                  </button>
                </div>
              </div>

              {/* Reject Modal for Team Users */}
              {rejectingUserId === teamUser.id && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                  <h5 className="font-medium text-red-900 mb-2">Reject Team Member</h5>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g., 'This position has been filled' or 'Please contact the board for more information about volunteer opportunities'"
                    className="w-full p-2 border border-red-300 rounded text-sm"
                    rows={3}
                  />
                  <p className="text-xs text-amber-700 mt-1 bg-amber-50 p-2 rounded border border-amber-200">
                    ⚠️ <strong>Note:</strong> This message will be sent directly to the user via email. Please write a helpful, non-alarming message.
                  </p>
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={() => {
                        rejectTeamUser(teamUser.id, rejectReason);
                        setRejectingUserId(null);
                        setRejectReason('');
                      }}
                      className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                      disabled={!rejectReason.trim()}
                    >
                      Confirm Rejection
                    </button>
                    <button
                      onClick={() => {
                        setRejectingUserId(null);
                        setRejectReason('');
                      }}
                      className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        {/* Approval Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-hide pb-2 md:pb-0">
            <button
              onClick={() => setActiveApprovalTab('homeowners')}
              className={`${
                activeApprovalTab === 'homeowners'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 min-w-max`}
            >
              <span>🏠</span>
              <span>Homeowners ({pendingApprovals.length})</span>
            </button>
            <button
              onClick={() => setActiveApprovalTab('team')}
              className={`${
                activeApprovalTab === 'team'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 min-w-max`}
            >
              <span>👥</span>
              <span>Team Members ({pendingTeamUsers.length})</span>
            </button>
            <button
              onClick={() => setActiveApprovalTab('documents')}
              className={`${
                activeApprovalTab === 'documents'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 min-w-max`}
            >
              <span>📄</span>
              <span>Meeting Minutes ({pendingDocuments.length})</span>
            </button>
            {/* Proxy Approvals tab hidden - no backend functionality */}
            <button
              onClick={() => setActiveApprovalTab('archive')}
              className={`${
                activeApprovalTab === 'archive'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 min-w-max`}
            >
              <span>📦</span>
              <span>Archive</span>
            </button>
            <button
              onClick={() => setActiveApprovalTab('email-tracker')}
              className={`${
                activeApprovalTab === 'email-tracker'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 min-w-max`}
            >
              <span>📧</span>
              <span>Welcome Email Tracker</span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <>
          {activeApprovalTab === 'team' && renderTeamUserApprovals()}
          
          {activeApprovalTab === 'documents' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Pending Document Approvals ({pendingDocuments.length})</h3>
              </div>
              
              {/* Board Member Guidelines */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">📝 Meeting Minutes Approval Guidelines</h4>
                <p className="text-sm text-blue-800">
                  <strong>Primary Signers:</strong> President and Secretary should primarily sign off on meeting minutes.<br />
                  <strong>Other Board Members:</strong> Can provide feedback in communications tab and approve if necessary.<br />
                  <strong>Electronic Signature:</strong> Your approval serves as your electronic signature on these official documents.
                </p>
              </div>

              {/* Documents List */}
              {pendingDocuments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📄</div>
                  <p>No documents pending approval.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingDocuments.map((doc) => (
                    <div key={doc.id} className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h4 className="text-lg font-semibold text-gray-900">
                              {doc.meeting_type} Meeting Minutes
                            </h4>
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                              Pending Approval
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                            <div>
                              <strong>Meeting Date:</strong> {(() => {
                                const [year, month, day] = doc.meeting_date.split('-');
                                return new Date(year, month - 1, day).toLocaleDateString();
                              })()}
                            </div>
                            <div>
                              <strong>Submitted By:</strong> {doc.created_by_name || 'Board Member'}
                            </div>
                            <div>
                              <strong>Motions:</strong> {doc.motions?.length || 0}
                            </div>
                            <div>
                              <strong>Discussions:</strong> {doc.discussions?.length || 0}
                            </div>
                          </div>

                          {/* Document Preview */}
                          <div className="mb-4 p-3 bg-gray-50 rounded border text-sm">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold">Meeting Summary:</h5>
                              <button
                                onClick={() => {
                                  // Show full document preview modal
                                  const modalContent = generateFullMeetingMinutesPreview(doc);
                                  const modal = document.createElement('div');
                                  modal.className = 'fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4';
                                  modal.innerHTML = `
                                    <div class="bg-white rounded-lg w-full max-w-5xl h-[90dvh] flex flex-col">
                                      <div class="flex-shrink-0 bg-white border-b p-4 flex justify-between items-center">
                                        <h3 class="text-xl font-semibold">📄 Full Meeting Minutes Preview</h3>
                                        <button onclick="this.closest('.fixed').remove()" class="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
                                      </div>
                                      <div class="flex-1 overflow-y-auto p-6" style="font-size: 14px; line-height: 1.6;">
                                        ${modalContent}
                                      </div>
                                    </div>
                                  `;
                                  document.body.appendChild(modal);
                                  // Prevent body scrolling when modal is open - save previous state
                                  const prevOverflow = document.body.style.overflow;
                                  const prevPosition = document.body.style.position;
                                  document.body.style.overflow = 'hidden';
                                  document.body.style.position = 'relative';
                                  // Clean up when modal is closed - restore previous state
                                  const cleanup = () => {
                                    document.body.style.overflow = prevOverflow || '';
                                    document.body.style.position = prevPosition || '';
                                    modal.remove();
                                  };
                                  modal.addEventListener('click', (e) => {
                                    if (e.target === modal) cleanup();
                                  });
                                  modal.querySelector('button').addEventListener('click', cleanup);
                                }}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                              >
                                📄 View Full Document
                              </button>
                            </div>
                            <p className="text-gray-700 mb-2">
                              {doc.meeting_notes || 'Meeting minutes available for review and approval.'}
                            </p>
                            {doc.action_items && doc.action_items.length > 0 && (
                              <div className="mt-2">
                                <strong>Action Items:</strong> {doc.action_items.map(item => 
                                  typeof item === 'string' ? item : (item.text || item.title || String(item))
                                ).join(', ')}
                              </div>
                            )}
                            {doc.motions && doc.motions.length > 0 && (
                              <div className="mt-2">
                                <strong>Motions ({doc.motions.length}):</strong>
                                <ul className="list-disc list-inside mt-1 text-xs">
                                  {doc.motions.slice(0, 3).map((motion, idx) => (
                                    <li key={idx}>
                                      <strong>{motion.title}</strong> - {motion.status}
                                      {motion.votes && ` (${motion.votes.approve || 0} for, ${motion.votes.oppose || 0} against)`}
                                      {motion.discussion && <span className="text-gray-600"> - Has discussion notes</span>}
                                    </li>
                                  ))}
                                  {doc.motions.length > 3 && <li>... and {doc.motions.length - 3} more motions</li>}
                                </ul>
                              </div>
                            )}
                            {doc.discussions && doc.discussions.length > 0 && (
                              <div className="mt-2">
                                <strong>Discussions ({doc.discussions.length}):</strong>
                                <ul className="list-disc list-inside mt-1 text-xs">
                                  {doc.discussions.slice(0, 2).map((discussion, idx) => (
                                    <li key={idx}>{discussion.topic}</li>
                                  ))}
                                  {doc.discussions.length > 2 && <li>... and {doc.discussions.length - 2} more discussions</li>}
                                </ul>
                              </div>
                            )}
                          </div>

                          {/* Inline Comments & Edit */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                            {/* Comments */}
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                              <h5 className="font-semibold text-yellow-800 mb-2">💬 Comments</h5>
                              <div className="space-y-2 max-h-48 overflow-y-auto mb-2">
                                {(docDetails[doc.id]?.comments || []).map(c => (
                                  <div key={c.id} className="text-sm bg-white p-2 rounded border">
                                    <div className="text-gray-800"><strong>{c.user_name}</strong> <span className="text-gray-400 text-xs">{new Date(c.created_at).toLocaleString()}</span></div>
                                    <div className="text-gray-700">{c.text}</div>
                                  </div>
                                ))}
                                {(!docDetails[doc.id]?.comments || docDetails[doc.id]?.comments.length === 0) && (
                                  <div className="text-xs text-gray-500">No comments yet.</div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  value={commentInputs[doc.id] || ''}
                                  onChange={e => setCommentInputs(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                  onFocus={() => { if (!docDetails[doc.id]) loadApprovalDetails(doc.id); }}
                                  placeholder="Write a comment..."
                                  className="flex-1 p-2 border border-yellow-300 rounded text-sm"
                                />
                                <button onClick={() => postApprovalComment(doc.id)} className="px-3 py-2 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700">Post</button>
                              </div>
                            </div>

                            {/* Edit Minutes */}
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                              <h5 className="font-semibold text-blue-800 mb-2">✏️ Edit Minutes (this approval)</h5>
                              <div className="space-y-2">
                                <label className="block text-xs text-blue-900">Start time</label>
                                <input value={editStartTime[doc.id] || ''} onChange={e => setEditStartTime(p => ({ ...p, [doc.id]: e.target.value }))} onFocus={() => { if (!docDetails[doc.id]) loadApprovalDetails(doc.id); }} className="w-full p-2 border rounded text-sm" placeholder="e.g., 7:00 PM" />
                                <label className="block text-xs text-blue-900">End time</label>
                                <input value={editEndTime[doc.id] || ''} onChange={e => setEditEndTime(p => ({ ...p, [doc.id]: e.target.value }))} className="w-full p-2 border rounded text-sm" placeholder="e.g., 8:30 PM" />
                                <label className="block text-xs text-blue-900">Notes</label>
                                <textarea value={editNotes[doc.id] || ''} onChange={e => setEditNotes(p => ({ ...p, [doc.id]: e.target.value }))} className="w-full p-2 border rounded text-sm" rows={3} placeholder="Additional notes..." />
                                <label className="block text-xs text-blue-900">Action items (one per line)</label>
                                <textarea value={editActionItems[doc.id] || ''} onChange={e => setEditActionItems(p => ({ ...p, [doc.id]: e.target.value }))} className="w-full p-2 border rounded text-sm" rows={3} placeholder="Item one\nItem two" />
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="col-span-2 flex items-center gap-2 text-gray-700 relative">
                                    <span className="font-medium">Include in final document</span>
                                    <button
                                      type="button"
                                      aria-label="Info about section visibility"
                                      onMouseEnter={() => setVisHelpOpen(p => ({ ...p, [doc.id]: true }))}
                                      onMouseLeave={() => setVisHelpOpen(p => ({ ...p, [doc.id]: false }))}
                                      onClick={() => setVisHelpOpen(p => ({ ...p, [doc.id]: !p[doc.id] }))}
                                      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-700 text-xs hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    >
                                      i
                                    </button>
                                    {visHelpOpen[doc.id] && (
                                      <div className="absolute top-6 left-0 z-10 w-[28rem] max-w-[80vw] p-3 bg-black text-white text-xs rounded shadow-lg">
                                        These checkboxes control which sections will appear in the finalized minutes. Hiding a section does not delete its data and does not affect rollover eligibility. Always-included sections: Header, Called to Order, Rules, Adjourned.
                                      </div>
                                    )}
                                  </div>
                                  {['attendance','meetingLocation','proofOfNotice','rulesOfConduct','priorBusiness','manualPriorMinutes','motions','discussions','notes','actionItems'].map(key => (
                                    <label key={key} className="inline-flex items-center gap-1">
                                      <input
                                        type="checkbox"
                                        checked={(editVisibility[doc.id]?.[key] ?? true)}
                                        onChange={e => setEditVisibility(p => ({ ...p, [doc.id]: { ...(p[doc.id] || {}), [key]: e.target.checked } }))}
                                      />
                                      <span className="capitalize text-xs">{
                                        key === 'actionItems' ? 'Action Items' : 
                                        key === 'priorBusiness' ? 'Prior Business' : 
                                        key === 'proofOfNotice' ? 'Proof of Notice' :
                                        key === 'meetingLocation' ? 'Meeting Location' :
                                        key === 'manualPriorMinutes' ? 'Manual Prior Minutes' :
                                        key === 'rulesOfConduct' ? 'Rules of Conduct' :
                                        key.charAt(0).toUpperCase() + key.slice(1)
                                      }</span>
                                    </label>
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => saveApprovalEdits(doc.id)} className="px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Save Changes</button>
                                  <button onClick={() => loadApprovalDetails(doc.id)} className="px-3 py-2 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200">Refresh</button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Approval Actions */}
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <button
                              onClick={async () => {
                                const signature = prompt('Please type your full name to provide electronic signature:');
                                if (signature && signature.trim()) {
                                  await approveDocument(doc.id, signature.trim());
                                  // Rely on legacy generate-signed-minutes flow to create the final PDF and index it
                                  // We avoid double-finalizing to prevent creating blank HTML entries
                                }
                              }}
                              className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                            >
                              ✅ Approve & Sign
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt('Please provide reason for rejection:');
                                if (reason && reason.trim()) {
                                  rejectDocument(doc.id, reason.trim());
                                }
                              }}
                              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                            >
                              ❌ Reject
                            </button>
                            <button
                              onClick={() => deleteDocument(doc.id)}
                              className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
                            >
                              🗑️ Delete
                            </button>
                            <button
                              onClick={() => {
                                // Navigate to Communications tab for feedback
                                alert('Please use the Communications tab to provide feedback on this document.');
                              }}
                              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              💬 Provide Feedback
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          

          {activeApprovalTab === 'email-tracker' && (
            <WelcomeEmailTracker user={user} hoaData={hoaData} />
          )}

          {activeApprovalTab === 'homeowners' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Pending Homeowner Approvals ({pendingApprovals.length})</h3>
          
          <div className="flex space-x-3">
            {pendingApprovals.length > 0 && (
              <button
                onClick={handleDownloadCSV}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                📄 Download CSV
              </button>
            )}
          </div>
        </div>
        
        {/* Bulk Actions Bar */}
        {showBulkActions && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-blue-900">
                {selectedUsers.size} users selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={handleBulkApprove}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  ✅ Approve Selected
                </button>
                <button
                  onClick={handleBulkReject}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  ❌ Reject Selected
                </button>
                <button
                  onClick={() => {
                    setSelectedUsers(new Set());
                    setShowBulkActions(false);
                  }}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Select All Checkbox */}
        {pendingApprovals.length > 0 && (
          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              checked={selectedUsers.size === pendingApprovals.length && pendingApprovals.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <label className="ml-2 text-sm text-gray-600">
              Select all ({pendingApprovals.length} users)
            </label>
          </div>
        )}
        
        {pendingApprovals.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No pending approvals</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingApprovals.map((user) => (
              <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={(e) => handleSelectUser(user.id, e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </h4>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <p className="text-sm text-gray-500">{user.property_address}</p>
                      {user.phone_number && (
                        <p className="text-sm text-gray-500">📞 {user.phone_number}</p>
                      )}
                      <p className="text-xs text-gray-400">
                        Registered: {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleApprove(user.id)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                    >
                      ✅ Approve
                    </button>
                    
                    <button
                      onClick={() => setConditionalApprovalUserId(user.id)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700"
                    >
                      ⚠️ Conditional
                    </button>
                    
                    <button
                      onClick={() => setRejectingUserId(user.id)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>

                {/* Conditional Approval Modal */}
                {conditionalApprovalUserId === user.id && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <h5 className="font-medium text-yellow-900 mb-2">Select Missing Requirements:</h5>
                    <div className="space-y-2 text-sm">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={conditionalReasons.nameMatch}
                          onChange={(e) => setConditionalReasons(prev => ({...prev, nameMatch: e.target.checked}))}
                          className="h-4 w-4 text-yellow-600 mr-2"
                        />
                        Name needs to match title
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={conditionalReasons.addressVerification}
                          onChange={(e) => setConditionalReasons(prev => ({...prev, addressVerification: e.target.checked}))}
                          className="h-4 w-4 text-yellow-600 mr-2"
                        />
                        Address/lot/unit needs verification
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={conditionalReasons.expiredCode}
                          onChange={(e) => setConditionalReasons(prev => ({...prev, expiredCode: e.target.checked}))}
                          className="h-4 w-4 text-yellow-600 mr-2"
                        />
                        Used expired code - needs new code
                      </label>
                    </div>
                    <div className="flex space-x-2 mt-3">
                      <button
                        onClick={() => handleConditionalApproval(user.id)}
                        className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                      >
                        Send Requirements Email
                      </button>
                      <button
                        onClick={() => setConditionalApprovalUserId(null)}
                        className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Rejection Modal */}
                {rejectingUserId === user.id && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                    <label className="block text-sm font-medium text-red-900 mb-2">
                      Reason for rejection:
                    </label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="w-full px-3 py-2 border border-red-300 rounded-md text-sm"
                      rows="2"
                      placeholder="e.g., 'We need additional documentation to verify your address' or 'Please re-register with your correct property address'"
                    />
                    <p className="text-xs text-amber-700 mt-1 bg-amber-50 p-2 rounded border border-amber-200">
                      ⚠️ <strong>Note:</strong> This message will be sent directly to the user via email. Please write a helpful, non-alarming message explaining what they need to do to re-apply.
                    </p>
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => handleReject(user.id)}
                        className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                      >
                        Confirm Rejection
                      </button>
                      <button
                        onClick={() => {
                          setRejectingUserId(null);
                          setRejectReason('');
                        }}
                        className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
          </div>
          )}
          
          {/* Proxy Approvals Tab */}
          {/* Proxy Approvals tab hidden - no backend functionality */}
          
          {/* Archive Tab */}
          {activeApprovalTab === 'archive' && (
            <div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> This archive is for visual reference only to see past approval decisions and history. 
                      For full document details and downloads, please visit the <strong>Documents</strong> tab.
                    </p>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Archived Approvals</h3>
              
              {/* Archived Homeowners */}
              <div className="mb-8">
                <h4 className="text-md font-medium text-gray-700 mb-3">Homeowners ({archivedHomeowners.length})</h4>
                {archivedHomeowners.length === 0 ? (
                  <p className="text-gray-500 text-sm">No archived homeowner approvals</p>
                ) : (
                  <div className="space-y-3">
                    {archivedHomeowners.map((homeowner) => (
                      <div key={homeowner.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{homeowner.first_name} {homeowner.last_name}</p>
                            <p className="text-sm text-gray-600">{homeowner.email}</p>
                            <p className="text-sm text-gray-600">{homeowner.property_address}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            homeowner.status === 'approved' ? 'bg-green-100 text-green-800' :
                            homeowner.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {homeowner.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </div>
                        {homeowner.approved_at && (
                          <p className="text-xs text-gray-500 mt-2">
                            {homeowner.status === 'approved' ? 'Approved' : 'Rejected'} on {new Date(homeowner.approved_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Archived Team Members */}
              <div className="mb-8">
                <h4 className="text-md font-medium text-gray-700 mb-3">Team Members ({archivedTeamUsers.length})</h4>
                {archivedTeamUsers.length === 0 ? (
                  <p className="text-gray-500 text-sm">No archived team member approvals</p>
                ) : (
                  <div className="space-y-3">
                    {archivedTeamUsers.map((member) => (
                      <div key={member.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{member.first_name} {member.last_name}</p>
                            <p className="text-sm text-gray-600">{member.email}</p>
                            <p className="text-sm text-gray-600">Role: {member.role}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            member.status === 'approved' ? 'bg-green-100 text-green-800' :
                            member.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {member.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Archived Documents */}
              <div className="mb-8">
                <h4 className="text-md font-medium text-gray-700 mb-3">Meeting Minutes ({archivedDocuments.length})</h4>
                {archivedDocuments.length === 0 ? (
                  <p className="text-gray-500 text-sm">No archived meeting minutes</p>
                ) : (
                  <div className="space-y-3">
                    {archivedDocuments.map((doc) => (
                      <div key={doc.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{doc.title || `${doc.meeting_type} Meeting Minutes`}</p>
                            <p className="text-sm text-gray-600">
                              {doc.meeting_date ? (() => {
                                const [year, month, day] = doc.meeting_date.split('-');
                                return new Date(year, month - 1, day).toLocaleDateString();
                              })() : 'No date'}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            doc.status === 'approved' || doc.status === 'signed' ? 'bg-green-100 text-green-800' :
                            doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {doc.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </div>
                        
                        {/* Approver Information */}
                        {doc.approvals && doc.approvals.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-700 mb-2">Approved by:</p>
                            <div className="space-y-1">
                              {doc.approvals.map((approval, idx) => (
                                <div key={idx} className="text-xs text-gray-600 flex items-center justify-between">
                                  <span>
                                    <strong>{approval.signature || approval.user_name || 'Unknown'}</strong>
                                    {approval.user_name && approval.signature !== approval.user_name && ` (${approval.user_name})`}
                                  </span>
                                  <span className="text-gray-500">
                                    {approval.approved_at ? new Date(approval.approved_at).toLocaleDateString() : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {doc.updated_at && (
                          <p className="text-xs text-gray-500 mt-2">
                            Updated {new Date(doc.updated_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  );
};


// Contact HOA Manager Pro Component (Board Members Only)
const ContactHOAManagerSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [supportCategory, setSupportCategory] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { user, hoaData } = useHOA();

  const supportCategories = [
    { 
      value: 'registration_codes', 
      label: 'Registration Codes',
      example: 'Need help generating codes for new homeowners to register on the platform.'
    },
    { 
      value: 'team_management', 
      label: 'Team/Board Management',
      example: 'Need assistance with board member roles, permissions, or team setup.'
    },
    { 
      value: 'initial_setup', 
      label: 'Initial Setup Configuration',
      example: 'Need help with community setup, amenities configuration, or platform customization.'
    },
    { 
      value: 'how_to', 
      label: 'How To',
      example: 'Need guidance on using specific features or navigating the platform.'
    },
    { 
      value: 'general', 
      label: 'General',
      example: 'General questions or issues not covered by other categories.'
    },
    { 
      value: 'add_remove_service', 
      label: 'Add/Remove Service',
      example: 'Need to add or remove platform services, features, or subscription changes.'
    },
    { 
      value: 'change_main_admin', 
      label: 'Change Main Admin Request',
      example: 'Current main admin: john@hoa.com, New admin should be: jane@hoa.com. Please transfer admin privileges.'
    },
    { 
      value: 'homeowners_management', 
      label: 'Homeowners Management',
      example: 'Need assistance with homeowner accounts, property assignments, or resident management.'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supportCategory || !description.trim()) {
      alert('Please select a category and provide a description.');
      return;
    }

    try {
      // Send support request to backend
      await api.post('/api/hoa/support-request', {
        category: supportCategory,
        description: description.trim(),
        hoa_id: user?.hoa_id,
        hoa_name: hoaData?.name,
        requestor_name: `${user?.first_name} ${user?.last_name}`,
        requestor_email: user?.email,
        requestor_role: user?.role
      });

      setSubmitted(true);
      setSupportCategory('');
      setDescription('');
      
      setTimeout(() => {
        setSubmitted(false);
        setIsOpen(false);
      }, 3000);

    } catch (error) {
      console.error('Failed to submit support request:', error);
      alert('Failed to submit support request. Please try again or contact support directly.');
    }
  };

  const selectedCategory = supportCategories.find(cat => cat.value === supportCategory);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Contact HOA MANAGER PRO</h3>
            <p className="text-sm text-gray-600">Get help from our team with platform setup and management.</p>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Contact Support
          </button>
        </div>

        {/* Support Request Modal */}
        {isOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Contact HOA MANAGER PRO</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {submitted ? (
                  <div className="text-center py-4">
                    <div className="text-green-600 text-4xl mb-2">✅</div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Request Submitted!</h4>
                    <p className="text-sm text-gray-600">
                      A team member will reply as soon as possible.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What do you need help with? *
                      </label>
                      <select
                        value={supportCategory}
                        onChange={(e) => setSupportCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      >
                        <option value="">Select a category...</option>
                        {supportCategories.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedCategory && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <p className="text-sm text-blue-800">
                          <strong>Example:</strong> {selectedCategory.example}
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Describe what you would like help with *
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="A team member will reply with you as soon as possible."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                      >
                        Submit Request
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Meeting Minutes Approval Settings Component
const MeetingMinutesApprovalSettings = () => {
  const { user } = useAuth();
  const { activeHoaId } = useMultiHOA();
  const [votingSettings, setVotingSettings] = useState({
    required_approvals_for_minutes: 2,
    board_member_count: 1,
    allow_committee_voting: true,
    auto_archive_after_approval: true,
    meeting_quorum_percentage: 50,
    motion_voting_timeout_hours: 72
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load voting settings
  useEffect(() => {
    const loadVotingSettings = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) return;
        
        const response = await api.get(`/api/hoa/${currentHoaId}/voting-settings`);
        if (response.data.success) {
          setVotingSettings(response.data.settings);
        }
      } catch (error) {
        console.error('Failed to load voting settings:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.hoa_id) {
      loadVotingSettings();
    }
  }, [user?.hoa_id]);

  // Save voting settings
  const saveVotingSettings = async (newSettings) => {
    setSaving(true);
    try {
      const response = await api.put(`/api/hoa/${user.hoa_id}/voting-settings`, newSettings);
      if (response.data.success) {
        setVotingSettings(newSettings);
        alert('✅ Voting settings saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save voting settings:', error);
      alert('❌ Failed to save voting settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading voting settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg leading-6 font-medium text-gray-900">🗳️ Voting & Approval Settings</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure how voting and document approvals work in your HOA.
        </p>
      </div>

      {/* Meeting Minutes Approval Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">📄 Meeting Minutes Approval</h4>
        <p className="text-sm text-gray-600 mb-6">
          Control how many board member approvals are required for meeting minutes to be finalized and saved to documents.
        </p>
        
        <div className="space-y-6">
          {/* Current Board Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-2">📊 Current Board Status</h5>
            <p className="text-sm text-blue-800">
              <strong>Active Board Members:</strong> {votingSettings.board_member_count}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {votingSettings.board_member_count <= 2 
                ? "With 2 or fewer board members, only 1 approval is typically needed."
                : "With 3+ board members, 2 approvals are recommended for accountability."
              }
            </p>
          </div>

          {/* Required Approvals Setting */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Required Approvals for Meeting Minutes
            </label>
            <select
              value={votingSettings.required_approvals_for_minutes}
              onChange={(e) => {
                const newSettings = {...votingSettings, required_approvals_for_minutes: parseInt(e.target.value)};
                setVotingSettings(newSettings);
                saveVotingSettings(newSettings);
              }}
              disabled={saving}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value={1}>1 Approval (Secretary or President can approve alone)</option>
              <option value={2}>2 Approvals (Recommended for 3+ board members)</option>
              <option value={3}>3 Approvals (High security for large boards)</option>
              <option value={4}>4 Approvals (Maximum security)</option>
              <option value={5}>5 Approvals (All board positions required)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Current setting: {votingSettings.required_approvals_for_minutes} approval{votingSettings.required_approvals_for_minutes !== 1 ? 's' : ''} required
            </p>
          </div>
        </div>
      </div>

      {/* General Voting Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">🗳️ General Voting Settings</h4>
        
        <div className="space-y-6">
          {/* Committee Voting */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Allow Committee Member Voting</label>
              <p className="text-xs text-gray-500">Committee members can participate in community votes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={votingSettings.allow_committee_voting}
                onChange={(e) => {
                  const newSettings = {...votingSettings, allow_committee_voting: e.target.checked};
                  setVotingSettings(newSettings);
                  saveVotingSettings(newSettings);
                }}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Auto Archive */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Auto-Archive After Approval</label>
              <p className="text-xs text-gray-500">Automatically archive meeting minutes after full approval</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={votingSettings.auto_archive_after_approval}
                onChange={(e) => {
                  const newSettings = {...votingSettings, auto_archive_after_approval: e.target.checked};
                  setVotingSettings(newSettings);
                  saveVotingSettings(newSettings);
                }}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Meeting Quorum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Quorum Percentage
            </label>
            <input
              type="number"
              min="25"
              max="100"
              value={votingSettings.meeting_quorum_percentage}
              onChange={(e) => {
                const newSettings = {...votingSettings, meeting_quorum_percentage: parseInt(e.target.value)};
                setVotingSettings(newSettings);
              }}
              onBlur={() => saveVotingSettings(votingSettings)}
              disabled={saving}
              className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Minimum percentage of board members required for meeting quorum
            </p>
          </div>

          {/* Motion Voting Timeout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motion Voting Timeout (Hours)
            </label>
            <select
              value={votingSettings.motion_voting_timeout_hours}
              onChange={(e) => {
                const newSettings = {...votingSettings, motion_voting_timeout_hours: parseInt(e.target.value)};
                setVotingSettings(newSettings);
                saveVotingSettings(newSettings);
              }}
              disabled={saving}
              className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value={24}>24 Hours (1 Day)</option>
              <option value={48}>48 Hours (2 Days)</option>
              <option value={72}>72 Hours (3 Days)</option>
              <option value={96}>96 Hours (4 Days)</option>
              <option value={168}>168 Hours (1 Week)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              How long motions stay open for voting before auto-closing
            </p>
          </div>
        </div>
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          💾 Saving settings...
        </div>
      )}
    </div>
  );
};

const SettingsTab = () => {
  const { user } = useAuth();
  const { activeHoaId } = useMultiHOA();
  const { hoaData, accountUsage, homeowners, hoaSettings } = useHOA();
  const [searchParams, setSearchParams] = useSearchParams();
  const usagePercentage = accountUsage.limit > 0 ? Math.round((accountUsage.used / accountUsage.limit) * 100) : 0;
  const isNearLimit = usagePercentage >= 80;
  
  // Initialize tab param if missing (only once)
  const initRef = React.useRef(false);
  React.useEffect(() => {
    if (!initRef.current && !searchParams.get('tab')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'profile');
      setSearchParams(newParams, { replace: true });
      initRef.current = true;
    }
  }, [searchParams, setSearchParams]);
  
  // Get active settings tab from URL, default to 'profile'
  const activeSettingsTab = searchParams.get('tab') || 'profile';
  const isBrandingTab = activeSettingsTab === 'branding';
  
  const setActiveSettingsTab = (tab) => {
    // Preserve the main view param when updating settings tab
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  // Function to load pending team users (used by TeamMemberManagement)
  const loadPendingTeamUsers = async () => {
    // This function is called by TeamMemberManagement after adding a new team member
    // The actual pending team users are loaded in the Approvals tab
    console.log('Team member added - pending approvals will be refreshed in Approvals tab');
  };

  // Community branding state (declare BEFORE effects)
  const [brandingSettings, setBrandingSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(`hoa_theme_${hoaData?.id}`) || localStorage.getItem('branding_theme_fallback');
      if (saved) {
        const theme = JSON.parse(saved);
        return {
          logo: theme.logo || null,
          primaryColor: theme.primaryColor || '#4f46e5',
          secondaryColor: theme.secondaryColor || '#6366f1',
          communityName: hoaData?.name || '',
          tagline: hoaData?.tagline || ''
        };
      }
    } catch (_) {}
    return {
      logo: null,
      primaryColor: '#4f46e5',
      secondaryColor: '#6366f1',
      communityName: hoaData?.name || '',
      tagline: ''
    };
  });

  // HOA Track settings for controlling tab visibility
  const [hoaTrackSettings, setHoaTrackSettings] = useState({
    committees_enabled: true,
    security_enabled: true,
    maintenance_enabled: true
  });

  // Load HOA Track settings
  useEffect(() => {
    const loadHOATrackSettings = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) return;
        const { data } = await api.get(`/api/hoa-track/settings/${currentHoaId}`);
        if (data.success) {
          setHoaTrackSettings(data.settings);
        }
      } catch (error) {
        console.error('Failed to load HOA Track settings:', error);
      }
    };
    loadHOATrackSettings();
  }, [user?.hoa_id]);

  // Settings state with localStorage persistence
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('hoa_user_settings');
    return savedSettings ? JSON.parse(savedSettings) : {
      notifications: {
        email: true,
        sms: false,
        push: true,
        announcements: true,
        payments: true,
        violations: true,
        boardMeetings: true,
        maintenance: false
      },
      preferences: {
        theme: 'light',
        language: 'english',
        timezone: 'America/New_York',
        dateFormat: 'MM/DD/YYYY',
        emailDigest: 'daily'
      },
      privacy: {
        showProfile: true,
        showContactInfo: false,
        allowDirectMessages: true,
        shareActivity: false
      }
    };
  });

  // Load email preferences when profile tab is opened
  useEffect(() => {
    const loadEmailPreferences = async () => {
      if (activeSettingsTab !== 'profile') return;
      try {
        const { data } = await api.get('/api/email-preferences');
        setSettings(prev => ({
          ...prev,
          notifications: {
            ...prev.notifications,
            newsletters: data.newsletters ?? true,
            announcements: data.announcements ?? true
          }
        }));
      } catch (e) {
        console.error('Failed to load email preferences:', e);
      }
    };
    loadEmailPreferences();
  }, [activeSettingsTab]);

  // Initialize branding from server on opening Settings → Branding
  useEffect(() => {
    const init = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId || !isBrandingTab) return;
        // Resolve HOA code
        const { data: my } = await api.get('/api/hoa/my');
        const code = my?.hoa?.code;
        if (!code) return;
        const { data: branding } = await api.get(`/api/hoa/${code}/branding`);
        setBrandingSettings(prev => ({
          ...prev,
          logo: branding.logo_url || prev.logo,
          primaryColor: branding.primaryColor || prev.primaryColor,
          secondaryColor: branding.secondaryColor || prev.secondaryColor
        }));
        if (branding.logo_url) window.__serverBrandingLogoUrl = branding.logo_url;
      } catch (e) { /* non-blocking */ }
    };
    init();
  }, [activeHoaId, user?.hoa_id, isBrandingTab]);


  // User management state
  // Load board members for Settings > Users tab
  useEffect(() => {
    const loadBoardMembers = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!user?.id || !currentHoaId) return;
        const { data } = await api.get('/api/board/members');
        setBoardMembers(data?.board_members || []);
      } catch (e) {
        console.warn('Failed to load board members', e);
        setBoardMembers([]);
      }
    };
    loadBoardMembers();
    // Auto-fix chat participants right after login/hoa selection
    if (user?.id && user?.hoa_id) {
      chatApi.fixParticipants(user.id, user.hoa_id).catch(()=>{});
    }
  }, [user?.id, user?.hoa_id]);

  const [boardMembers, setBoardMembers] = useState([]);
  const [newMemberData, setNewMemberData] = useState({
    name: '',
    email: '',
    role: 'board_member'
  });

  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  // Registration settings state (for board members)
  const [registrationSettings, setRegistrationSettings] = useState(null);
  const [loadingRegSettings, setLoadingRegSettings] = useState(false);
  
  // Check if user is a board member
  const isBoardMemberUser = user?.role && ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(user.role);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  // Fetch registration settings for board members
  React.useEffect(() => {
    if (isBoardMemberUser && user?.hoa_id) {
      fetchRegistrationSettings();
    }
  }, [isBoardMemberUser, user?.hoa_id]);
  
  const fetchRegistrationSettings = async () => {
    const currentHoaId = activeHoaId || user?.hoa_id;
    if (!currentHoaId) return;
    
    setLoadingRegSettings(true);
    try {
      const { data } = await api.get(`/api/hoa/${currentHoaId}/registration-settings`);
      setRegistrationSettings(data);
    } catch (error) {
      console.error('Failed to fetch registration settings:', error);
    } finally {
      setLoadingRegSettings(false);
    }
  };

  const handleSettingChange = async (category, key, value) => {
    const newSettings = {
      ...settings,
      [category]: {
        ...settings[category],
        [key]: value
      }
    };
    setSettings(newSettings);
    localStorage.setItem('hoa_user_settings', JSON.stringify(newSettings));
    
    // If changing notification preferences, sync with email_preferences API
    if (category === 'notifications' && (key === 'newsletters' || key === 'announcements')) {
      try {
        await api.put('/api/email-preferences', { [key]: value });
        console.log(`✅ Email preference '${key}' updated to ${value}`);
      } catch (error) {
        console.error(`Failed to update email preference '${key}':`, error);
      }
    }
  };

  const saveSettings = async () => {
    try {
      // Here you would normally save to backend
      // const response = await axios.post('/api/user/settings', settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage your community settings, branding, and user preferences
        </p>
      </div>

      {/* Settings Navigation Tabs - Mobile Friendly */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          {/* Mobile Dropdown */}
          <div className="sm:hidden px-4 py-3">
            <select
              value={activeSettingsTab}
              onChange={(e) => setActiveSettingsTab(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {[
                { id: 'profile', name: 'Profile', icon: '👤', boardOnly: false },
                { id: 'subscription', name: 'Billing & Subscription', icon: '💳', adminOrTreasurer: true },
                { id: 'users', name: 'User Management', icon: '👥', boardOnly: true },
                { id: 'committees', name: 'Committee Management', icon: '👥', boardOnly: true, requiresSetting: 'committees_enabled' },
                { id: 'maintenance_team', name: 'Maintenance Team', icon: '🔧', boardOnly: true, requiresSetting: 'maintenance_enabled' },
                { id: 'security_team', name: 'Security Team', icon: '🛡️', boardOnly: true, requiresSetting: 'security_enabled' },
                { id: 'team_members', name: 'Team Members', icon: '👔', boardOnly: true },
                { id: 'amenities', name: 'Amenity Management', icon: '🏊‍♂️', boardOnly: true, requiresAddon: 'amenity_management' },
                { id: 'voting_settings', name: 'Voting & Approval Settings', icon: '🗳️', boardOnly: true },
                { id: 'branding', name: 'Community Branding', icon: '🎨', boardOnly: true },
                { id: 'community', name: 'Community Services', icon: '🏘️', boardOnly: true },
                { id: 'public_page', name: 'Public Page', icon: '🌐', boardOnly: true },
                { id: 'registration_codes', name: 'Registration Codes', icon: '🔐', boardOnly: true },
                { id: 'hoa_track', name: 'HOA Track Settings', icon: '📋', boardOnly: true }
              ].filter(tab => {
                // Filter by admin or treasurer
                if (tab.adminOrTreasurer && user?.role !== 'president' && user?.role !== 'treasurer') return false;
                // Filter by board-only first
                if (tab.boardOnly && !isBoardMemberUser) return false;
                // Filter by HOA Track settings
                if (tab.requiresSetting && hoaTrackSettings[tab.requiresSetting] === false) return false;
                // Don't filter amenities tab - show it always but with overlay when no add-on
                // if (tab.requiresAddon && !hoaSettings?.subscription?.addons?.includes(tab.requiresAddon)) return false;
                return true;
              }).map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.icon} {tab.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Desktop Tabs - 2 Rows */}
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <div className="overflow-x-auto">
                <nav className="-mb-px flex flex-wrap gap-x-2 gap-y-1" aria-label="Settings Tabs">
                  {[
                    { id: 'profile', name: 'Profile', icon: '👤', boardOnly: false },
                    { id: 'subscription', name: 'Billing & Subscription', icon: '💳', adminOrTreasurer: true },
                    { id: 'users', name: 'User Management', icon: '👥', boardOnly: true },
                    { id: 'committees', name: 'Committee Management', icon: '👥', boardOnly: true, requiresSetting: 'committees_enabled' },
                    { id: 'maintenance_team', name: 'Maintenance Team', icon: '🔧', boardOnly: true, requiresSetting: 'maintenance_enabled' },
                    { id: 'security_team', name: 'Security Team', icon: '🛡️', boardOnly: true, requiresSetting: 'security_enabled' },
                    { id: 'team_members', name: 'Team Members', icon: '👔', boardOnly: true },
                    { id: 'amenities', name: 'Amenity Management', icon: '🏊‍♂️', boardOnly: true, requiresAddon: 'amenity_management' },
                    { id: 'branding', name: 'Community Branding', icon: '🎨', boardOnly: true },
                    { id: 'community', name: 'Community Services', icon: '🏘️', boardOnly: true },
                    { id: 'public_page', name: 'Public Page', icon: '🌐', boardOnly: true },
                    { id: 'registration_codes', name: 'Registration Codes', icon: '🔐', boardOnly: true },
                    { id: 'hoa_track', name: 'HOA Track Settings', icon: '📋', boardOnly: true }
                  ].filter(tab => {
                    // Filter by admin or treasurer
                    if (tab.adminOrTreasurer && user?.role !== 'president' && user?.role !== 'treasurer') return false;
                    // Filter by board-only first
                    if (tab.boardOnly && !isBoardMemberUser) return false;
                    // Filter by HOA Track settings
                    if (tab.requiresSetting && hoaTrackSettings[tab.requiresSetting] === false) return false;
                    // Don't filter amenities tab - show it always but with overlay when no add-on
                    // if (tab.requiresAddon && !hoaSettings?.subscription?.addons?.includes(tab.requiresAddon)) return false;
                    return true;
                  }).map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSettingsTab(tab.id)}
                      className={`${
                        activeSettingsTab === tab.id
                          ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      } py-3 px-4 border-b-2 font-medium text-xs flex flex-col items-center text-center whitespace-nowrap flex-shrink-0 transition-colors duration-200`}
                    >
                      <span className="text-lg mb-1">{tab.icon}</span>
                      <span className="leading-tight">{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeSettingsTab === 'profile' && (
            <ProfileSettings 
              settings={settings}
              setSettings={setSettings}
              handleSettingChange={handleSettingChange}
              saveSettings={saveSettings}
              user={user}
              isBoardMemberUser={isBoardMemberUser}
              registrationSettings={registrationSettings}
              loadingRegSettings={loadingRegSettings}
              hoaData={hoaData}
            />
          )}

          {activeSettingsTab === 'subscription' && (user?.role === 'president' || user?.role === 'treasurer') && (
            <SubscriptionManagement user={user} />
          )}

          {activeSettingsTab === 'users' && isBoardMemberUser && (
            <UserManagementSettings 
              boardMembers={boardMembers}
              setBoardMembers={setBoardMembers}
              newMemberData={newMemberData}
              setNewMemberData={setNewMemberData}
            />
          )}

          {activeSettingsTab === 'branding' && isBoardMemberUser && (
            <CommunityBrandingSettings 
              brandingSettings={brandingSettings}
              setBrandingSettings={setBrandingSettings}
            />
          )}

          {activeSettingsTab === 'voting_settings' && isBoardMemberUser && (
            <MeetingMinutesApprovalSettings />
          )}

          {activeSettingsTab === 'community' && isBoardMemberUser && (
            <CommunityServicesSettings homeowners={homeowners || []} />
          )}

          {activeSettingsTab === 'public_page' && isBoardMemberUser && (
            <PublicPageSettings user={user} hoaData={hoaData} />
          )}

          {activeSettingsTab === 'documents' && isBoardMemberUser && (
            <DocumentManagementSettings 
              documents={documents}
              setDocuments={setDocuments}
              uploadingDocument={uploadingDocument}
              setUploadingDocument={setUploadingDocument}
            />
          )}

          {activeSettingsTab === 'registration_codes' && isBoardMemberUser && (
            <BoardRegistrationCodes />
          )}

          {activeSettingsTab === 'committees' && isBoardMemberUser && (
            <CommitteeManagement />
          )}

          {activeSettingsTab === 'maintenance_team' && isBoardMemberUser && (
            <MaintenanceTeamManagement />
          )}

          {activeSettingsTab === 'security_team' && isBoardMemberUser && (
            <SecurityTeamManagement />
          )}

          {activeSettingsTab === 'team_members' && isBoardMemberUser && (
            <TeamMemberManagement onTeamMemberAdded={loadPendingTeamUsers} />
          )}

          {activeSettingsTab === 'amenities' && isBoardMemberUser && (
            <div>
              {!hasAmenityManagementAddon(hoaSettings) && (
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-300 rounded-lg p-6 mb-6 shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">🔒 Amenity Management Add-on Required</h3>
                      <p className="text-gray-700 mb-3">
                        You can preview the features below, but you'll need the add-on to create, edit, or manage amenities and reservations.
                      </p>
                      <div className="flex items-center space-x-6">
                        <div className="bg-white border border-indigo-200 rounded-lg px-4 py-2">
                          <div className="text-xl font-bold text-indigo-600">$9/mo</div>
                          <p className="text-xs text-gray-600">or $100/year</p>
                        </div>
                        <div className="text-sm text-gray-700">
                          <div className="font-medium mb-1">Includes:</div>
                          <div className="flex space-x-4">
                            <span>✓ Pool & Clubhouse</span>
                            <span>✓ Booking Calendar</span>
                            <span>✓ Auto Reminders</span>
                            <span>✓ Reports</span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setActiveSettingsTab('subscription');
                          }}
                          className="ml-auto inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap"
                        >
                          <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Upgrade Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className={!hasAmenityManagementAddon(hoaSettings) ? 'opacity-50 pointer-events-none' : ''}>
                <AmenityManagement />
              </div>
            </div>
          )}

          {activeSettingsTab === 'hoa_track' && isBoardMemberUser && (
            <HOATrackSettings />
          )}
        </div>
      </div>

      {/* Setup Support Tools - Board Members Only */}
      {isBoardMemberUser && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Setup Support Tools</h3>
            
            {/* Return to Checklist Button */}
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 002 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Return to Setup Checklist</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Continue where you left off with your HOA setup checklist. Complete all tasks to ensure your community is fully configured.
                </p>
                <button
                  onClick={async () => {
                    try {
                      // Always allow access to setup checklist for board members - remove 10 day restriction
                      let hoaCode = hoaData?.code || hoaData?.hoa?.code || user?.hoa_code;
                      if (!hoaCode) {
                        const { data } = await api.get('/api/hoa/my');
                        hoaCode = data?.hoa?.code;
                      }
                      if (hoaCode) {
                        window.location.href = `/setup-checklist/${hoaCode}`;
                      } else {
                        alert('❌ Unable to find HOA code. Please contact support.');
                      }
                    } catch (e) {
                      alert('❌ Unable to find HOA code. Please try again.');
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 002 2m-6 9l2 2 4-4" />
                  </svg>
                  📋 Return to Checklist
                </button>
              </div>
            </div>

            {/* Setup Guide */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Setup Checklist Guide</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Comprehensive guide explaining what each setup checklist item means and how to complete them.
                  Perfect for understanding your community setup requirements.
                </p>
                <button
                  onClick={() => setShowSetupGuide(true)}
                  className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  📖 View Setup Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Setup Guide Modal */}
      {showSetupGuide && (
        <SetupGuideModal onClose={() => setShowSetupGuide(false)} />
      )}
    </div>
  );
};

// Profile Settings Component
const ProfileSettings = ({ settings, setSettings, handleSettingChange, saveSettings, user, isBoardMemberUser, registrationSettings, loadingRegSettings, hoaData }) => {
  const { impersonationMode } = useAuth();
  const { activeHoaId } = useMultiHOA();
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [localEmail, setLocalEmail] = useState(user?.email || '');
  const [localPhone, setLocalPhone] = useState(user?.phone || '');
  const [localAddress, setLocalAddress] = useState(user?.property_address || '');
  const [localMailingAddress, setLocalMailingAddress] = useState(user?.mailing_address || '');
  const [localPropertyId, setLocalPropertyId] = useState(user?.property_identifier || '');
  const [confidentialityConsent, setConfidentialityConsent] = useState(null);
  
  // Email change modal state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [emailChangeForm, setEmailChangeForm] = useState({
    currentEmail: user?.email || '',
    newEmail: '',
    password: ''
  });
  const [changingEmail, setChangingEmail] = useState(false);
  
  // Password change modal state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [passwordChangeForm, setPasswordChangeForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    setLocalEmail(user?.email || '');
    setLocalPhone(user?.phone || '');
    setLocalAddress(user?.property_address || '');
    setLocalMailingAddress(user?.mailing_address || '');
    setLocalPropertyId(user?.property_identifier || '');
  }, [user?.email, user?.phone, user?.property_address, user?.mailing_address, user?.property_identifier]);

  // Load confidentiality consent status
  useEffect(() => {
    const loadConsentStatus = async () => {
      try {
        const { data } = await api.get('/api/confidentiality/status');
        if (data.success && data.has_signed) {
          setConfidentialityConsent(data.consent);
        }
      } catch (error) {
        console.error('Error loading confidentiality consent:', error);
      }
    };
    loadConsentStatus();
  }, []);

  // Load notification preferences from server for homeowner profiles
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!user?.id || !currentHoaId) return;
        const { data } = await api.get('/api/notifications/preferences', { params: { user_id: user.id, hoa_id: currentHoaId } });
        if (data?.success && data.preferences) {
          const prefs = data.preferences;
          const merged = { ...settings, notifications: { ...settings.notifications, ...prefs.notifications } };
          setSettings(merged);
          localStorage.setItem('hoa_user_settings', JSON.stringify(merged));
        }
      } catch (_) { /* ignore */ }
    };
    loadPrefs();
  }, [user?.id, activeHoaId, user?.hoa_id]);

  const saveNotificationPreferences = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!user?.id || !currentHoaId) return;
      setSavingPrefs(true);
      await api.put('/api/notifications/preferences', {
        notifications: settings.notifications
      }, { params: { user_id: user.id, hoa_id: currentHoaId } });
    } catch (_) { /* ignore */ }
    finally { setSavingPrefs(false); }
  };

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      const profileData = {
        phone: localPhone,
        mailing_address: localMailingAddress
      };
      
      // Only board members can update property address and property identifier
      if (isBoardMemberUser) {
        profileData.property_address = localAddress;
        profileData.property_identifier = localPropertyId;
      }
      
      await api.put('/api/user/profile', profileData);
      alert('✅ Profile updated successfully!');
      window.location.reload(); // Reload to show updated data
    } catch (e) {
      console.error('Failed to save profile', e);
      alert('❌ Failed to save profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEmailChange = async () => {
    try {
      // Validation
      if (!emailChangeForm.newEmail) {
        alert('Please enter a new email address');
        return;
      }
      if (!emailChangeForm.password) {
        alert('Please enter your password to confirm this change');
        return;
      }
      if (emailChangeForm.newEmail === emailChangeForm.currentEmail) {
        alert('New email must be different from current email');
        return;
      }
      
      setChangingEmail(true);
      
      // Call the dedicated email change endpoint
      const { data } = await api.post('/api/user/update-email', {
        new_email: emailChangeForm.newEmail,
        password: emailChangeForm.password
      });
      
      alert('✅ Email changed successfully! Confirmation emails have been sent to BOTH your old and new email addresses.');
      console.log('Email change response:', data);
      
      // Close modal and reset form
      setShowEmailChangeModal(false);
      setEmailChangeForm({
        currentEmail: user?.email || '',
        newEmail: '',
        password: ''
      });
      
      // Reload page to show new email
      setTimeout(() => window.location.reload(), 2000);
      
    } catch (e) {
      console.error('Failed to change email:', e);
      const errorMsg = e.response?.data?.detail || 'Failed to change email. Please check your password and try again.';
      alert('❌ ' + errorMsg);
    } finally {
      setChangingEmail(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      // Validation
      if (!passwordChangeForm.currentPassword) {
        alert('Please enter your current password');
        return;
      }
      if (!passwordChangeForm.newPassword) {
        alert('Please enter a new password');
        return;
      }
      if (passwordChangeForm.newPassword.length < 8) {
        alert('New password must be at least 8 characters');
        return;
      }
      if (passwordChangeForm.newPassword !== passwordChangeForm.confirmPassword) {
        alert('New passwords do not match');
        return;
      }
      
      setChangingPassword(true);
      
      const { data } = await api.post('/api/user/update-password', {
        current_password: passwordChangeForm.currentPassword,
        new_password: passwordChangeForm.newPassword
      });
      
      alert('✅ Password changed successfully! A confirmation email has been sent.');
      console.log('Password change response:', data);
      
      // Close modal and reset form
      setShowPasswordChangeModal(false);
      setPasswordChangeForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (e) {
      console.error('Failed to change password:', e);
      const errorMsg = e.response?.data?.detail || 'Failed to change password. Please check your current password and try again.';
      alert('❌ ' + errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  const maskEmail = (email) => {
    if (!email) return '';
    const [name, domain] = email.split('@');
    if (!domain) return email;
    const first = name[0] || '';
    const last = name.length > 1 ? name[name.length - 1] : '';
    return `${first}${'*'.repeat(Math.max(1, name.length - 2))}${last}@${domain}`;
  };
  const maskPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    const last4 = digits.slice(-4);
    return `***-***-${last4}`;
  };
  const displayEmail = impersonationMode ? maskEmail(user?.email) : user?.email;
  const displayPhone = impersonationMode ? maskPhone(user?.phone) : user?.phone;

  return (
    <div className="space-y-6">
      {/* Profile Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h3>
        <div className="flex items-center space-x-4">
          <ProfilePicture user={user} size="xl" />
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Your Account</h4>
            <p className="text-sm text-gray-900">{user?.first_name} {user?.last_name}</p>
            <p className="text-sm text-gray-600">{displayEmail}</p>
            <p className="text-sm text-gray-600 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        </div>

        {/* Contact & Address for homeowner */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-4">Contact Information</h4>
            
            {/* Email - Read-only with change button */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">Email</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={user?.email || ''}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 cursor-not-allowed"
                  readOnly
                />
                <button
                  onClick={() => {
                    setEmailChangeForm({
                      currentEmail: user?.email || '',
                      newEmail: '',
                      password: ''
                    });
                    setShowEmailChangeModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 whitespace-nowrap"
                >
                  Change Email
                </button>
              </div>
            </div>

            {/* Password - Button only */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">Password</label>
              <button
                onClick={() => setShowPasswordChangeModal(true)}
                className="w-full px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700"
              >
                🔒 Change Password
              </button>
            </div>
            
            <label className="block text-xs text-gray-600 mb-1">Phone</label>
            <input
              type="tel"
              value={localPhone}
              onChange={(e) => setLocalPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="(555) 555-5555"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Property Information</h4>
            <label className="block text-xs text-gray-600 mb-1">Property Address</label>
            <input
              type="text"
              value={localAddress}
              onChange={isBoardMemberUser ? (e) => setLocalAddress(e.target.value) : undefined}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm ${
                isBoardMemberUser ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'
              }`}
              placeholder="123 Main St Apt 4 City State 12345"
              readOnly={!isBoardMemberUser}
            />
            {isBoardMemberUser && (
              <p className="text-xs text-blue-600 mt-1">
                ℹ️ Write out full address with no commas
              </p>
            )}
            {!isBoardMemberUser && (
              <p className="text-xs text-gray-500 mt-1">
                🏠 Property address is managed by the board (Contact board to update)
              </p>
            )}
            <label className="block text-xs text-gray-600 mt-3 mb-1">Mailing Address (if different)</label>
            <input
              type="text"
              value={localMailingAddress}
              onChange={(e) => setLocalMailingAddress(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="PO Box or alternate mailing address"
            />
            <p className="text-xs text-gray-500 mt-1">
              📬 For statements and correspondence (leave blank if same as property address)
            </p>
            <label className="block text-xs text-gray-600 mt-3 mb-1">Property Identifier</label>
            <input
              type="text"
              value={localPropertyId}
              onChange={isBoardMemberUser ? (e) => setLocalPropertyId(e.target.value) : undefined}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm ${
                isBoardMemberUser ? 'bg-white' : 'bg-gray-100'
              }`}
              placeholder="LOT001, Unit 5A, Building B-12"
              readOnly={!isBoardMemberUser}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isBoardMemberUser ? 
                '🏷️ Your unique property identifier for voting and HOA identification purposes' :
                '🏷️ Your unique property identifier for voting and HOA identification purposes (Contact board to update)'
              }
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center space-x-3">
          <button
            onClick={saveProfile}
            disabled={savingProfile}
            className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold disabled:opacity-50"
            data-theme="primary"
          >
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
          <button
            onClick={saveNotificationPreferences}
            disabled={savingPrefs}
            className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold disabled:opacity-50"
            data-theme="primary"
          >
            {savingPrefs ? 'Saving...' : 'Save Notification Preferences'}
          </button>
        </div>
      </div>

      {/* Notification Settings */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Notification Methods</h4>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Email notifications are enabled by default. Use the checkboxes below to control which types of emails you receive.
              </p>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Notification Types</h4>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notifications.newsletters !== false}
                  onChange={(e) => handleSettingChange('notifications', 'newsletters', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Newsletter</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notifications.announcements}
                  onChange={(e) => handleSettingChange('notifications', 'announcements', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Community announcements</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notifications.meeting_agendas !== false}
                  onChange={(e) => handleSettingChange('notifications', 'meeting_agendas', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Meeting agendas from calendar</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Electronic Communications Consent */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">📧 Electronic Communications Consent</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          {user?.electronic_consent?.consented ? (
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    ✅ Consent Given
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {(() => {
                      const timezone = hoaData?.timezone || 'America/Chicago';
                      const { dateStr, timeStr } = formatDateInTimezone(user.electronic_consent.date_signed, timezone);
                      return `Consented on ${dateStr} at ${timeStr}`;
                    })()}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    <strong>Version:</strong> {user.electronic_consent.consent_version || 'N/A'}
                    {hoaData?.timezone && (
                      <span className="ml-2 text-gray-400">({hoaData.timezone})</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  ❌ No Consent on File
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  You have not yet consented to electronic communications. You will be prompted on your next login.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confidentiality and Data Use Notice */}
      {confidentialityConsent && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">🔒 Confidentiality and Data Use Notice</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  ✅ Confidentiality Agreement Signed
                </p>
                <p className="text-sm text-blue-800 mb-3">
                  All individuals with access to HOA data are required to sign and comply with the Association's Confidentiality and Data-Use Agreement. This ensures that all personal or association information is handled securely and used only for official association purposes.
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Date of Acknowledgment:</strong> {new Date(confidentialityConsent.signed_at).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Settings for Board Members */}
      {isBoardMemberUser && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Registration Management</h3>
          {loadingRegSettings ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading registration settings...</p>
            </div>
          ) : registrationSettings ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">🏠 Address Limits</h4>
                  <p className="text-2xl font-bold text-indigo-600 mb-1">
                    {registrationSettings.settings?.users_per_address || 2}
                  </p>
                  <p className="text-sm text-gray-600">Max users per home address</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">📧 Registration Method</h4>
                  <p className="text-lg font-medium text-purple-600 mb-1 capitalize">
                    {registrationSettings.settings?.registration_mode || 'shared'} Codes
                  </p>
                  <p className="text-sm text-gray-600">
                    {registrationSettings.settings?.registration_mode === 'individual' 
                      ? 'Unique codes per email address' 
                      : 'Shared code for all residents'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Failed to load registration settings</p>
            </div>
          )}
        </div>
      )}

      {/* Email Change Modal */}
      {showEmailChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📧 Change Email Address</h3>
            <p className="text-sm text-gray-600 mb-4">
              You will receive confirmation emails at BOTH your current and new email addresses for security.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Email</label>
                <input
                  type="email"
                  value={emailChangeForm.currentEmail}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                  readOnly
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Email *</label>
                <input
                  type="email"
                  value={emailChangeForm.newEmail}
                  onChange={(e) => setEmailChangeForm({...emailChangeForm, newEmail: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="newemail@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm with Password *</label>
                <input
                  type="password"
                  value={emailChangeForm.password}
                  onChange={(e) => setEmailChangeForm({...emailChangeForm, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your current password"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEmailChangeModal(false);
                  setEmailChangeForm({
                    currentEmail: user?.email || '',
                    newEmail: '',
                    password: ''
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={changingEmail}
              >
                Cancel
              </button>
              <button
                onClick={handleEmailChange}
                disabled={changingEmail}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {changingEmail ? 'Changing...' : 'Change Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">🔒 Change Password</h3>
            <p className="text-sm text-gray-600 mb-4">
              You will receive a confirmation email after your password is changed.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password *</label>
                <input
                  type="password"
                  value={passwordChangeForm.currentPassword}
                  onChange={(e) => setPasswordChangeForm({...passwordChangeForm, currentPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Enter current password"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password * (min 8 characters)</label>
                <input
                  type="password"
                  value={passwordChangeForm.newPassword}
                  onChange={(e) => setPasswordChangeForm({...passwordChangeForm, newPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Enter new password"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password *</label>
                <input
                  type="password"
                  value={passwordChangeForm.confirmPassword}
                  onChange={(e) => setPasswordChangeForm({...passwordChangeForm, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordChangeModal(false);
                  setPasswordChangeForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={changingPassword}
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordChange}
                disabled={changingPassword}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Voting Settings Section Component
const VotingSettingsSection = () => {
  const [elections, setElections] = useState([]);
  const [stockPhotos, setStockPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStockPhotoManager, setShowStockPhotoManager] = useState(false);

  useEffect(() => {
    loadVotingData();
    loadStockPhotos();
  }, []);

  const loadVotingData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/vote/elections');
      setElections(response.data.elections || []);
    } catch (error) {
      console.error('Error loading elections:', error);
      setElections([]);
    }
    setLoading(false);
  };

  const loadStockPhotos = () => {
    const defaultStockPhotos = [
      { id: 'board_meeting', name: 'Board Meeting', path: '/assets/events/stock/board_meeting.png' },
      { id: 'pool_party', name: 'Pool Party', path: '/assets/events/stock/pool_party.png' },
      { id: 'community_event', name: 'Community Event', path: '/assets/events/stock/community_event.png' },
      { id: 'maintenance', name: 'Maintenance', path: '/assets/events/stock/maintenance.png' },
      { id: 'social_meeting', name: 'Social Meeting', path: '/assets/events/stock/social_meeting.png' },
      { id: 'workshop', name: 'Workshop', path: '/assets/events/stock/workshop.png' },
      { id: 'holiday_event', name: 'Holiday Event', path: '/assets/events/stock/holiday_event.png' },
      { id: 'fundraiser', name: 'Fundraiser', path: '/assets/events/stock/fundraiser.png' },
      { id: 'emergency_meeting', name: 'Emergency Meeting', path: '/assets/events/stock/emergency_meeting.png' },
      { id: 'other', name: 'Other', path: '/assets/events/stock/other.png' }
    ];
    setStockPhotos(defaultStockPhotos);
  };



  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-2">🗳️ Voting System Settings</h4>
        <p className="text-sm text-gray-500">
          Manage elections, stock photos for events, and voting system configuration.
        </p>
      </div>

      {/* Elections Management */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h5 className="font-medium text-gray-900">Elections Management</h5>
          <div className="text-sm text-indigo-600">
            → Use the <strong>Voting</strong> tab for comprehensive election management
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading elections...</div>
        ) : elections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">🗳️</div>
            <p className="text-gray-600 mb-4">No elections found.</p>
            <p className="text-sm text-gray-500">
              Go to the <strong>Voting</strong> tab to create and manage elections with full board member integration.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {elections.map(election => (
              <div key={election.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h6 className="font-medium text-gray-900">{election.name}</h6>
                    <p className="text-sm text-gray-600">
                      Voting: {new Date(election.voting_open).toLocaleDateString()} - {new Date(election.voting_close).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {getElectionPhase(election)}
                  </span>
                </div>
              </div>
            ))}
            <div className="pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                Manage elections in the <strong>Voting</strong> tab for advanced features
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Stock Photos Management */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h5 className="font-medium text-gray-900">Event Stock Photos</h5>
          <button
            onClick={() => setShowStockPhotoManager(true)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Manage Photos
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stockPhotos.slice(0, 8).map(photo => (
            <div key={photo.id} className="text-center">
              <img 
                src={photo.path} 
                alt={photo.name}
                className="w-full h-20 object-cover rounded-lg mb-2"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <p className="text-xs text-gray-600">{photo.name}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Stock photos are used as default images for community events when no custom image is uploaded.
        </p>
      </div>

      {/* Live Voting Access Control Settings */}
      <LiveVotingAccessControl />
      
      {/* Stock Photo Manager Modal */}
      {showStockPhotoManager && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Stock Photo Manager</h3>
              <button
                onClick={() => setShowStockPhotoManager(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const photoName = prompt('Enter a name for this stock photo:');
                      if (!photoName) return;
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('photo_name', photoName);
                        
                        await api.post('/api/platform-admin/stock-photos/add', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        alert(`Stock photo "${photoName}" added successfully!`);
                        loadStockPhotos();
                      } catch (error) {
                        console.error('Failed to add stock photo:', error);
                        alert('Failed to add stock photo. Please try again.');
                      }
                    }
                  };
                  input.click();
                }}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
              >
                + Add New Stock Photo
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {stockPhotos.map(photo => (
                <div key={photo.id} className="border border-gray-200 rounded-lg p-3">
                  <img 
                    src={photo.path} 
                    alt={photo.name}
                    className="w-full h-32 object-cover rounded-lg mb-2"
                    onError={(e) => {
                      e.target.src = '/assets/events/stock/other.png';
                    }}
                  />
                  <p className="text-sm font-medium text-gray-900">{photo.name}</p>
                  <p className="text-xs text-gray-500 mb-2">{photo.path}</p>
                  <button 
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('stock_photo_id', photo.id);
                            
                            await api.post('/api/platform-admin/stock-photos/replace', formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            
                            alert(`Stock photo "${photo.name}" replaced successfully!`);
                            loadStockPhotos();
                          } catch (error) {
                            console.error('Failed to replace stock photo:', error);
                            alert('Failed to replace stock photo. Please try again.');
                          }
                        }
                      };
                      input.click();
                    }}
                    className="w-full px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    Replace
                  </button>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h6 className="font-medium text-blue-900 mb-2">📝 Stock Photo Usage</h6>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Stock photos are used as default images for community events</li>
                <li>• Homeowners can still upload custom photos when creating events</li>
                <li>• Images are automatically resized and watermarked</li>
                <li>• Recommended size: 1600×900 pixels (landscape)</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Live Voting Access Control Component
const LiveVotingAccessControl = () => {
  const { user } = useAuth();
  const { activeHoaId } = useMultiHOA();
  const [accessSettings, setAccessSettings] = useState({
    live_voting_bod: {
      view_permissions: 'all_members', // default: everyone can view BOD tab
      interaction_permissions: 'board_only', // only board can process BOD
      specific_users: [] // Array of user IDs with special access
    },
    live_voting_committee: {
      view_permissions: 'all_members', // default: everyone can view Committee tab
      interaction_permissions: 'board_and_committee', // board or committee can process
      specific_users: []
    },
    poll_creation_permissions: 'board_only', // default per request: only BOD members
    discussion_property_display: 'property_identifier', // MOVED HERE: Property identifier vs address setting
    quorum_settings: {
      bod_quorum_type: 'majority_or_minimum',
      bod_minimum_members: 3,
      committee_quorum_type: 'majority',
      committee_minimum_members: 2
    },
    voting_requirements: {
      bod_voting_type: 'majority',
      committee_voting_type: 'majority',
      motion_voting_type: 'majority'
    },
    meeting_rules: {
      speaking_time_minutes: 3,
      custom_rules: []
    }
  });
  const [boardMembers, setBoardMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAccessSettings();
    loadMembers();
  }, []);

  const loadAccessSettings = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      // Load live voting settings (access control)
      const votingResponse = await api.get(`/api/hoa/${currentHoaId}/voting/settings`);
      
      // Load meeting rules and approval settings (separate endpoint)
      const votingSettingsResponse = await api.get(`/api/hoa/${currentHoaId}/voting-settings`);
      
      if (votingResponse.data?.success) {
        const loadedSettings = votingResponse.data.settings;
        const votingSettings = votingSettingsResponse.data?.success ? votingSettingsResponse.data.settings : {};
        
        console.log('📥 Loaded voting settings:', loadedSettings);
        console.log('📥 Loaded meeting rules:', votingSettings);
        
        // Ensure all required fields have defaults
        const safeSettings = {
          live_voting_bod: {
            view_permissions: 'board_only',
            interaction_permissions: 'board_only',
            specific_users: [],
            ...loadedSettings.live_voting_bod
          },
          live_voting_committee: {
            view_permissions: 'board_and_committee',
            interaction_permissions: 'board_and_committee',
            specific_users: [],
            ...loadedSettings.live_voting_committee
          },
          poll_creation_permissions: loadedSettings.poll_creation_permissions || 'all_homeowners',
          meeting_rules: {
            speaking_time_minutes: votingSettings.speaking_time_limit_minutes || 3,
            // Load the ACTUAL rules from database (use meeting_rules_raw which has placeholders)
            all_rules: votingSettings.meeting_rules_raw || votingSettings.meeting_rules || [
              "Members of the HOA will be allotted {speaking_time} minutes to speak, address issues, concerns or ask questions that are listed on the agenda, during 'Open to the Floor'.",
              "Board members have no time restriction for responses.",
              "Speakers will be reminded to state their name and property identifier, speak calmly, and clearly for all to hear.",
              "Board members and homeowners are reminded that all ideas are welcome and this is a safe space for everyone to voice their concerns even in difference of opinions.",
              "To transact such other business as may properly come before the Board or any Adjournment or Postponement thereof."
            ]
          }
        };
        
        console.log('✅ Final settings with meeting rules:', safeSettings);
        setAccessSettings(safeSettings);
      }
    } catch (error) {
      console.error('Error loading access settings:', error);
      // Keep default settings if loading fails
    }
  };

  const loadMembers = async () => {
    setLoading(true);
    try {
      // Load board members
      const boardResponse = await api.get('/api/vote/board-members');
      setBoardMembers(boardResponse.data?.board_members || []);
      
      // Load all HOA members
      const membersResponse = await api.get('/api/hoa/members');
      setAllMembers(membersResponse.data?.members || []);
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAccessSettings = async () => {
    setSaving(true);
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      // Save access settings (live voting permissions)
      await api.put(`/api/hoa/${currentHoaId}/voting/settings`, accessSettings);
      
      // Also save meeting rules to voting settings
      const meetingRulesData = {
        meeting_rules: accessSettings.meeting_rules.all_rules,
        speaking_time_limit_minutes: accessSettings.meeting_rules.speaking_time_minutes
      };
      
      console.log('💾 Saving meeting rules:', meetingRulesData);
      console.log('Meeting rules array:', meetingRulesData.meeting_rules);
      
      const response = await api.put(`/api/hoa/${currentHoaId}/voting-settings`, meetingRulesData);
      console.log('✅ Backend response:', response.data);
      
      alert('✅ Live Voting access settings and meeting rules saved successfully!');
    } catch (error) {
      console.error('Error saving access settings:', error);
      console.error('Error details:', error.response?.data);
      alert('❌ Failed to save access settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addCustomRule = () => {
    const newRule = prompt('Enter a new meeting rule:');
    if (newRule && newRule.trim()) {
      setAccessSettings(prev => ({
        ...prev,
        meeting_rules: {
          ...prev.meeting_rules,
          all_rules: [...(prev.meeting_rules.all_rules || []), newRule.trim()]
        }
      }));
    }
  };

  const resetToStandardRules = async () => {
    if (window.confirm('Reset to standard HOA meeting rules? This will replace all rules with the default 5 standard rules.')) {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) return;
        
        // Call the backend reset endpoint
        const response = await api.post(`/api/hoa/${currentHoaId}/voting-settings/reset-meeting-rules`);
        
        if (response.data.success) {
          // Reload settings to get fresh data
          await loadAccessSettings();
          
          alert('✅ Meeting rules have been reset to standard defaults.');
        }
      } catch (error) {
        console.error('Error resetting meeting rules:', error);
        alert('❌ Failed to reset meeting rules. Please try again.');
      }
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h5 className="font-medium text-gray-900">🔒 Live Voting Access Control</h5>
        <button
          onClick={saveAccessSettings}
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Live Voting - BOD Settings */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">🏛️ Live Voting - BOD Settings</h6>
          
          {/* BOD View Permissions */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who can view BOD Live Voting tab
            </label>
            <div className="space-y-2">
              {[
                { value: 'board_only', label: 'Board Members Only', description: 'Only board members can see the BOD Live Voting tab' },
                { value: 'all_members', label: 'All HOA Members', description: 'All residents can view BOD live voting (view only)' }
              ].map(option => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="bod_view_permissions"
                    value={option.value}
                    checked={accessSettings.live_voting_bod.view_permissions === option.value}
                    onChange={(e) => setAccessSettings(prev => ({ 
                      ...prev, 
                      live_voting_bod: { ...prev.live_voting_bod, view_permissions: e.target.value }
                    }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* BOD Interaction Permissions */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who can interact in BOD Live Voting (conduct roll call, create motions, etc.)
            </label>
            <div className="space-y-2">
              {[
                { value: 'board_only', label: 'Board Members Only', description: 'Only board members can control BOD meetings' },
                { value: 'specific_users', label: 'Add Specific People', description: 'Grant interaction access to specific individuals' }
              ].map(option => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="bod_interaction_permissions"
                    value={option.value}
                    checked={accessSettings.live_voting_bod.interaction_permissions === option.value}
                    onChange={(e) => setAccessSettings(prev => ({ 
                      ...prev, 
                      live_voting_bod: { ...prev.live_voting_bod, interaction_permissions: e.target.value }
                    }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* BOD Specific Users Selection */}
          {accessSettings.live_voting_bod.interaction_permissions === 'specific_users' && (
            <div className="bg-blue-50 rounded-lg p-3 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add specific people to BOD interaction (they will automatically be able to view)
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {loading ? (
                  <div className="text-center py-2 text-gray-500 text-sm">Loading members...</div>
                ) : (
                  allMembers.filter(member => !['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(member.role)).map(member => (
                    <label key={member.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={accessSettings.live_voting_bod.specific_users.includes(member.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAccessSettings(prev => ({
                              ...prev,
                              live_voting_bod: {
                                ...prev.live_voting_bod,
                                specific_users: [...prev.live_voting_bod.specific_users, member.id]
                              }
                            }));
                          } else {
                            setAccessSettings(prev => ({
                              ...prev,
                              live_voting_bod: {
                                ...prev.live_voting_bod,
                                specific_users: prev.live_voting_bod.specific_users.filter(id => id !== member.id)
                              }
                            }));
                          }
                        }}
                      />
                      <span className="text-sm text-gray-900">{member.name || member.first_name + ' ' + member.last_name}</span>
                      <span className="text-xs text-gray-500">({member.role})</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Live Voting - Committee Settings */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">🏛️ Live Voting - Committee Settings</h6>
          
          {/* Committee View Permissions */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who can view Committee Live Voting tab
            </label>
            <div className="space-y-2">
              {[
                { value: 'board_and_committee', label: 'Board + Committee Members', description: 'Board members and all committee members can view' },
                { value: 'all_members', label: 'All HOA Members', description: 'All residents can view committee live voting (view only)' }
              ].map(option => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="committee_view_permissions"
                    value={option.value}
                    checked={accessSettings.live_voting_committee.view_permissions === option.value}
                    onChange={(e) => setAccessSettings(prev => ({ 
                      ...prev, 
                      live_voting_committee: { ...prev.live_voting_committee, view_permissions: e.target.value }
                    }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Committee Interaction Permissions */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who can interact in Committee Live Voting (conduct roll call, create motions, etc.)
            </label>
            <div className="space-y-2">
              {[
                { value: 'board_and_committee', label: 'Board + Committee Members Only', description: 'Board members and committee members can control committee meetings' },
                { value: 'specific_users', label: 'Add Specific Members', description: 'Grant interaction access to specific individuals' }
              ].map(option => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="committee_interaction_permissions"
                    value={option.value}
                    checked={accessSettings.live_voting_committee.interaction_permissions === option.value}
                    onChange={(e) => setAccessSettings(prev => ({ 
                      ...prev, 
                      live_voting_committee: { ...prev.live_voting_committee, interaction_permissions: e.target.value }
                    }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Committee Specific Users Selection */}
          {accessSettings.live_voting_committee.interaction_permissions === 'specific_users' && (
            <div className="bg-green-50 rounded-lg p-3 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add specific members to committee interaction
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {loading ? (
                  <div className="text-center py-2 text-gray-500 text-sm">Loading members...</div>
                ) : (
                  allMembers.map(member => (
                    <label key={member.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={accessSettings.live_voting_committee.specific_users.includes(member.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAccessSettings(prev => ({
                              ...prev,
                              live_voting_committee: {
                                ...prev.live_voting_committee,
                                specific_users: [...prev.live_voting_committee.specific_users, member.id]
                              }
                            }));
                          } else {
                            setAccessSettings(prev => ({
                              ...prev,
                              live_voting_committee: {
                                ...prev.live_voting_committee,
                                specific_users: prev.live_voting_committee.specific_users.filter(id => id !== member.id)
                              }
                            }));
                          }
                        }}
                      />
                      <span className="text-sm text-gray-900">{member.name || member.first_name + ' ' + member.last_name}</span>
                      <span className="text-xs text-gray-500">({member.role})</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Poll Creation Permissions */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">📊 Poll Creation Settings</h6>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Who can create polls in the Voting Dashboard
          </label>
          <div className="space-y-2">
            {[
              { value: 'board_only', label: 'Board Members Only', description: 'Only board members can create polls' },
              { value: 'board_and_committee', label: 'Board & Committee Members', description: 'Board members and committee members can create polls' },
              { value: 'all_homeowners', label: 'All Homeowners', description: 'All HOA members can create polls' }
            ].map(option => (
              <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="poll_creation_permissions"
                  value={option.value}
                  checked={accessSettings.poll_creation_permissions === option.value}
                  onChange={(e) => setAccessSettings(prev => ({ ...prev, poll_creation_permissions: e.target.value }))}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">{option.label}</div>
                  <div className="text-xs text-gray-600">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Quorum Settings */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">📊 Quorum Settings</h6>
          
          {/* BOD Quorum */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Board of Directors Quorum</label>
            <div className="space-y-2">
              {[
                { value: 'majority_or_minimum', label: 'Majority or Minimum Required', description: 'Whichever is higher: majority of total board or minimum number' },
                { value: 'majority', label: 'Simple Majority', description: '51% of total board members must be present' },
                { value: 'minimum_only', label: 'Minimum Number Only', description: 'Fixed minimum number regardless of total members' }
              ].map(option => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="bod_quorum_type"
                    value={option.value}
                    checked={accessSettings.quorum_settings?.bod_quorum_type === option.value}
                    onChange={(e) => setAccessSettings(prev => ({
                      ...prev,
                      quorum_settings: {
                        ...prev.quorum_settings,
                        bod_quorum_type: e.target.value
                      }
                    }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-2">
              <label className="block text-xs text-gray-600 mb-1">Minimum BOD Members Required</label>
              <input
                type="number"
                min="1"
                max="10"
                value={accessSettings.quorum_settings?.bod_minimum_members || 3}
                onChange={(e) => setAccessSettings(prev => ({
                  ...prev,
                  quorum_settings: {
                    ...prev.quorum_settings,
                    bod_minimum_members: parseInt(e.target.value) || 3
                  }
                }))}
                className="w-20 px-3 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>

          {/* Committee Quorum */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Committee Quorum</label>
            <div className="space-y-2">
              {[
                { value: 'majority', label: 'Majority of Committee', description: '51% of committee members must be present' },
                { value: 'minimum_only', label: 'Minimum Number', description: 'Fixed minimum number for all committees' }
              ].map(option => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="committee_quorum_type"
                    value={option.value}
                    checked={accessSettings.quorum_settings?.committee_quorum_type === option.value}
                    onChange={(e) => setAccessSettings(prev => ({
                      ...prev,
                      quorum_settings: {
                        ...prev.quorum_settings,
                        committee_quorum_type: e.target.value
                      }
                    }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-2">
              <label className="block text-xs text-gray-600 mb-1">Minimum Committee Members Required</label>
              <input
                type="number"
                min="1"
                max="5"
                value={accessSettings.quorum_settings?.committee_minimum_members || 2}
                onChange={(e) => setAccessSettings(prev => ({
                  ...prev,
                  quorum_settings: {
                    ...prev.quorum_settings,
                    committee_minimum_members: parseInt(e.target.value) || 2
                  }
                }))}
                className="w-20 px-3 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        </div>

        {/* Meeting Rules */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">📋 Meeting Rules Configuration</h6>
          
          {/* Speaking Time */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Speaking Time Limit (minutes)</label>
            <input
              type="number"
              min="1"
              max="10"
              value={accessSettings.meeting_rules.speaking_time_minutes}
              onChange={(e) => setAccessSettings(prev => ({
                ...prev,
                meeting_rules: {
                  ...prev.meeting_rules,
                  speaking_time_minutes: parseInt(e.target.value) || 3
                }
              }))}
              className="w-20 px-3 py-1 border border-gray-300 rounded text-sm"
            />
            <span className="text-xs text-gray-500 ml-2">Default: 3 minutes per member during 'Open to the Floor'</span>
          </div>

          {/* Meeting Rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Meeting Rules (Editable)</label>
              <div className="space-x-2">
                <button
                  onClick={addCustomRule}
                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                >
                  + Add Rule
                </button>
                <button
                  onClick={resetToStandardRules}
                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  title="Reset to standard HOA meeting rules"
                >
                  🔄 Reset to Standard
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {(accessSettings.meeting_rules.all_rules || []).map((rule, index) => (
                <div key={index} className="flex items-start gap-2 bg-white border rounded p-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-semibold">
                    {index + 1}
                  </div>
                  <textarea
                    value={rule}
                    onChange={(e) => {
                      const newRules = [...(accessSettings.meeting_rules.all_rules || [])];
                      newRules[index] = e.target.value;
                      setAccessSettings(prev => ({
                        ...prev,
                        meeting_rules: {
                          ...prev.meeting_rules,
                          all_rules: newRules
                        }
                      }));
                    }}
                    className="flex-1 text-sm text-gray-700 border border-gray-300 rounded p-2 min-h-[60px]"
                    placeholder={`Rule ${index + 1}`}
                  />
                  {index >= 5 && (
                    <button
                      onClick={() => {
                        const newRules = (accessSettings.meeting_rules.all_rules || []).filter((_, i) => i !== index);
                        setAccessSettings(prev => ({
                          ...prev,
                          meeting_rules: {
                            ...prev.meeting_rules,
                            all_rules: newRules
                          }
                        }));
                      }}
                      className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              💡 Tip: Use {'{speaking_time}'} in Rule #1 to automatically insert the speaking time limit.
            </div>
          </div>
        </div>

        {/* Discussion Property Display Setting - MOVED TO CORRECT LOCATION */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">🏠 Discussion Property Display</h6>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Property information shown in discussion member search
          </label>
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={async () => {
                const newValue = 'property_identifier';
                setAccessSettings(prev => ({ ...prev, discussion_property_display: newValue }));
                const updatedSettings = { ...accessSettings, discussion_property_display: newValue };
                try {
                  await api.put(`/api/hoa/${user?.hoa_id}/voting/settings`, updatedSettings);
                } catch (error) {
                  setAccessSettings(prev => ({ ...prev, discussion_property_display: accessSettings.discussion_property_display }));
                }
              }}
              className={`px-3 py-2 text-sm font-medium border rounded-l-md ${accessSettings.discussion_property_display === 'property_identifier' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'}`}
            >
              Property Identifier (Unit/Lot)
            </button>
            <button
              type="button"
              onClick={async () => {
                const newValue = 'address';
                setAccessSettings(prev => ({ ...prev, discussion_property_display: newValue }));
                const updatedSettings = { ...accessSettings, discussion_property_display: newValue };
                try {
                  await api.put(`/api/hoa/${user?.hoa_id}/voting/settings`, updatedSettings);
                } catch (error) {
                  setAccessSettings(prev => ({ ...prev, discussion_property_display: accessSettings.discussion_property_display }));
                }
              }}
              className={`px-3 py-2 text-sm font-medium border rounded-r-md -ml-px ${accessSettings.discussion_property_display === 'address' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'}`}
            >
              Full Address
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Choose what property information to display when searching members in discussions. Property identifier provides privacy protection.
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper function for election phase
const getElectionPhase = (election) => {
  const now = Date.now();
  const nominationsOpen = new Date(election.nominations_open).getTime();
  const nominationsClose = new Date(election.nominations_close).getTime();
  const votingOpen = new Date(election.voting_open).getTime();
  const votingClose = new Date(election.voting_close).getTime();
  
  if (now < nominationsOpen) return 'Draft';
  if (now < nominationsClose) return 'Nominations Open';
  if (now < votingOpen) return 'Pending Vote';
  if (now < votingClose) return 'Voting Active';
  return 'Completed';
};

// User Management Settings Component with Complete Functionality
const UserManagementSettings = ({ boardMembers, setBoardMembers, newMemberData, setNewMemberData }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyIdentifier, setPropertyIdentifier] = useState('');
  const [inviteRole, setInviteRole] = useState('board_member');
  const [sending, setSending] = useState(false);
  const [activeUserTab, setActiveUserTab] = useState('board_members');
  const [showAddHomeowner, setShowAddHomeowner] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [propertyIdValues, setPropertyIdValues] = useState({});
  
  // Upgrade to Board Member state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeUser, setUpgradeUser] = useState(null);
  const [upgradeRole, setUpgradeRole] = useState('board_member');
  
  // Document permissions settings
  const [documentSettings, setDocumentSettings] = useState({
    allowHomeownerDelete: false,
    allowHomeownerUpload: false,  // OFF by default for all HOAs
    allowSecurityUpload: false,
    allowMaintenanceUpload: false
  });

  // Property sale/transfer state
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertySaleModal, setShowPropertySaleModal] = useState(false);
  const [showOwnershipHistory, setShowOwnershipHistory] = useState(false);
  const [ownershipHistory, setOwnershipHistory] = useState([]);
  const [saleForm, setSaleForm] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    new_owner_email: '',
    new_owner_name: '',
    new_owner_phone: ''
  });
  
  const boardRoleLabel = (role) => {
    const map = { president: 'President', vice_president: 'Vice President', treasurer: 'Treasurer', secretary: 'Secretary', board_member: 'Board Member' };
    return map[role] || 'Board Member';
  };
  
  // Use real data from context
  const { homeowners, archivedHomeowners, pendingApprovals, approveUser, rejectUser, hoaSettings, fetchHomeowners, fetchArchivedHomeowners, fetchPendingApprovals } = useHOA();
  const { user } = useAuth();
  const { activeHoaId } = useMultiHOA();

  // Listen for settings tab changes from quick actions
  useEffect(() => {
    const handleSetSettingsTab = (event) => {
      if (event.detail.tab === 'users' && event.detail.subTab) {
        setActiveUserTab(event.detail.subTab);
      }
    };
    
    window.addEventListener('setSettingsTab', handleSetSettingsTab);
    return () => window.removeEventListener('setSettingsTab', handleSetSettingsTab);
  }, []);

  // Load document settings on component mount
  useEffect(() => {
    const savedSettings = JSON.parse(localStorage.getItem('hoa_document_settings') || '{}');
    setDocumentSettings({
      allowHomeownerDelete: savedSettings.allowHomeownerDelete || false,
      allowHomeownerUpload: savedSettings.allowHomeownerUpload || false,  // Default to false
      allowSecurityUpload: savedSettings.allowSecurityUpload || false,
      allowMaintenanceUpload: savedSettings.allowMaintenanceUpload || false
    });
  }, []);

  // Save document settings when they change
  const saveDocumentSettings = (newSettings) => {
    setDocumentSettings(newSettings);
    localStorage.setItem('hoa_document_settings', JSON.stringify(newSettings));
  };

  // Helper function to count unique properties
  const getUniquePropertiesCount = () => {
    const uniqueAddresses = new Set();
    homeowners.forEach(homeowner => {
      if (homeowner.property_address || homeowner.address) {
        // Normalize to lowercase for case-insensitive counting
        uniqueAddresses.add((homeowner.property_address || homeowner.address).toLowerCase().trim());
      }
    });
    boardMembers.forEach(boardMember => {
      if (boardMember.property_address || boardMember.address) {
        // Normalize to lowercase for case-insensitive counting
        uniqueAddresses.add((boardMember.property_address || boardMember.address).toLowerCase().trim());
      }
    });
    pendingApprovals.forEach(homeowner => {
      if (homeowner.property_address || homeowner.address) {
        // Normalize to lowercase for case-insensitive counting
        uniqueAddresses.add((homeowner.property_address || homeowner.address).toLowerCase().trim());
      }
    });
    return uniqueAddresses.size;
  };

  // Helper function to group users by property
  const getPropertiesWithOccupants = () => {
    const propertiesMap = new Map();
    
    // Add current homeowners
    homeowners.forEach(homeowner => {
      const rawAddress = homeowner.property_address || homeowner.address;
      if (rawAddress) {
        // Normalize address to lowercase for case-insensitive matching
        const normalizedAddress = rawAddress.toLowerCase().trim();
        
        if (!propertiesMap.has(normalizedAddress)) {
          propertiesMap.set(normalizedAddress, {
            address: rawAddress, // Keep original case for display
            property_identifier: homeowner.property_identifier || 'N/A',
            occupants: [],
            pending_occupants: []
          });
        }
        propertiesMap.get(normalizedAddress).occupants.push({
          ...homeowner,
          status: 'active',
          name: `${homeowner.first_name || ''} ${homeowner.last_name || ''}`.trim() || homeowner.name || homeowner.email
        });
      }
    });

    // Add board members (they are also property owners)
    boardMembers.forEach(boardMember => {
      const rawAddress = boardMember.property_address || boardMember.address;
      if (rawAddress) {
        // Normalize address to lowercase for case-insensitive matching
        const normalizedAddress = rawAddress.toLowerCase().trim();
        
        // Check if this board member is already added as a homeowner (avoid duplicates)
        const existingProperty = propertiesMap.get(normalizedAddress);
        const isDuplicate = existingProperty?.occupants.some(occupant => 
          occupant.email === boardMember.email || occupant.id === boardMember.id
        );
        
        if (!isDuplicate) {
          if (!propertiesMap.has(normalizedAddress)) {
            propertiesMap.set(normalizedAddress, {
              address: rawAddress, // Keep original case for display
              property_identifier: boardMember.property_identifier || 'N/A',
              occupants: [],
              pending_occupants: []
            });
          }
          propertiesMap.get(normalizedAddress).occupants.push({
            ...boardMember,
            status: 'active',
            name: `${boardMember.first_name || ''} ${boardMember.last_name || ''}`.trim() || boardMember.name || boardMember.email,
            role: boardMember.role // Keep board role for display
          });
        }
      }
    });

    // Add pending approvals
    pendingApprovals.forEach(homeowner => {
      const rawAddress = homeowner.property_address || homeowner.address;
      if (rawAddress) {
        // Normalize address to lowercase for case-insensitive matching
        const normalizedAddress = rawAddress.toLowerCase().trim();
        
        if (!propertiesMap.has(normalizedAddress)) {
          propertiesMap.set(normalizedAddress, {
            address: rawAddress, // Keep original case for display
            property_identifier: homeowner.property_identifier || 'N/A',
            occupants: [],
            pending_occupants: []
          });
        }
        propertiesMap.get(normalizedAddress).pending_occupants.push({
          ...homeowner,
          status: 'pending',
          name: `${homeowner.first_name || ''} ${homeowner.last_name || ''}`.trim() || homeowner.name || homeowner.email
        });
      }
    });

    return Array.from(propertiesMap.values()).sort((a, b) => a.address.localeCompare(b.address));
  };

  const [newHomeowner, setNewHomeowner] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    propertyType: 'single_family',
    property_identifier: ''
  });

  const handleSendInvite = async () => {
    // Validate required fields
    if (!fullName || !inviteEmail) {
      alert('⚠️ Please fill in Full Name and Email');
      return;
    }

    setSending(true);
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      
      // Parse full name into first and last
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const inviteData = {
        email: inviteEmail,
        first_name: firstName,
        last_name: lastName,
        property_address: propertyAddress,
        property_identifier: propertyIdentifier,
        role: inviteRole,
        hoa_id: currentHoaId
      };
      
      const { data } = await api.post(`/api/vote/board/${currentHoaId}/invite-board-member`, inviteData);
      
      if (data.success) {
        alert(`✅ Board Member Invitation Sent Successfully!\n\n👤 Name: ${fullName}\n📧 Email: ${inviteEmail}\n🏛️ Role: ${inviteRole.replace('_', ' ').toUpperCase()}\n🔐 Temporary password sent via email\n\n⏳ The board member can now log in at hoamanagerpro.us/login`);
        setInviteEmail('');
        setFullName('');
        setPropertyAddress('');
        setPropertyIdentifier('');
        setInviteRole('board_member');
        // Refresh board members list if available
        if (typeof fetchBoardMembers === 'function') {
          await fetchBoardMembers();
        }
      } else {
        alert(`❌ Failed to send invitation: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(`❌ Error sending invitation: ${error.response?.data?.detail || error.message || 'Please try again'}`);
    }
    setSending(false);
  };
  
  const approveHomeowner = async (homeownerId) => {
    try {
      const res = await approveUser(homeownerId);
      if (res?.success) {
        const homeowner = homeowners.find(h => h.id === homeownerId) || {};
        const fullName = `${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim();
        alert(`✅ Homeowner Approved!\n\n👤 Name: ${fullName}\n📧 Email: ${homeowner.email || ''}\n\n✅ Status: Active\n\n📧 EMAIL NOTIFICATION:\n• User will receive approval email shortly\n• Email includes login instructions\n• For password setup, direct them to:\n  1. Contact HOA board members\n  2. Board can generate a temporary password\n  3. User will be required to change password on first login\n\n📅 Approved: ${new Date().toLocaleDateString()}`);
      } else {
        alert('Approval failed');
      }
    } catch (e) {
      alert('Approval failed');
    }
  };
  
  const suspendHomeowner = async (homeownerId) => {
    const homeowner = homeowners.find(h => h.id === homeownerId) || {};
    const fullName = `${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim();
    
    const confirmed = confirm(`Suspend ${fullName}? They will lose portal access until reactivated.`);
    if (confirmed) {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) return;
        
        const { data } = await api.put(`/api/hoa/${currentHoaId}/suspend-homeowner/${homeownerId}`);
        
        if (data.success) {
          alert(`⚠️ Homeowner Suspended!\n\n👤 Name: ${fullName}\n📧 Email: ${homeowner.email}\n\n🚫 Access: Portal access revoked\n📧 Notification: User will be notified\n🔄 Note: Can be reactivated at any time`);
          fetchHomeowners(); // Refresh list
        } else {
          alert('❌ Failed to suspend homeowner: ' + (data.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error suspending homeowner:', error);
        alert('❌ Error suspending homeowner. Please try again.');
      }
    }
  };
  
  const reactivateHomeowner = async (homeownerId) => {
    const homeowner = homeowners.find(h => h.id === homeownerId) || {};
    const fullName = `${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim();
    
    const confirmed = confirm(`Reactivate ${fullName}? They will regain portal access.`);
    if (confirmed) {
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) return;
        
        const { data } = await api.put(`/api/hoa/${currentHoaId}/reactivate-homeowner/${homeownerId}`);
        
        if (data.success) {
          alert(`✅ Homeowner Reactivated!\n\n👤 Name: ${fullName}\n📧 Email: ${homeowner.email}\n\n✅ Access: Portal access restored\n📧 Notification: User will be notified`);
          fetchHomeowners(); // Refresh list
        } else {
          alert('❌ Failed to reactivate homeowner: ' + (data.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error reactivating homeowner:', error);
        alert('❌ Error reactivating homeowner. Please try again.');
      }
    }
  };
  
  const removeHomeowner = async (homeownerId) => {
    const homeowner = homeowners.find(h => h.id === homeownerId) || {};
    const fullName = `${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim();
    
    const reason = prompt(`Remove ${fullName} from the community?\n\nPlease provide a reason for removal (required):\n\nCommon reasons:\n• Property sold\n• Moved out\n• Account consolidation\n• Other`);
    
    if (reason && reason.trim()) {
      const notes = prompt(`Optional: Add any additional notes for this removal:\n\n• Forwarding address\n• Contact information\n• Special instructions`);
      
      const finalConfirm = confirm(`FINAL CONFIRMATION\n\nRemove: ${fullName}\nReason: ${reason}\nNotes: ${notes || 'None'}\n\nThis action CANNOT be undone. The homeowner will be archived.\n\nProceed with removal?`);
      
      if (finalConfirm) {
        try {
          const currentHoaId = activeHoaId || user?.hoa_id;
          if (!currentHoaId) return;
          
          const { data } = await api.delete(`/api/hoa/${currentHoaId}/remove-homeowner/${homeownerId}`, {
            data: {
              reason: reason.trim(),
              notes: notes ? notes.trim() : '',
              removed_by: user.id,
              removed_at: new Date().toISOString()
            }
          });
          
          if (data.success) {
            alert(`🗑️ Homeowner Removed\n\n👤 Name: ${fullName}\n📧 Email: ${homeowner.email}\n📝 Reason: ${reason}\n🗂️ Status: Archived\n\n✅ Removal logged successfully`);
            fetchHomeowners(); // Refresh active list
            fetchArchivedHomeowners(); // Refresh archived list
          } else {
            alert('❌ Failed to remove homeowner: ' + (data.message || 'Unknown error'));
          }
        } catch (error) {
          console.error('Error removing homeowner:', error);
          alert('❌ Error removing homeowner. Please try again.');
        }
      }
    }
  };
  
  const addNewHomeowner = async () => {
    if (!newHomeowner.name || !newHomeowner.email || !newHomeowner.address) {
      alert('❌ Please fill in all required fields');
      return;
    }
    
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      // Split name into first and last name
      const nameParts = newHomeowner.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const homeownerData = {
        email: newHomeowner.email,
        first_name: firstName,
        last_name: lastName,
        property_address: newHomeowner.address,
        mailing_address: newHomeowner.mailing_address || '',
        property_identifier: newHomeowner.property_identifier || '',
        phone: newHomeowner.phone || '',
        role: 'homeowner',
        verification_status: 'pending',
        is_active: true,
        hoa_id: currentHoaId,
        property_type: newHomeowner.propertyType
      };
      
      const { data } = await api.post(`/api/hoa/${currentHoaId}/add-homeowner`, homeownerData);
      
      const result = data;
      
      if (result.success) {
        alert(`✅ Homeowner Added Successfully!\n\n👤 Name: ${newHomeowner.name}\n📧 Email: ${newHomeowner.email}\n🏠 Address: ${newHomeowner.address}\n🏷️ Property ID: ${newHomeowner.property_identifier || 'Not assigned'}\n📞 Phone: ${newHomeowner.phone}\n\n🔐 PASSWORD SETUP:\n• User will receive approval notification via email\n• Board members can generate temporary passwords\n• Direct users to contact board for initial password\n• No temporary passwords are created for security reasons\n\n📧 NEXT STEPS:\n• Approve this homeowner when ready\n• User will get email with login instructions\n• Board can provide temporary password for first login\n\n✅ The homeowner has been added to the system and will appear in the Pending Approvals tab.`);
        
        setNewHomeowner({ 
          name: '', 
          email: '', 
          address: '', 
          phone: '', 
          propertyType: 'single_family',
          property_identifier: ''
        });
        setShowAddHomeowner(false);
        
        // Refresh homeowners list and pending approvals
        await fetchHomeowners();
        await fetchPendingApprovals();
        
      } else {
        alert(`❌ Failed to add homeowner: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding homeowner:', error);
      alert('❌ Error adding homeowner. Please try again.');
    }
  };

  // PERMANENTLY DELETE phantom users from database
  const deletePantomUsers = async () => {
    if (!confirm('🗑️ PERMANENTLY DELETE phantom users (John Homeowner, Jane Resident) from database?\n\nThis will remove them completely from all records including attendance and discussions.\n\nContinue?')) {
      return;
    }
    
    try {
      const response = await api.delete('/api/board/users/delete-phantom-users');
      
      if (response.data?.success) {
        const results = response.data;
        alert(`✅ PHANTOM USERS PERMANENTLY DELETED!\n\n🗑️ Deleted Users:\n${results.deleted_users.join('\n')}\n\n📊 Total Records Removed:\n• User records: ${results.total_user_records_deleted}\n• Attendance records: ${results.attendance_records_deleted}\n• Discussion records: ${results.discussion_records_deleted}\n\n💡 Phantom users will no longer appear anywhere in the system.`);
        
        // Refresh all data
        fetchHomeowners();
      } else {
        throw new Error('Delete phantom users response indicated failure');
      }
    } catch (error) {
      console.error('Delete phantom users error:', error);
      alert(`❌ Failed to delete phantom users: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Data cleanup function to fix field contamination
  const handleDataCleanup = async () => {
    if (!confirm('This will fix data integrity issues like email appearing in phone fields and missing property identifiers. Continue?')) {
      return;
    }
    
    try {
      const response = await api.post('/api/board/users/cleanup-data');
      
      if (response.data?.success) {
        const results = response.data.results;
        alert(`✅ DATA CLEANUP COMPLETED!\n\n📊 Results:\n• Fixed email in phone field: ${results.fixed_email_in_phone} users\n• Populated property identifiers: ${results.populated_property_identifiers} users\n• Errors: ${results.errors.length}\n\n💡 Please export CSV again to see cleaned data.`);
        
        // Refresh the homeowners list
        fetchHomeowners();
      } else {
        throw new Error('Cleanup response indicated failure');
      }
    } catch (error) {
      console.error('Data cleanup error:', error);
      alert(`❌ Data cleanup failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  // CSV Export function for homeowners - USE BACKEND API
  const handleExportHomeownersCSV = async () => {
    try {
      const response = await api.get('/api/board/users/export', {
        params: { user_type: 'homeowner' },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `hoa_users_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      alert(`✅ All Users CSV Downloaded!\n\n📁 File: hoa_users_export_${new Date().toISOString().split('T')[0]}.csv\n📊 Records: ALL users (approved, pending, conditional, rejected)\n\n💡 TIP: This includes ALL users regardless of status - edit and re-upload to bulk update`);
    } catch (error) {
      console.error('Export error:', error);
      alert(`❌ Export failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  // CSV Import function for homeowners
  const handleImportHomeownersCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvData = e.target.result;
        const rows = csvData.split('\n').map(row => row.split(','));
        const headers = rows[0];
        const dataRows = rows.slice(1).filter(row => row.some(cell => cell.trim()));

        let users = dataRows.map(row => {
          const user = {};
          headers.forEach((header, index) => {
            user[header.trim()] = (row[index] || '').trim();
          });
          return user;
        });

        // Sort users to handle new vs existing
        const existingEmails = new Set(homeowners.map(h => h.email));
        const newUsers = users.filter(u => !existingEmails.has(u.email));
        const existingUpdates = users.filter(u => existingEmails.has(u.email));

        processCSVImport(users, newUsers, existingUpdates);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        alert('❌ Error parsing CSV file. Please ensure it follows the correct format.');
      }
    };
    reader.readAsText(file);
  };

  const processCSVImport = async (users, newApprovals, statusChanges) => {
    try {
      // Create FormData for file upload to match backend expectation
      const csvContent = [
        'email,first_name,last_name,property_address,property_identifier,phone,role,verification_status,is_active',
        ...users.map(user => 
          `${user.email},${user.first_name},${user.last_name},${user.property_address},${user.property_identifier},${user.phone},${user.role},${user.verification_status},${user.is_active}`
        )
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const formData = new FormData();
      formData.append('file', blob, 'import.csv');
      
      // Use the backend import endpoint with user_type parameter
      const { data } = await api.post('/api/board/users/import?user_type=homeowner', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (data.success) {
        const { created, updated, errors, error_count } = data;
        
        let errorDetails = '';
        if (errors && errors.length > 0) {
          // Show first 10 errors with details
          const errorSample = errors.slice(0, 10);
          errorDetails = '\n\n❌ ERROR DETAILS:\n' + errorSample.map(e => 
            `Row ${e.row}: ${e.error}${e.email ? ` (${e.email})` : ''}`
          ).join('\n');
          if (errors.length > 10) {
            errorDetails += `\n... and ${errors.length - 10} more errors`;
          }
        }
        
        alert(`✅ CSV Import Completed!\n\n📊 RESULTS:\n• Created: ${created} new users\n• Updated: ${updated} existing users\n• Errors: ${error_count} rows had issues${errorDetails}\n\nRefresh the page to see updated user lists.`);
        
        // Refresh data - try to call functions if they exist, otherwise reload page
        try {
          // Check if functions exist in current scope before calling
          if (typeof window.hoaRefreshFunctions !== 'undefined') {
            // Call the refresh functions if they're available
            await window.hoaRefreshFunctions.fetchHomeowners();
            await window.hoaRefreshFunctions.fetchPendingApprovals();
          } else {
            // Fallback to page refresh after short delay
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          }
        } catch (refreshError) {
          console.warn('Could not refresh data automatically, reloading page:', refreshError);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
        
      } else {
        alert(`❌ Import failed: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      const detail = error.response?.data?.detail;
      const errors = error.response?.data?.errors;
      alert(
        `❌ Import failed: ${detail || error.message}${
          errors ? `\n\nRow errors:\n- ${errors.join('\n- ')}` : ''
        }`
      );
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">User Management</h3>
        <p className="text-sm text-gray-600 mb-4">
          Manage board members, homeowners, and community access. Send invitations and handle approvals.
        </p>
        
        {/* One Email Per Account Policy */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-900 mb-1">One Email Per Account Policy</h4>
              <p className="text-xs text-blue-800 leading-relaxed">
                This platform uses one email address per account across the entire system. If you have a second property or are part of a second HOA, you should use a second email address. A third property/HOA requires a third email, and so on. This helps track voting rights and eligibility, ensuring one property or HOA membership is always tied to only one account. If you are removed from an HOA and archived, your record can be wiped so you can reuse your email without issues.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* User Management Tabs */}
      <div className="border-b border-gray-200">
        <div className="overflow-x-auto overscroll-x-contain" style={{WebkitOverflowScrolling: 'touch'}}>
          <nav className="-mb-px flex space-x-8 min-w-max px-2">
            {[
              { id: 'board_members', name: 'Board Members', count: Array.isArray(boardMembers) ? boardMembers.length : 0 },
              { id: 'homeowners', name: 'Homeowners', count: homeowners.length },
              { id: 'archived_homeowners', name: 'Archived Homeowners', count: archivedHomeowners.length, icon: '🗂️' },
              { id: 'property_management', name: 'Property Management', count: getUniquePropertiesCount() },
              { id: 'pending_approvals', name: 'Pending Approvals', count: pendingApprovals.length },
              { id: 'voting', name: 'Voting Settings', count: 0, icon: '🗳️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveUserTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeUserTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon && <span className="mr-1">{tab.icon}</span>}
                {tab.name} {tab.count !== undefined && `(${tab.count})`}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Board Members Tab */}
      {activeUserTab === 'board_members' && (
        <div className="space-y-6">
          {/* Invite New Board Member */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-md font-medium text-blue-900 mb-3">🏛️ Invite Board Member</h4>
      {/* Summary header with board member count */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">
          Board Members: <span className="font-semibold">{Array.isArray(boardMembers) ? boardMembers.length : 0}</span>
          {/* Platform Admin Info */}
          <div className="bg-white border border-yellow-200 rounded-lg p-4 mb-4">
            <h5 className="text-sm font-medium text-gray-900 mb-2">HOA Admin</h5>
            <HOAAdminInfo hoaId={user?.hoa_id} />
          </div>

        </div>
      </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Full Name *"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="email"
                placeholder="Email *"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="text"
                placeholder="Property Address (no commas)"
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="text"
                placeholder="Property Identifier (Lot/Unit #)"
                value={propertyIdentifier}
                onChange={(e) => setPropertyIdentifier(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="board_member">Board Member</option>
                <option value="president">President</option>
                <option value="vice_president">Vice President</option>
                <option value="treasurer">Treasurer</option>
                <option value="secretary">Secretary</option>
              </select>
            </div>
            <button
              onClick={handleSendInvite}
              disabled={sending}
              className="mt-4 w-full primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold disabled:opacity-50"
              data-theme="primary"
            >
              {sending ? 'Sending...' : '📧 Send Invitation'}
            </button>
          </div>

          {/* Current Board Members */}
          {/* Board Members Table - MOBILE FRIENDLY: Horizontal scroll + touch-friendly */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h4 className="text-md font-medium text-gray-900">Current Board Members</h4>
            </div>
            <div className="overflow-x-auto overscroll-x-contain" style={{WebkitOverflowScrolling: 'touch'}}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mailing Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(boardMembers) && boardMembers.length > 0 ? boardMembers.map((m) => (
                    <tr key={m.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{`${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email}</div>
                          <div className="text-sm text-gray-500">{m.email}</div>
                        </div>
                      </td>
                      {/* Property Address column */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{m.property_address || 'Not provided'}</div>
                      </td>
                      {/* Mailing Address column */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{m.mailing_address || '-'}</div>
                        {m.mailing_address && <div className="text-xs text-gray-500">Diff. from property</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 capitalize">
                          {(m.role || 'board_member').replace('_',' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {/* View Details Button */}
                        <button
                          onClick={() => {
                            const fullName = `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email;
                            const timezone = hoaSettings?.timezone || 'America/Chicago';  // FIX: Use hoaSettings instead of hoaData
                            
                            // Format consent history
                            let consentStatus = '';
                            const consent = m.electronic_consent;
                            const isSuspended = m.is_suspended;
                            const suspensionReason = m.suspension_reason;
                            
                            if (isSuspended) {
                              consentStatus = `🚫 ACCOUNT SUSPENDED\nReason: ${suspensionReason || 'No reason provided'}\n\n`;
                            }
                            
                            if (consent?.consented) {
                              const { dateStr, timeStr } = formatDateInTimezone(consent.date_signed, timezone);
                              consentStatus += `✅ Current Status: Consented\n📅 Date: ${dateStr} at ${timeStr}\n📋 Version: ${consent.consent_version || 'N/A'}\n⏰ Timezone: ${timezone}`;
                            } else {
                              consentStatus += `❌ Current Status: No active consent`;
                            }
                            
                            // Add history if available
                            if (consent?.history && consent.history.length > 0) {
                              consentStatus += '\n\n📜 CONSENT HISTORY:\n';
                              consent.history.forEach((entry, idx) => {
                                const { dateStr, timeStr } = formatDateInTimezone(entry.timestamp, timezone);
                                const action = entry.action.toUpperCase();
                                consentStatus += `${idx + 1}. ${action} on ${dateStr} at ${timeStr}`;
                                if (entry.reason) consentStatus += ` (${entry.reason})`;
                                consentStatus += '\n';
                              });
                            }
                            
                            alert(`📋 BOARD MEMBER DETAILS\n\n👤 Name: ${fullName}\n📧 Email: ${m.email || ''}\n🏠 Property Address: ${m.property_address || '-'}\n📬 Mailing Address: ${m.mailing_address || 'Same as property address'}\n📛 Role: ${(m.role || 'board_member').replace('_',' ').toUpperCase()}\n📊 Status: ${isSuspended ? 'SUSPENDED' : 'ACTIVE'}\n\n📧 Electronic Communications Consent:\n${consentStatus}`);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          View Details
                        </button>

                        {/* Edit Role inline for platform admins */}
                        <div className="flex items-center space-x-2 mt-2">
                          <select
                            value={m.role}
                            onChange={e => setBoardMembers(prev => prev.map(bm => bm.id === m.id ? { ...bm, role: e.target.value, _dirty: true } : bm))}
                            className="text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 capitalize"
                          >
                            <option value="board_member">Board Member</option>
                            <option value="president">President</option>
                            <option value="vice_president">Vice President</option>
                            <option value="treasurer">Treasurer</option>
                            <option value="secretary">Secretary</option>
                          </select>
                          <button
                            onClick={async () => {
                              try {
                                const updated = boardMembers.find(b => b.id === m.id);
                                if (!updated || !updated._dirty) return;
                                await api.put(`/api/hoa/${user.hoa_id}/board-members/${m.id}/role`, { role: updated.role });
                                // Refresh lists used across app
                                const [bmRes] = await Promise.all([
                                  api.get('/api/board/members')
                                ]);
                                setBoardMembers(bmRes.data?.board_members || []);
                                // Also refresh Community Information on Communications tab via event
                                window.dispatchEvent(new CustomEvent('refreshBoardMembers'));
                                alert('✅ Role updated');
                              } catch (e) {
                                alert(`❌ Failed to update role: ${e.response?.data?.detail || e.message}`);
                              }
                            }}
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              // revert local edit by refetching
                              api.get('/api/board/members').then(res => setBoardMembers(res.data?.board_members || []));
                            }}
                            className="text-gray-500 hover:text-gray-700 text-sm"
                          >
                            Cancel
                          </button>
                        </div>

                        {/* Downgrade Board Member */}
                        <button 
                          onClick={async () => {
                            const fullName = `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email;
                            const confirmed = window.confirm(
                              `⚠️ Downgrade ${fullName} from board position?\n\n` +
                              `This will:\n` +
                              `• Remove board member role\n` +
                              `• Keep their user account active\n` +
                              `• They can be re-invited later\n\n` +
                              `Continue?`
                            );
                            
                            if (!confirmed) return;
                            
                            try {
                              await api.delete(`/api/vote/board/${user.hoa_id}/members/${m.id}`);
                              
                              // Refresh board members list
                              const { data } = await api.get('/api/vote/board-members');
                              setBoardMembers(data?.board_members || []);
                              
                              alert(`✅ ${fullName} downgraded from board successfully`);
                            } catch (error) {
                              console.error('Error downgrading board member:', error);
                              alert(`❌ Failed to downgrade: ${error.response?.data?.detail || error.message}`);
                            }
                          }}
                          className="text-red-600 hover:text-red-900 mt-2 font-medium"
                        >
                          Downgrade
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-500" colSpan={4}>No board members found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Homeowners Tab */}
      {activeUserTab === 'homeowners' && (
        <div className="space-y-6">
          {/* Add New Homeowner Button */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
            <div>
              <h4 className="text-md font-medium text-gray-900">Community Homeowners</h4>
              <p className="text-sm text-gray-500">Manage homeowner accounts and portal access</p>
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowAddHomeowner(true)}
                className="bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 text-sm whitespace-nowrap"
              >
                ➕ Add New
              </button>
              <button
                onClick={handleExportHomeownersCSV}
                className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm whitespace-nowrap"
              >
                📥 Export
              </button>
              <label className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 cursor-pointer text-sm whitespace-nowrap" title="Import CSV - Write out full addresses with no commas">
                📤 Import
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportHomeownersCSV}
                  className="hidden"
                />
              </label>
              <button
                onClick={deletePantomUsers}
                className="bg-red-600 text-white px-2 py-2 rounded-md hover:bg-red-700 text-sm font-bold whitespace-nowrap"
                title="PERMANENTLY DELETE phantom users John Homeowner and Jane Resident from database"
              >
                🗑️ Phantoms
              </button>
              <button
                onClick={handleDataCleanup}
                className="bg-orange-600 text-white px-2 py-2 rounded-md hover:bg-orange-700 text-sm whitespace-nowrap"
                title="Fix data integrity issues (email in phone field, missing property IDs)"
              >
                🔧 Fix Data
              </button>
              <button
                onClick={() => alert('📥 BULK OPERATIONS GUIDE\n\n💡 IMPORTANT TIPS:\n\n1. 📋 Always download the latest CSV before bulk adding\n   • This ensures you start with current approval statuses\n   • Prevents re-adding already approved homeowners\n\n2. 🔐 Password Setup for New Users:\n   • New users added via bulk will receive approval emails\n   • Board members can generate temporary passwords via admin panel\n   • Users contact board for initial login credentials\n   • This ensures security and proper access control\n\n3. 📧 Email Notifications:\n   • Approval emails are sent automatically\n   • Only NEW approvals trigger welcome emails\n   • Existing users won\'t receive duplicate emails\n\n4. 📅 Track Approval Dates:\n   • Check the "Joined" date in homeowner details\n   • Use this to identify recently approved users\n   • Send targeted communications to new users\n\n5. 🔄 Best Practice Workflow:\n   • Download current CSV\n   • Add new entries to downloaded file\n   • Upload updated CSV\n   • Review and approve new entries\n   • Board provides passwords to new users')}
                className="bg-blue-100 text-blue-700 px-2 py-2 rounded-md hover:bg-blue-200 text-sm whitespace-nowrap"
              >
                💡 Guide
              </button>
            </div>
          </div>
            
          {/* CSV Import Instructions */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              📝 <strong>CSV Import Instructions:</strong> Write out full addresses with no commas (e.g., 123 Main St Apt 4 City State 12345). 
              Use commas only to separate CSV fields, not within addresses.
            </p>
          </div>

          {/* Homeowners Table - MOBILE FRIENDLY: Horizontal scroll + touch-friendly */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto overscroll-x-contain" style={{WebkitOverflowScrolling: 'touch'}}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Homeowner</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Addresses</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {homeowners.map((homeowner) => (
                    <tr key={homeowner.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{`${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim()}</div>
                        </div>
                      </td>
                      {/* Addresses column - shows both property and mailing */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div>🏠 {homeowner.property_address || homeowner.address || '-'}</div>
                          {homeowner.mailing_address && (
                            <div className="text-xs text-blue-600 mt-1">📬 Mail: {homeowner.mailing_address}</div>
                          )}
                        </div>
                      </td>
                      {/* Property Identifier - Editable */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingPropertyId === homeowner.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={propertyIdValues[homeowner.id] ?? homeowner.property_identifier ?? ''}
                              onChange={(e) => setPropertyIdValues({...propertyIdValues, [homeowner.id]: e.target.value})}
                              className="border border-gray-300 rounded px-2 py-1 text-sm w-32"
                              placeholder="Enter ID"
                              autoFocus
                            />
                            <button
                              onClick={async () => {
                                try {
                                  const currentHoaId = activeHoaId || user?.hoa_id;
                                  const newValue = propertyIdValues[homeowner.id] ?? homeowner.property_identifier ?? '';
                                  await api.put(`/api/hoa/${currentHoaId}/homeowners/${homeowner.id}/property-identifier`, {
                                    property_identifier: newValue
                                  });
                                  setEditingPropertyId(null);
                                  await fetchHomeowners();
                                } catch (error) {
                                  alert('Failed to update property identifier: ' + (error.response?.data?.detail || error.message));
                                }
                              }}
                              className="text-green-600 hover:text-green-900 text-xl"
                              title="Save"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => {
                                setEditingPropertyId(null);
                                setPropertyIdValues({...propertyIdValues, [homeowner.id]: homeowner.property_identifier});
                              }}
                              className="text-red-600 hover:text-red-900 text-xl"
                              title="Cancel"
                            >
                              ✗
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900">{homeowner.property_identifier || '-'}</span>
                            <button
                              onClick={() => {
                                setEditingPropertyId(homeowner.id);
                                setPropertyIdValues({...propertyIdValues, [homeowner.id]: homeowner.property_identifier || ''});
                              }}
                              className="text-indigo-600 hover:text-indigo-900 text-sm"
                              title="Edit Property ID"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-gray-900">{homeowner.email}</div>
                          {(() => {
                            const emailUpdated = homeowner.email_updated_at;
                            const mailingUpdated = homeowner.mailing_address_updated_at;
                            const now = new Date();
                            const fiveDaysAgo = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000);
                            
                            const showEmailBadge = emailUpdated && new Date(emailUpdated) > fiveDaysAgo;
                            const showMailingBadge = mailingUpdated && new Date(mailingUpdated) > fiveDaysAgo;
                            
                            if (showEmailBadge || showMailingBadge) {
                              const badges = [];
                              if (showEmailBadge) {
                                badges.push(`Email changed on ${new Date(emailUpdated).toLocaleDateString()}\nPrevious: ${homeowner.email_updated_from || 'N/A'}`);
                              }
                              if (showMailingBadge) {
                                badges.push(`Mailing address changed on ${new Date(mailingUpdated).toLocaleDateString()}\nPrevious: ${homeowner.mailing_address_updated_from || 'N/A'}`);
                              }
                              
                              return (
                                <span 
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                  title={badges.join('\n\n')}
                                >
                                  {showEmailBadge && '📧'} {showMailingBadge && '📬'} Updated
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="text-sm text-gray-500">Joined: {homeowner.created_at ? new Date(homeowner.created_at).toLocaleDateString() : (homeowner.joinDate ? new Date(homeowner.joinDate).toLocaleDateString() : '-') }</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          homeowner.status === 'active' ? 'bg-green-100 text-green-800' :
                          homeowner.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {(homeowner.status || '').toString().replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {(typeof homeowner.balance === 'number' ? homeowner.balance : 0) > 0 ? (
                          <span className="text-red-600 font-medium">${(typeof homeowner.balance === 'number' ? homeowner.balance : 0).toFixed(2)}</span>
                        ) : (
                          <span className="text-green-600">Paid</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          {homeowner.status === 'pending_approval' ? (
                            <button
                              onClick={() => approveHomeowner(homeowner.id)}
                              className="text-green-600 hover:text-green-900 whitespace-nowrap"
                            >
                              ✅ Approve
                            </button>
                          ) : (homeowner.status === 'suspended' || homeowner.status === 'inactive') ? (
                            <button
                              onClick={() => reactivateHomeowner(homeowner.id)}
                              className="text-green-600 hover:text-green-900 whitespace-nowrap"
                            >
                              🔄 Reactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => suspendHomeowner(homeowner.id)}
                              className="text-red-600 hover:text-red-900 whitespace-nowrap"
                            >
                              ⚠️ Suspend
                            </button>
                          )}
                          
                          {/* Remove Button - Available for all statuses */}
                          <button
                            onClick={() => removeHomeowner(homeowner.id)}
                            className="text-red-700 hover:text-red-900 whitespace-nowrap"
                            title="Remove homeowner permanently"
                          >
                            🗑️ Remove
                          </button>
                          
                          {/* Upgrade to Board Button */}
                          <button
                            onClick={() => {
                              setUpgradeUser(homeowner);
                              setUpgradeRole('board_member');
                              setShowUpgradeModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 whitespace-nowrap"
                            title="Upgrade this homeowner to a board position"
                          >
                            ⬆️ Upgrade
                          </button>
                          
                          {/* Resend Welcome Email Button */}
                          <button
                            onClick={async () => {
                              if (!confirm(`Resend welcome email to ${homeowner.email}?\n\nThis will send a new welcome email with login credentials.`)) {
                                return;
                              }
                              try {
                                const result = await api.post(`/api/board/user/${homeowner.id}/resend-welcome-email`);
                                if (result.data.success) {
                                  alert(`✅ Welcome email sent successfully to ${homeowner.email}!`);
                                } else {
                                  alert(`⚠️ Email sent but may have failed:\n${result.data.message}\n\nYou can copy the manual template from the Welcome Email Tracker if needed.`);
                                }
                              } catch (error) {
                                alert(`❌ Failed to send email: ${error.response?.data?.detail || error.message}\n\nTry using the Welcome Email Tracker to copy the template manually.`);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900 whitespace-nowrap"
                            title="Resend welcome email with login credentials"
                          >
                            📧 Resend
                          </button>
                        </div>
                        
                        <button 
                          onClick={() => {
                            const balanceVal = typeof homeowner.balance === 'number' ? homeowner.balance : 0;
                            const statusText = (homeowner.verification_status || homeowner.status || 'approved').toString().replace('_',' ');
                            const joinedText = homeowner.created_at ? new Date(homeowner.created_at).toLocaleDateString() : (homeowner.joinDate ? new Date(homeowner.joinDate).toLocaleDateString() : '-');
                            const fullName = `${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim();
                            const timezone = hoaSettings?.timezone || 'America/Chicago';  // FIX: Use hoaSettings instead of hoaData
                            
                            // Format consent history
                            let consentStatus = '';
                            const consent = homeowner.electronic_consent;
                            const isSuspended = homeowner.is_suspended;
                            const suspensionReason = homeowner.suspension_reason;
                            
                            if (isSuspended) {
                              consentStatus = `🚫 ACCOUNT SUSPENDED\nReason: ${suspensionReason || 'No reason provided'}\n\n`;
                            }
                            
                            if (consent?.consented) {
                              const { dateStr, timeStr } = formatDateInTimezone(consent.date_signed, timezone);
                              consentStatus += `✅ Current Status: Consented\n📅 Date: ${dateStr} at ${timeStr}\n📋 Version: ${consent.consent_version || 'N/A'}\n⏰ Timezone: ${timezone}`;
                            } else {
                              consentStatus += `❌ Current Status: No active consent`;
                            }
                            
                            // Add history if available
                            if (consent?.history && consent.history.length > 0) {
                              consentStatus += '\n\n📜 CONSENT HISTORY:\n';
                              consent.history.forEach((entry, idx) => {
                                const { dateStr, timeStr } = formatDateInTimezone(entry.timestamp, timezone);
                                const action = entry.action.toUpperCase();
                                consentStatus += `${idx + 1}. ${action} on ${dateStr} at ${timeStr}`;
                                if (entry.reason) consentStatus += ` (${entry.reason})`;
                                consentStatus += '\n';
                              });
                            }
                            
                            alert(`📋 HOMEOWNER DETAILS\n\n👤 Name: ${fullName}\n📧 Email: ${homeowner.email || ''}\n🏠 Property Address: ${homeowner.property_address || homeowner.address || '-'}\n📬 Mailing Address: ${homeowner.mailing_address || 'Same as property address'}\n📛 Identifier: ${homeowner.property_identifier || '-'}\n📅 Joined: ${joinedText}\n💰 Balance: $${balanceVal.toFixed(2)}\n📊 Status: ${statusText.toUpperCase()}\n\n📧 Electronic Communications Consent:\n${consentStatus}\n\n📞 Contact Options:\n• Send Email\n• Send Payment Reminder`);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          View Details
                        </button>
                        <button 
                          onClick={() => {
                            const fullName = `${homeowner.first_name || homeowner.name || ''} ${homeowner.last_name || ''}`.trim();
                            if (window.confirm(`🔐 RESET USER PASSWORD\n\n👤 User: ${fullName}\n📧 Email: ${homeowner.email || ''}\n\n⚠️ WARNING:\nThis will reset the user's password and should only be used if:\n• They asked for a password reset\n• This is their first time login\n• They are locked out of their account\n\n🔒 After reset:\n• User will need to contact you for the new temporary password\n• They should change it on first login\n\nAre you sure you want to proceed?`)) {
                              (async () => {
                                try {
                                  // Use board member endpoint (works for board members and platform admins)
                                  const { data: resp } = await api.post('/api/users/reset-password-by-email', { email: homeowner.email });
                                  if (resp?.success) {
                                    try { await fetchHomeowners(); } catch(_) {}
                                    try { await fetchPendingApprovals(); } catch(_) {}
                                    alert(`🔐 Password Reset\n\n✅ Temporary password generated for ${fullName}\n📧 Email: ${homeowner.email}\n\n🔒 Temporary Password: ${resp.temp_password}\n\n📋 Next Steps:\n• Share this password with the user securely\n• Instruct them to log in and change it immediately\n• They will be prompted to change password on first login`);
                                    // Optionally refresh homeowner list if available
                                    try { await fetchHomeowners(); } catch(_) {}
                                    try { await fetchPendingApprovals(); } catch(_) {}
                                  } else {
                                    alert('❌ Failed to reset password');
                                  }
                                } catch (err) {
                                  console.error('Failed to reset password:', err);
                                  alert('❌ Failed to reset password: ' + (err.response?.data?.detail || err.message));
                                }
                              })();
                            }
                          }}
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          🔐 Reset Password
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeUserTab === 'pending_approvals' && (
        <div className="space-y-6">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-2">Pending Homeowner Approvals</h4>
            <p className="text-sm text-gray-500">Review and approve new homeowner registrations</p>
          </div>

          {pendingApprovals.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <div className="text-gray-400 text-4xl mb-4">✅</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Approvals</h3>
              <p className="text-gray-500">All homeowner registrations have been processed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map((user) => (
                <div key={user.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h5 className="text-lg font-medium text-gray-900 mb-2">{user.first_name} {user.last_name}</h5>
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                        <div><strong>Email:</strong> {user.email}</div>
                        <div><strong>Address:</strong> {user.property_address}</div>
                        {hoaSettings?.property_identifier?.enabled ? (
                          <div><strong>{hoaSettings.property_identifier.label}:</strong> {user.property_identifier || '-'}</div>
                        ) : null}
                        <div><strong>Applied:</strong> {new Date(user.created_at).toLocaleDateString()}</div>
                        <div><strong>Status:</strong> Pending Review</div>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => approveHomeowner(user.id)}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                      >
                        ✅ Approve
                      </button>
                      <button
                        onClick={() => {
                          const confirmed = confirm(`Reject ${user.first_name} ${user.last_name}'s application?`);
                          if (confirmed) {
                            rejectUser(user.id, 'Rejected by board');
                            alert(`❌ Application rejected for ${user.first_name} ${user.last_name}\n\n📧 Rejection notice sent\n💾 Application archived`);
                          }
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                      >
                        ❌ Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ADD NEW HOMEOWNER MODAL */}
      {showAddHomeowner && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Homeowner</h3>
              <button
                onClick={() => setShowAddHomeowner(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Full Name *"
                value={newHomeowner.name}
                onChange={(e) => setNewHomeowner(prev => ({...prev, name: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="email"
                placeholder="Email Address *"
                value={newHomeowner.email}
                onChange={(e) => setNewHomeowner(prev => ({...prev, email: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="text"
                placeholder="Property Address *"
                value={newHomeowner.address}
                onChange={(e) => setNewHomeowner(prev => ({...prev, address: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="text"
                placeholder="Mailing Address (if different from property address)"
                value={newHomeowner.mailing_address || ''}
                onChange={(e) => setNewHomeowner(prev => ({...prev, mailing_address: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 -mt-2">
                Optional: For mailing statements/correspondence to a different address (PO Box, etc.)
              </p>
              <input
                type="text"
                placeholder="Property Identifier (LOT001, Unit 5A, Building B-12)"
                value={newHomeowner.property_identifier}
                onChange={(e) => setNewHomeowner(prev => ({...prev, property_identifier: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 -mt-2">
                Optional: Unit number, lot number, or building identifier for voting/identification purposes
              </p>
              <input
                type="tel"
                placeholder="Phone Number"
                value={newHomeowner.phone}
                onChange={(e) => setNewHomeowner(prev => ({...prev, phone: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <select
                value={newHomeowner.propertyType}
                onChange={(e) => setNewHomeowner(prev => ({...prev, propertyType: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="single_family">Single Family Home</option>
                <option value="condo">Condominium</option>
                <option value="townhouse">Townhouse</option>
                <option value="apartment">Apartment</option>
              </select>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowAddHomeowner(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={addNewHomeowner}
                  className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  ➕ Add Homeowner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UPGRADE TO BOARD MEMBER MODAL */}
      {showUpgradeModal && upgradeUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">⬆️ Upgrade to Board Member</h3>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  setUpgradeUser(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-800">
                  <strong>Upgrading:</strong> {`${upgradeUser.first_name || ''} ${upgradeUser.last_name || ''}`.trim() || upgradeUser.email}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Email: {upgradeUser.email}
                </p>
                <p className="text-xs text-purple-600">
                  Property: {upgradeUser.property_address || upgradeUser.address || 'N/A'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Board Position
                </label>
                <select
                  value={upgradeRole}
                  onChange={(e) => setUpgradeRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="board_member">Board Member</option>
                  <option value="president">President</option>
                  <option value="vice_president">Vice President</option>
                  <option value="treasurer">Treasurer</option>
                  <option value="secretary">Secretary</option>
                </select>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  ⚠️ <strong>Note:</strong> This will give the user board member access and permissions. 
                  They will be able to manage HOA settings, approve homeowners, and access board-only features.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    setUpgradeUser(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const currentHoaId = activeHoaId || user?.hoa_id;
                      const response = await api.post(
                        `/api/vote/board/${currentHoaId}/upgrade-to-board/${upgradeUser.id}`,
                        { role: upgradeRole }
                      );
                      
                      if (response.data?.success) {
                        alert(`✅ ${response.data.message}`);
                        setShowUpgradeModal(false);
                        setUpgradeUser(null);
                        // Refresh both homeowners and board members lists
                        await fetchHomeowners();
                        const bmRes = await api.get('/api/vote/board-members');
                        setBoardMembers(bmRes.data?.board_members || []);
                      }
                    } catch (error) {
                      console.error('Error upgrading user:', error);
                      alert(`❌ Failed to upgrade: ${error.response?.data?.detail || error.message}`);
                    }
                  }}
                  className="px-4 py-2 text-sm text-white bg-purple-600 rounded-md hover:bg-purple-700"
                >
                  ⬆️ Upgrade to {upgradeRole.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archived Homeowners Tab */}
      {activeUserTab === 'archived_homeowners' && (
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-amber-800">Archived Homeowner Records</h4>
                <p className="text-sm text-amber-700 mt-1">
                  This section contains records of homeowners who have been permanently removed from the community. 
                  These records are kept for audit purposes and contain the reason for removal.
                </p>
              </div>
            </div>
          </div>

          {archivedHomeowners.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Archived Records</h3>
              <p className="mt-1 text-sm text-gray-500">No homeowners have been permanently removed yet.</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-900">
                  Archived Homeowner Records ({archivedHomeowners.length})
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Homeowner Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Property
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Removal Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Info
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {archivedHomeowners.map((archived) => {
                      const fullName = `${archived.first_name || archived.name || ''} ${archived.last_name || ''}`.trim();
                      const removalDate = archived.removed_at ? new Date(archived.removed_at).toLocaleDateString() : 'Unknown';
                      
                      return (
                        <tr key={archived.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                                  <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{fullName}</div>
                                <div className="text-sm text-gray-500">{archived.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>{archived.property_address || archived.address || 'N/A'}</div>
                            <div className="text-xs text-gray-500">
                              {archived.property_identifier ? `ID: ${archived.property_identifier}` : ''}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium text-red-600">Removed: {removalDate}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                <strong>Reason:</strong> {archived.removal_reason || 'No reason provided'}
                              </div>
                              {archived.removal_notes && (
                                <div className="text-xs text-gray-500 mt-1">
                                  <strong>Notes:</strong> {archived.removal_notes}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 mt-1">
                                <strong>By:</strong> {archived.removed_by || 'System'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => {
                                const contactInfo = `📋 ARCHIVED HOMEOWNER CONTACT\n\n👤 Name: ${fullName}\n📧 Email: ${archived.email}\n🏠 Address: ${archived.property_address || archived.address || 'N/A'}\n📛 ID: ${archived.property_identifier || 'N/A'}\n\n🗑️ REMOVAL DETAILS:\n📅 Removed: ${removalDate}\n📝 Reason: ${archived.removal_reason || 'No reason provided'}\n📋 Notes: ${archived.removal_notes || 'None'}\n👤 Removed By: ${archived.removed_by || 'System'}\n\n💼 This is an archived record for audit purposes only.`;
                                alert(contactInfo);
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              📋 View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Property Management Tab */}
      {activeUserTab === 'property_management' && (
        <div className="space-y-6">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-2">Property Management & Occupancy Tracking</h4>
            <p className="text-sm text-gray-500 mb-4">
              Track property addresses and occupants. Each property is limited to 2 registered users. 
              Additional occupants must request approval from HOA Manager Pro.
            </p>
          </div>

          {/* Property List */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Occupant 1
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Occupant 2
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getPropertiesWithOccupants().map((property, index) => {
                    const totalOccupants = property.occupants.length + property.pending_occupants.length;
                    const isOverLimit = totalOccupants > 2;
                    const activeOccupants = property.occupants.slice(0, 2);
                    const pendingOccupants = property.pending_occupants;
                    
                    return (
                      <tr key={index} className={isOverLimit ? 'bg-red-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {property.address}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {property.property_identifier}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {activeOccupants[0] ? (
                            <div>
                              <div className="font-medium">{activeOccupants[0].name}</div>
                              <div className="text-xs text-gray-500">{activeOccupants[0].email}</div>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                activeOccupants[0].status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {activeOccupants[0].status}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Available</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {activeOccupants[1] ? (
                            <div>
                              <div className="font-medium">{activeOccupants[1].name}</div>
                              <div className="text-xs text-gray-500">{activeOccupants[1].email}</div>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                activeOccupants[1].status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {activeOccupants[1].status}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Available</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isOverLimit ? (
                            <div>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                ⚠️ Over Limit ({totalOccupants}/2)
                              </span>
                              {pendingOccupants.length > 0 && (
                                <div className="mt-1 text-xs text-red-600">
                                  {pendingOccupants.length} pending request{pendingOccupants.length > 1 ? 's' : ''}
                                </div>
                              )}
                            </div>
                          ) : totalOccupants === 2 ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Full (2/2)
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Available ({totalOccupants}/2)
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col space-y-1">
                            {isOverLimit && (
                              <>
                                <button
                                  onClick={() => {
                                    if (pendingOccupants.length > 0) {
                                      const rejectedNames = pendingOccupants.map(p => p.name).join(', ');
                                      if (window.confirm(`Reject ${pendingOccupants.length} pending occupant(s)?\n\n${rejectedNames}\n\nReason: Property is limited to 2 occupants. They can request additional space from HOA Manager Pro.`)) {
                                        pendingOccupants.forEach(pending => {
                                          rejectUser(pending.id, 'Property is limited to 2 occupants. Please contact HOA Manager Pro to request additional occupancy space for this address.');
                                        });
                                      }
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-900 text-xs"
                                >
                                  ❌ Reject Excess
                                </button>
                                <button
                                  onClick={() => {
                                    alert(`💡 Request Additional Space\n\nTo allow more than 2 occupants at this property:\n\n1. Contact HOA Manager Pro support\n2. Request occupancy limit increase\n3. Provide justification (family size, etc.)\n4. Pay additional fees if applicable\n\n📧 Contact: support@hoamanagerpro.us\n📞 Phone: 1-800-HOA-HELP`);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 text-xs"
                                >
                                  📞 Request More Space
                                </button>
                              </>
                            )}
                            {!isOverLimit && totalOccupants < 2 && (
                              <span className="text-green-600 text-xs block mb-1">✅ Space Available</span>
                            )}
                            {/* Property Management Actions - Always visible */}
                            <button
                              onClick={() => {
                                setSelectedProperty({
                                  address: property.address,
                                  identifier: property.property_identifier
                                });
                                setShowPropertySaleModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 text-xs"
                            >
                              🏷️ Sell & Transfer
                            </button>
                            <button
                              onClick={() => {
                                setSelectedProperty({
                                  address: property.address,
                                  identifier: property.property_identifier
                                });
                                setShowOwnershipHistory(true);
                              }}
                              className="text-gray-600 hover:text-gray-900 text-xs"
                            >
                              📜 Ownership History
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {getPropertiesWithOccupants().length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">🏠</div>
                <p>No properties found</p>
                <p className="text-sm mt-1">Properties will appear here once homeowners register</p>
              </div>
            )}
          </div>

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500">Total Properties</div>
              <div className="text-2xl font-bold text-gray-900">{getPropertiesWithOccupants().length}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500">Available Spaces</div>
              <div className="text-2xl font-bold text-green-600">
                {getPropertiesWithOccupants().reduce((acc, prop) => acc + Math.max(0, 2 - prop.occupants.length), 0)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500">Over Limit</div>
              <div className="text-2xl font-bold text-red-600">
                {getPropertiesWithOccupants().filter(prop => (prop.occupants.length + prop.pending_occupants.length) > 2).length}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500">Pending Requests</div>
              <div className="text-2xl font-bold text-yellow-600">
                {getPropertiesWithOccupants().reduce((acc, prop) => acc + prop.pending_occupants.length, 0)}
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-2">📋 Property Management Rules</h5>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Each property is limited to <strong>2 registered users</strong> by default</li>
              <li>• Additional occupants will be automatically rejected with instructions</li>
              <li>• Property owners can request additional space from HOA Manager Pro</li>
              <li>• Board members can manually approve exceptions on case-by-case basis</li>
              <li>• Pending requests for over-limit properties should be rejected</li>
              <li>• Use "Sell & Transfer" button to mark properties as sold and transfer ownership</li>
            </ul>
          </div>

          {/* Property Sale Modal */}
          {showPropertySaleModal && selectedProperty && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Sell & Transfer Property
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Property: {selectedProperty.address} ({selectedProperty.identifier})
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sale Date *
                    </label>
                    <input
                      type="date"
                      value={saleForm.sale_date}
                      onChange={(e) => setSaleForm({...saleForm, sale_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Owner Name *
                    </label>
                    <input
                      type="text"
                      value={saleForm.new_owner_name}
                      onChange={(e) => setSaleForm({...saleForm, new_owner_name: e.target.value})}
                      placeholder="John Doe"
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Owner Email *
                    </label>
                    <input
                      type="email"
                      value={saleForm.new_owner_email}
                      onChange={(e) => setSaleForm({...saleForm, new_owner_email: e.target.value})}
                      placeholder="john@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Owner Phone
                    </label>
                    <input
                      type="tel"
                      value={saleForm.new_owner_phone}
                      onChange={(e) => setSaleForm({...saleForm, new_owner_phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowPropertySaleModal(false);
                      setSaleForm({
                        sale_date: new Date().toISOString().split('T')[0],
                        new_owner_email: '',
                        new_owner_name: '',
                        new_owner_phone: ''
                      });
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!saleForm.new_owner_email || !saleForm.new_owner_name) {
                        alert('Please fill in required fields');
                        return;
                      }

                      try {
                        // Step 1: Find or create property record
                        let propertyId;
                        
                        // Try to find existing property by address
                        try {
                          const propertiesResponse = await api.get('/api/properties', {
                            params: { hoa_id: activeHoaId }
                          });
                          const existingProperty = propertiesResponse.data.find(
                            p => p.address?.toLowerCase() === selectedProperty.address?.toLowerCase()
                          );
                          
                          if (existingProperty) {
                            propertyId = existingProperty.id;
                          } else {
                            // Create property record if it doesn't exist
                            const createPropertyResponse = await api.post('/api/properties', {
                              hoa_id: activeHoaId,
                              address: selectedProperty.address,
                              property_identifier: selectedProperty.identifier,
                              status: 'active'
                            });
                            propertyId = createPropertyResponse.data.id;
                          }
                        } catch (propError) {
                          console.error('Error finding/creating property:', propError);
                          throw new Error('Failed to find or create property record');
                        }

                        // Step 2: Mark property as sold and transfer ownership
                        const response = await api.post(`/api/properties/${propertyId}/mark-sold`, {
                          sale_date: saleForm.sale_date,
                          new_owner_email: saleForm.new_owner_email,
                          new_owner_name: saleForm.new_owner_name,
                          new_owner_phone: saleForm.new_owner_phone || null
                        });

                        alert(`✅ Property sale completed successfully!\n\n` +
                              `🏠 Property: ${selectedProperty.address}\n` +
                              `👤 New Owner: ${saleForm.new_owner_name}\n` +
                              `📧 Email: ${saleForm.new_owner_email}\n` +
                              `📅 Sale Date: ${saleForm.sale_date}\n\n` +
                              `The new owner has been invited to join the HOA.`);
                        
                        // Reset form and close modal
                        setShowPropertySaleModal(false);
                        setSaleForm({
                          sale_date: new Date().toISOString().split('T')[0],
                          new_owner_email: '',
                          new_owner_name: '',
                          new_owner_phone: ''
                        });
                        
                        // Refresh homeowners list
                        await fetchHomeowners();
                        await fetchPendingApprovals();
                        
                      } catch (error) {
                        console.error('Failed to mark property as sold:', error);
                        const errorMsg = error.response?.data?.detail || error.message || 'Failed to process sale';
                        alert(`❌ Error: ${errorMsg}`);
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Confirm Sale
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Ownership History Modal */}
          {showOwnershipHistory && selectedProperty && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80dvh] overflow-auto">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Ownership History
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Property: {selectedProperty.address} ({selectedProperty.identifier})
                </p>

                <div className="space-y-3">
                  <div className="text-center py-8 text-gray-500">
                    Ownership history will be available once property records are created
                  </div>
                </div>

                <button
                  onClick={() => setShowOwnershipHistory(false)}
                  className="w-full mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Voting Settings Tab */}
      {activeUserTab === 'voting' && (
        <VotingSettingsSection />
      )}

      {/* Document Permissions have been moved to Documents Tab → Settings */}
    </div>
  );
};

// Community Services Settings Component
const CommunityServicesSettings = ({ homeowners = [] }) => {
  const { user } = useAuth();
  const { activeHoaId } = useMultiHOA();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHomeowners, setLoadingHomeowners] = useState(true);
  const [availableHomeowners, setAvailableHomeowners] = useState([]);
  const [availableTeamMembers, setAvailableTeamMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hoaSettings, setHoaSettings] = useState({
    hoa_name: '',
    mailing_address: '',
    hoa_email: '',
    phone: '',
    website: '',
    website_type: 'external', // 'public' or 'external'
    timezone: 'America/Chicago' // Default timezone
  });
  const [savingHoaSettings, setSavingHoaSettings] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    category: 'utility',
    website_url: '',
    phone: '',
    description: '',
    is_hoa_negotiated: false
  });
  const [showAddService, setShowAddService] = useState(false);
  const [editingService, setEditingService] = useState(null);

  useEffect(() => {
    fetchCommunityServices();
    fetchHoaSettings();
    fetchHomeowners();
    fetchTeamMembers();
  }, [user?.hoa_id]);

  const fetchHoaSettings = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      // First try to get HOA profile data (which has the signup details)
      let profileData = null;
      try {
        const profileResponse = await api.get(`/api/hoa/${currentHoaId}/profile`);
        profileData = profileResponse.data;
        console.log('HOA profile data loaded:', profileData);
      } catch (profileError) {
        console.log('HOA profile not available, falling back to settings');
      }

      // Then get the settings - Use the correct endpoint
      const response = await api.get(`/api/hoa/${currentHoaId}/settings`);
      if (response.data?.success && response.data?.settings) {
        const settings = response.data.settings;
        
        // Only use profile data as fallback if settings fields are actually empty
        // If user has saved settings, those take precedence over profile data
        const hasSavedSettings = settings.hoa_name || settings.hoa_email || settings.mailing_address || 
                                 settings.phone || settings.website;
        
        if (profileData && !hasSavedSettings) {
          // No saved settings yet, use profile data as initial values
          const mergedSettings = {
            ...settings,
            hoa_name: profileData.name || '',
            hoa_email: profileData.official_email || '',
            mailing_address: profileData.mailing_address || '',
            phone: profileData.phone || '',
            website: profileData.website || ''
          };
          setHoaSettings(mergedSettings);
          console.log('HOA settings initialized with profile data:', mergedSettings);
        } else {
          // Use saved settings (they take precedence)
          setHoaSettings(settings);
          console.log('HOA settings loaded from saved data:', settings);
        }
      } else {
        // Initialize with default calendar settings if no settings exist
        const defaultSettings = {
          calendar: {
            board_only_create: true,
            allowed_individuals: []
          }
        };
        setHoaSettings(defaultSettings);
        console.log('Initialized default HOA settings');
      }
    } catch (error) {
      console.error('Failed to fetch HOA settings:', error);
    }
  };

  const saveHoaSettings = async () => {
    setSavingHoaSettings(true);
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      console.log('Saving HOA settings:', hoaSettings);
      const response = await api.put(`/api/hoa/${currentHoaId}/settings`, hoaSettings);
      console.log('Save response:', response.data);
      
      if (response.data?.success) {
        alert('HOA settings saved successfully!');
        // Wait a moment before refreshing to ensure database write completed
        await new Promise(resolve => setTimeout(resolve, 500));
        // Refresh the settings to confirm they were saved
        await fetchHoaSettings();
      } else {
        throw new Error('Save response indicated failure');
      }
    } catch (error) {
      console.error('Failed to save HOA settings:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to save HOA settings. Please try again.');
    } finally {
      setSavingHoaSettings(false);
    }
  };

  const fetchCommunityServices = async () => {
    try {
      const response = await api.get('/api/community/resources');
      setServices(response.data.resources?.community_services || []);
    } catch (error) {
      console.error('Failed to fetch community services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeowners = async () => {
    try {
      const response = await api.get('/api/board/homeowners');
      console.log('Fetched homeowners for dropdown:', response.data);
      setAvailableHomeowners(response.data.homeowners || []);
    } catch (error) {
      console.error('Failed to fetch homeowners:', error);
      setAvailableHomeowners([]);
    } finally {
      setLoadingHomeowners(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/api/team-members');
      console.log('Fetched team members for dropdown:', response.data);
      setAvailableTeamMembers(response.data.team_members || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      setAvailableTeamMembers([]);
    }
  };

  // Function to find user name by email or ID
  const findUserDisplayName = (identifier) => {
    // First try to find by email
    const homeowner = availableHomeowners.find(h => 
      h.email === identifier || h.id === identifier
    );
    if (homeowner) {
      return homeowner.name || `${homeowner.first_name} ${homeowner.last_name}` || homeowner.email;
    }

    // Then try team members
    const teamMember = availableTeamMembers.find(t => 
      t.email === identifier || t.id === identifier  
    );
    if (teamMember) {
      return teamMember.name || teamMember.email;
    }

    // If it looks like an email, use the part before @
    if (identifier && identifier.includes('@')) {
      return identifier.split('@')[0];
    }

    // Otherwise return the identifier itself
    return identifier || 'Unknown User';
  };

  // Removed duplicate searchUsers function

  const searchUsers = async (searchTerm) => {
    try {
      console.log('Search function called with term:', searchTerm);
      const results = [];
      
      // Search homeowners
      const matchingHomeowners = availableHomeowners.filter(h => 
        h.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 5).map(h => ({
        ...h,
        name: h.name || `${h.first_name} ${h.last_name}`,
        memberType: 'homeowner'
      }));
      
      console.log('Matching homeowners:', matchingHomeowners);
      
      // Search team members  
      const matchingTeamMembers = availableTeamMembers.filter(t =>
        t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.email?.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 5).map(t => ({
        ...t,
        memberType: 'team_member'
      }));
      
      console.log('Matching team members:', matchingTeamMembers);
      
      results.push(...matchingHomeowners, ...matchingTeamMembers);
      console.log('Total search results:', results);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    }
  };

  const addUserToPermissions = async (email, userObj = null) => {
    try {
      let targetUser = userObj;
      
      if (!targetUser) {
        // Find user by email in homeowners or team members
        const homeowner = availableHomeowners.find(h => h.email?.toLowerCase() === email.toLowerCase());
        const teamMember = availableTeamMembers.find(t => t.email?.toLowerCase() === email.toLowerCase());
        
        if (homeowner) {
          targetUser = {
            id: homeowner.id,
            name: homeowner.name || `${homeowner.first_name} ${homeowner.last_name}`,
            email: homeowner.email,
            role: homeowner.role || 'homeowner'
          };
        } else if (teamMember) {
          targetUser = {
            id: teamMember.id,
            name: teamMember.name,
            email: teamMember.email,
            role: teamMember.role
          };
        } else {
          // Manual entry - create user object
          targetUser = {
            id: email, // Use email as ID for manual entries
            name: email.split('@')[0], // Use email prefix as name
            email: email,
            role: 'homeowner'
          };
        }
      }
      
      if (targetUser) {
        const currentAllowed = hoaSettings?.calendar?.allowed_individuals || [];
        if (!currentAllowed.includes(targetUser.email) && !currentAllowed.includes(targetUser.id)) {
          // Save as simple array of emails/IDs as expected by backend
          const updatedSettings = {
            ...hoaSettings,
            calendar: {
              ...hoaSettings.calendar,
              allowed_individuals: [
                ...currentAllowed,
                targetUser.email  // Save email as string, not object
              ]
            }
          };
          
          // Update state
          setHoaSettings(updatedSettings);
          
          // Immediately save to backend to fix persistence issue
          try {
            const currentHoaId = activeHoaId || user?.hoa_id;
            if (!currentHoaId) return;
            
            console.log('Saving calendar settings with allowed individual:', targetUser);
            const response = await api.put(`/api/hoa/${currentHoaId}/settings`, updatedSettings);
            if (response.data.success) {
              console.log('✅ Settings saved successfully');
              alert(`✅ Added ${targetUser.name} (${targetUser.email}) to event creation permissions`);
              
              // Clear search
              setSearchTerm('');
              setSearchResults([]);
              
              // Refresh settings from backend to verify persistence
              await fetchHoaSettings();
            } else {
              throw new Error('Settings save failed');
            }
          } catch (error) {
            console.error('Error saving settings:', error);
            alert('❌ Failed to save permission settings. Please try again.');
            // Revert state change
            setHoaSettings(hoaSettings);
          }
        } else {
          alert('❌ This person already has event creation access');
        }
      }
    } catch (error) {
      console.error('Error adding user to permissions:', error);
      alert('❌ Failed to add user. Please try again.');
    }
  };

  const addService = async () => {
    try {
      const response = await api.post('/api/board/community/services', newService);
      if (response.data.success) {
        await fetchCommunityServices();
        resetServiceForm();
        setShowAddService(false);
        alert('Service added successfully!');
      }
    } catch (error) {
      console.error('Failed to add service:', error);
      alert('Failed to add service. Please try again.');
    }
  };

  const updateService = async () => {
    try {
      const response = await api.put(`/api/board/community/services/${editingService.id}`, newService);
      if (response.data.success) {
        await fetchCommunityServices();
        resetServiceForm();
        setEditingService(null);
        alert('Service updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update service:', error);
      alert('Failed to update service. Please try again.');
    }
  };

  const deleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const response = await api.delete(`/api/board/community/services/${serviceId}`);
        if (response.data.success) {
          await fetchCommunityServices();
          alert('Service deleted successfully!');
        }
      } catch (error) {
        console.error('Failed to delete service:', error);
        alert('Failed to delete service. Please try again.');
      }
    }
  };

  const resetServiceForm = () => {
    setNewService({
      name: '',
      category: 'utility',
      website_url: '',
      phone: '',
      description: '',
      is_hoa_negotiated: false
    });
    setShowAddService(false);
    setEditingService(null);
  };

  const startEdit = (service) => {
    setEditingService(service);
    setNewService({
      name: service.name,
      category: service.category,
      website_url: service.website_url || '',
      phone: service.phone || '',
      description: service.description || '',
      is_hoa_negotiated: service.is_hoa_negotiated || false
    });
    setShowAddService(true);
  };

  const getCategoryName = (category) => {
    const categories = {
      utility: 'Utilities',
      internet: 'Internet & Cable',
      trash: 'Trash & Recycling',
      emergency: 'Emergency Services',
      maintenance: 'Maintenance & Repair',
      other: 'Other Services'
    };
    return categories[category] || 'Other Services';
  };

  return (
    <div className="space-y-6">
      {/* HOA Information Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">HOA Information</h3>
            <p className="text-sm text-gray-600 mt-1">
              Basic HOA information displayed in Neighborhood tab and meeting minutes
            </p>
          </div>
          <button
            onClick={saveHoaSettings}
            disabled={savingHoaSettings}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {savingHoaSettings ? 'Saving...' : 'Save HOA Info'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HOA Name</label>
            <input
              type="text"
              value={hoaSettings.hoa_name}
              onChange={(e) => setHoaSettings(prev => ({ ...prev, hoa_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Sunvalley HOA"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HOA Email</label>
            <input
              type="email"
              value={hoaSettings.hoa_email}
              onChange={(e) => setHoaSettings(prev => ({ ...prev, hoa_email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., board@sunvalleyhoa.com"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Mailing Address</label>
            <textarea
              value={hoaSettings.mailing_address}
              onChange={(e) => setHoaSettings(prev => ({ ...prev, mailing_address: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., 123 Community Drive, Sunvalley, CA 90210"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={hoaSettings.phone}
              onChange={(e) => setHoaSettings(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., (555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
            
            {/* Radio buttons for website type */}
            <div className="flex gap-6 mb-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="website_type"
                  value="public"
                  checked={hoaSettings.website_type === 'public'}
                  onChange={(e) => setHoaSettings(prev => ({ ...prev, website_type: e.target.value }))}
                  className="mr-2"
                />
                <span className="text-sm">Use Public Page</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="website_type"
                  value="external"
                  checked={hoaSettings.website_type === 'external'}
                  onChange={(e) => setHoaSettings(prev => ({ ...prev, website_type: e.target.value }))}
                  className="mr-2"
                />
                <span className="text-sm">Use External Website</span>
              </label>
            </div>
            
            {/* Show input only for external website */}
            {hoaSettings.website_type === 'external' && (
              <input
                type="url"
                value={hoaSettings.website}
                onChange={(e) => setHoaSettings(prev => ({ ...prev, website: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g., https://sunvalleyhoa.com"
              />
            )}
            
            {hoaSettings.website_type === 'public' && (
              <p className="text-xs text-gray-500 mt-1">
                Will link to your HOA Manager Pro public page
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HOA Timezone</label>
            <select
              value={hoaSettings.timezone}
              onChange={(e) => setHoaSettings(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Anchorage">Alaska Time (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
              <option value="America/Phoenix">Arizona Time (MST - No DST)</option>
              <option value="America/Indiana/Indianapolis">Indiana Time (ET)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Used for amenity reservations and event scheduling. Members see HOA time regardless of their location.
            </p>
          </div>
        </div>
      </div>

      {/* Event Email Settings Section */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">📧 Event Email Settings</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure when board meeting notices are sent and who receives agenda reminders
        </p>
        
        <div className="space-y-6">
          {/* Board Meeting Notices */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              When should Board Meeting notices be sent to homeowners?
            </label>
            <select
              value={hoaSettings.board_meeting_notice_timing || 'agenda_posted'}
              onChange={(e) => setHoaSettings(prev => ({...prev, board_meeting_notice_timing: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="event_created">When Event is Created</option>
              <option value="agenda_posted">When Agenda is Posted (Recommended)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              If "When Agenda is Posted" is selected, homeowners will only be notified once the agenda is added to the meeting.
            </p>
          </div>

          {/* Agenda Reminders */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-yellow-900 mb-3">⏰ Agenda Reminder Settings</h4>
            
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hoaSettings.agenda_reminder_2weeks || false}
                  onChange={(e) => setHoaSettings(prev => ({...prev, agenda_reminder_2weeks: e.target.checked}))}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Send reminder 2 weeks before meeting</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hoaSettings.agenda_reminder_48hours || false}
                  onChange={(e) => setHoaSettings(prev => ({...prev, agenda_reminder_48hours: e.target.checked}))}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Send reminder 48 hours before meeting</span>
              </label>
            </div>
            
            <p className="text-xs text-yellow-800 mt-3">
              💡 Note: Meetings that involve rules discussions or budget matters typically require 14-day notice to homeowners.
            </p>
          </div>

          {/* Reminder Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who should receive agenda reminders?
            </label>
            <select
              value={hoaSettings.agenda_reminder_recipients || 'all_board'}
              onChange={(e) => setHoaSettings(prev => ({...prev, agenda_reminder_recipients: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all_board">All Board Members</option>
              <option value="president_only">President Only</option>
              <option value="president_secretary">President & Secretary</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              These board members will see reminders in the Overview tab Community Activity Feed.
            </p>
          </div>
        </div>
      </div>

      {/* Event Creation Permission Settings Section */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">📅 Event Creation Permissions</h3>
        <p className="text-sm text-gray-600 mb-6">
          Control who can create, edit, and manage community events
        </p>
        
        <div className="space-y-6">
          {/* Board Members + Specific Persons Access */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who can create community events?
            </label>
            <div className="space-y-3">
              <label className="flex items-start gap-3">
                <input
                  type="radio"
                  name="event_creation_mode"
                  value="board_only"
                  checked={(hoaSettings?.calendar?.board_only_create !== false)}
                  onChange={(e) => setHoaSettings(prev => ({
                    ...prev, 
                    calendar: { 
                      ...prev.calendar, 
                      board_only_create: true,
                      allowed_individuals: prev.calendar?.allowed_individuals || []
                    }
                  }))}
                  className="text-indigo-600 focus:ring-indigo-500 mt-1"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Board Members + Specific Persons</span>
                  <p className="text-xs text-gray-500 mt-1">Board members can always create events. You can also grant access to specific homeowners, team members, or committee members.</p>
                </div>
              </label>
              
              <label className="flex items-start gap-3">
                <input
                  type="radio"
                  name="event_creation_mode"
                  value="all_homeowners"
                  checked={hoaSettings?.calendar?.board_only_create === false}
                  onChange={(e) => setHoaSettings(prev => ({
                    ...prev, 
                    calendar: { 
                      ...prev.calendar, 
                      board_only_create: false,
                      allowed_individuals: prev.calendar?.allowed_individuals || []
                    }
                  }))}
                  className="text-indigo-600 focus:ring-indigo-500 mt-1"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">All HOA Members</span>
                  <p className="text-xs text-gray-500 mt-1">Any verified homeowner can create community events.</p>
                </div>
              </label>
            </div>
          </div>

          {/* Specific Persons List - Only show when Board + Specific is selected */}
          {(hoaSettings?.calendar?.board_only_create !== false) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">🎯 Grant Event Creation Access</h4>
              
              {/* Add Person Input - AsyncSelect Dropdown */}
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Search and Add Person:</label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={async (inputValue) => {
                      if (!inputValue || inputValue.length < 2) return [];
                      try {
                        const response = await api.get('/api/users/search', {
                          params: { q: inputValue, limit: 10 }
                        });
                        return response.data || [];
                      } catch (error) {
                        console.error('Error searching users:', error);
                        return [];
                      }
                    }}
                    onChange={async (option) => {
                      if (!option) return;
                      try {
                        const response = await api.post('/api/calendar/creators/grant', {
                          user_id: option.value
                        });
                        if (response.data.success) {
                          alert(`✅ ${option.label} can now create events`);
                          await fetchHoaSettings(); // Refresh settings
                        }
                      } catch (error) {
                        console.error('Error granting permission:', error);
                        alert('❌ Failed to grant permission: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    placeholder="Search HOA users by name or email…"
                    isClearable
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#9ca3af' }
                      })
                    }}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Search by name or email, then select to grant event creation permission.
                  </p>
                </div>
                
                <p className="text-xs text-gray-600">
                  💡 Perfect for social committee chairs, event coordinators, or active volunteers
                </p>
              </div>

              {/* Current Allowed Persons List */}
              {(hoaSettings?.calendar?.allowed_individuals || []).length > 0 && (
                <div className="mt-4">
                  <h5 className="text-xs font-medium text-blue-800 mb-2">Current Event Creators:</h5>
                  <div className="space-y-2">
                    {(hoaSettings.calendar.allowed_individuals || []).map((personData, index) => {
                      // Handle both string format (email/ID) and object format for backwards compatibility
                      const identifier = typeof personData === 'string' ? personData : (personData.email || personData.id);
                      const displayName = findUserDisplayName(identifier);
                      
                      return (
                        <div key={`${identifier}-${index}`} className="flex items-center justify-between bg-white border border-blue-200 rounded-lg p-3">
                          <div>
                            <span className="text-sm font-medium text-gray-900">{displayName}</span>
                            <span className="text-xs text-gray-500 ml-2">({identifier})</span>
                            <span className="text-xs text-gray-400 ml-1">- Homeowner</span>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const response = await api.post('/api/calendar/creators/revoke', {
                                  email: identifier
                                });
                                if (response.data.success) {
                                  alert(`✅ Removed ${displayName} from event creation permissions`);
                                  await fetchHoaSettings(); // Refresh settings
                                } else {
                                  throw new Error('Revoke failed');
                                }
                              } catch (error) {
                                console.error('Error revoking permission:', error);
                                alert('❌ Failed to remove permission: ' + (error.response?.data?.detail || error.message));
                              }
                            }}
                            className="text-red-600 hover:text-red-800 text-xs font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Permission Scope Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 mt-0.5">⚠️</span>
              <div className="text-xs text-yellow-800">
                <p className="font-medium">Scope: Community Events Only</p>
                <p className="mt-1">These permissions only apply to the Community Events/Calendar section. Access to other HOA features (voting, documents, payments, etc.) must be granted separately in their respective settings.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Community Services Section */}
      <div className="flex justify-between items-center mt-8">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Community Services</h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage essential services shown in the Neighborhood tab
          </p>
        </div>
        <button
          onClick={() => setShowAddService(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          Add Service
        </button>
      </div>

      {/* Add/Edit Service Form */}
      {showAddService && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingService ? 'Edit Service' : 'Add New Service'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
              <input
                type="text"
                value={newService.name}
                onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g., Water & Sewer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={newService.category}
                onChange={(e) => setNewService(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="utility">Utilities</option>
                <option value="internet">Internet & Cable</option>
                <option value="trash">Trash & Recycling</option>
                <option value="emergency">Emergency Services</option>
                <option value="maintenance">Maintenance & Repair</option>
                <option value="other">Other Services</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
              <input
                type="url"
                value={newService.website_url}
                onChange={(e) => setNewService(prev => ({ ...prev, website_url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="https://example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                value={newService.phone}
                onChange={(e) => setNewService(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newService.description}
                onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Brief description of the service"
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newService.is_hoa_negotiated}
                  onChange={(e) => setNewService(prev => ({ ...prev, is_hoa_negotiated: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">HOA has negotiated special rates</span>
              </label>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={editingService ? updateService : addService}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              {editingService ? 'Update Service' : 'Add Service'}
            </button>
            <button
              onClick={resetServiceForm}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Services List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <span className="text-4xl block mb-2">🏘️</span>
            <p>No community services configured</p>
            <p className="text-sm mt-1">Add services to help residents find essential contacts</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {services.map((service, index) => (
              <div key={index} className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{service.name}</h4>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getCategoryName(service.category)}
                      </span>
                      {service.is_hoa_negotiated && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          HOA Negotiated
                        </span>
                      )}
                    </div>
                    {service.description && (
                      <p className="text-gray-600 text-sm mb-2">{service.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      {service.website_url && (
                        <a href={service.website_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">
                          🌐 Website
                        </a>
                      )}
                      {service.phone && (
                        <a href={`tel:${service.phone}`} className="text-indigo-600 hover:text-indigo-800">
                          📞 {service.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => startEdit(service)}
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteService(service.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Community Branding Settings Component
const CommunityBrandingSettings = ({ brandingSettings, setBrandingSettings }) => {
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingBranding, setLoadingBranding] = useState(false);

  const colorOptions = [
    { name: 'Indigo', primary: '#4f46e5', secondary: '#6366f1' },
    { name: 'Blue', primary: '#2563eb', secondary: '#3b82f6' },
    { name: 'Green', primary: '#059669', secondary: '#10b981' },
    { name: 'Purple', primary: '#7c3aed', secondary: '#8b5cf6' },
    { name: 'Red', primary: '#dc2626', secondary: '#ef4444' },
    { name: 'Orange', primary: '#ea580c', secondary: '#f97316' },
    { name: 'Teal', primary: '#0d9488', secondary: '#14b8a6' },
    { name: 'Pink', primary: '#db2777', secondary: '#ec4899' }
  ];

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    setUploading(true);
    setLogoFile(file);

    // Convert to base64 for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setBrandingSettings(prev => ({
        ...prev,
        logo: e.target.result
      }));
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleColorChange = (colorOption) => {
    setBrandingSettings(prev => ({
      ...prev,
      primaryColor: colorOption.primary,
      secondaryColor: colorOption.secondary
    }));
  };

  const handleSaveBranding = async () => {
    try {
      setUploading(true);
      // Get HOA code from server (authoritative)
      const { data: my } = await api.get('/api/hoa/my');
      const hoa = my?.hoa;
      if (!hoa?.code) throw new Error('HOA not found');

      // Build multipart form data for server persistence
      const form = new FormData();
      if (brandingSettings.primaryColor) form.append('primary_color', brandingSettings.primaryColor);
      if (brandingSettings.secondaryColor) form.append('secondary_color', brandingSettings.secondaryColor);
      if (brandingSettings.tagline) form.append('tagline', brandingSettings.tagline);
      if (logoFile) form.append('logo', logoFile);

      await api.put(`/api/board/hoa/${hoa.code}/branding`, form, { headers: { 'Content-Type': 'multipart/form-data' } });

      // Re-fetch branding from server and apply globally
      const { data: branding } = await api.get(`/api/hoa/${hoa.code}/branding`);
      const primary = branding.primaryColor || '#4f46e5';
      const secondary = branding.secondaryColor || '#6366f1';

      // Update settings state to reflect server
      setBrandingSettings(prev => ({
        ...prev,
        primaryColor: primary,
        secondaryColor: secondary,
        logo: branding.logo_url || prev.logo
      }));

      // Persist theme HOA-scoped and set server logo URL for headers
      const hoaId = window.currentHoaId || (hoa?.id) || (my?.hoa?.id);
      window.__serverBrandingLogoUrl = branding.logo_url || null;
      const themeConfig = {
        primaryColor: primary,
        secondaryColor: secondary,
        logo: branding.logo_url || null,
        appliedAt: new Date().toISOString(),
        hoaId
      };
      if (hoaId) {
        localStorage.setItem(`hoa_theme_${hoaId}`, JSON.stringify(themeConfig));
      }

      // Apply minimal CTA-only theming
      let styleEl = document.getElementById('hoa-dynamic-theme');
      if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'hoa-dynamic-theme'; document.head.appendChild(styleEl); }
      styleEl.innerHTML = `
        [data-theme="primary"], .primary-cta { background-color: ${primary} !important; color: #fff !important; }
        [data-theme="primary"]:hover, .primary-cta:hover { background-color: ${secondary} !important; }
        .primary-cta *, [data-theme="primary"] * { color: #fff !important; }
        .dashboard-nav .active { background-color: ${primary} !important; color: #fff !important; }
      `;

      alert(`✅ Branding settings saved! May require page refresh for full site branding to take effect.\n\nThis changes branding for the entire HOA.`);
    } catch (error) {
      alert('❌ Failed to save branding settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Community Branding</h3>
        <p className="text-sm text-gray-600 mb-6">
          Customize your community's appearance with your logo, colors, and branding elements.
        </p>
      </div>

      {/* Logo Upload */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">🖼️ Community Logo</h4>
        <div className="flex items-start space-x-6">
          <div className="flex-shrink-0">
            <div className="w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg flex items-center justify-center overflow-hidden">
              {brandingSettings.logo ? (
                <img src={brandingSettings.logo} alt="Community Logo" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center">
                  <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs text-gray-500">No logo</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 300x100px, PNG or JPG, max 2MB</p>
            </div>
            {uploading && (
              <div className="flex items-center text-sm text-indigo-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                Uploading logo...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Color Palette */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">🎨 Color Palette</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {colorOptions.map((colorOption, index) => (
            <div
              key={index}
              onClick={() => handleColorChange(colorOption)}
              className={`cursor-pointer p-4 border-2 rounded-lg transition-all ${
                brandingSettings.primaryColor === colorOption.primary
                  ? ''
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              style={brandingSettings.primaryColor === colorOption.primary 
                ? { borderColor: colorOption.primary, boxShadow: `0 0 0 3px ${colorOption.secondary}55` } 
                : {}}
            >
              <div className="flex space-x-2 mb-2">
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: colorOption.primary }}
                ></div>
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: colorOption.secondary }}
                ></div>
              </div>
              <p className="text-sm font-medium text-gray-900">{colorOption.name}</p>
              <p className="text-xs text-gray-500">Primary & Secondary</p>
            </div>
          ))}
        </div>
        
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h5 className="text-sm font-medium text-gray-900 mb-2">Current Colors</h5>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div
                className="w-8 h-8 rounded-full border border-gray-300"
                style={{ backgroundColor: brandingSettings.primaryColor }}
              ></div>
              <div>
                <p className="text-xs font-medium text-gray-700">Primary</p>
                <p className="text-xs text-gray-500">{brandingSettings.primaryColor}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div
                className="w-8 h-8 rounded-full border border-gray-300"
                style={{ backgroundColor: brandingSettings.secondaryColor }}
              ></div>
              <div>
                <p className="text-xs font-medium text-gray-700">Secondary</p>
                <p className="text-xs text-gray-500">{brandingSettings.secondaryColor}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Community Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">📝 Community Information</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Community Name</label>
            <input
              type="text"
              value={brandingSettings.communityName}
              onChange={(e) => setBrandingSettings(prev => ({ ...prev, communityName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Your HOA Community Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Community Tagline (Optional)</label>
            <input
              type="text"
              value={brandingSettings.tagline}
              onChange={(e) => setBrandingSettings(prev => ({ ...prev, tagline: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="A great place to call home"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveBranding}
          className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition-colors"
        >
          💾 Save Branding Settings
        </button>
      </div>
    </div>
  );
};

// Document Management Settings Component - ENHANCED: Full integration with Documents tab
const DocumentManagementSettings = ({ documents, setDocuments, uploadingDocument, setUploadingDocument }) => {
  const [selectedCategory, setSelectedCategory] = useState('governing');
  const [documentReviewDays, setDocumentReviewDays] = useState(3);
  const [loadingReviewSetting, setLoadingReviewSetting] = useState(false);
  const [savingReviewSetting, setSavingReviewSetting] = useState(false);
  const { user } = useAuth();
  const safeUser = user || { role: 'board_member' };
  
  const documentCategories = [
    { id: 'governing', name: 'Governing Documents', description: 'CC&Rs, Bylaws, Articles of Incorporation' },
    { id: 'financial', name: 'Financial Documents', description: 'Budgets, Financial Statements, Reserve Studies' },
    { id: 'policies', name: 'Policies & Rules', description: 'Rules & Regulations, Architectural Guidelines' },
    { id: 'legal', name: 'Legal Documents', description: 'Contracts, Legal Notices, Insurance Policies' },
    { id: 'meeting', name: 'Meeting Minutes', description: 'Board Meeting Minutes, Annual Meeting Minutes' },
    { id: 'maintenance', name: 'Maintenance Records', description: 'Maintenance Contracts, Service Records' }
  ];

  // Get current HOA data
  const hoaData = JSON.parse(localStorage.getItem('user_data') || '{}');

  const handleDocumentUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingDocument(true);
    
    try {
      for (const file of files) {
        // Convert file to base64 for storage
        const fileData = await new Promise((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.onload = (e) => resolve(e.target.result);
          fileReader.onerror = (e) => reject(e);
          fileReader.readAsDataURL(file);
        });

        // Create enhanced document entry compatible with Documents tab
        const newDocument = {
          id: Date.now() + Math.random(),
          name: file.name,
          category: selectedCategory,
          size: file.size,
          uploadDate: new Date().toISOString(),
          type: file.type,
          viewPermission: 'all', // Default to All Homeowners as requested
          specificUsers: [], // Empty for default uploads
          uploadedBy: `${safeUser.first_name || 'Board'} ${safeUser.last_name || 'Member'}`,
          uploadedByUserId: safeUser.id,
          hoaId: hoaData?.hoa?.id || hoaData?.id || 'default_hoa', // Tie to specific HOA
          fileData: fileData,
          source: 'settings' // Track where it was uploaded from
        };
        
        // Add to local state
        setDocuments(prev => [...prev, newDocument]);
        
        // CRITICAL: Add to main Documents tab localStorage with correct format
        const existingDocs = JSON.parse(localStorage.getItem('hoa_documents') || '[]');
        existingDocs.push(newDocument);
        localStorage.setItem('hoa_documents', JSON.stringify(existingDocs));
      }
      
      setUploadingDocument(false);
      alert(`✅ ${files.length} document(s) uploaded successfully!\n\n📄 Documents are now visible to all homeowners by default.\n🎯 Go to Documents tab to change visibility settings if needed.`);
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('❌ Error uploading documents. Please try again.');
      setUploadingDocument(false);
    }
  };

  // Load documents from main storage
  const mainDocuments = JSON.parse(localStorage.getItem('hoa_documents') || '[]');
  const visibleDocuments = mainDocuments; // Board members see all documents

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">📄 Document Management</h3>
        <p className="text-sm text-gray-600 mb-6">
          Upload and organize important HOA documents. Documents uploaded here are <strong>visible to all homeowners by default</strong> and will appear in the main Documents tab.
        </p>
      </div>

      {/* Document Upload */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-blue-900 mb-4">📤 Upload Documents</h4>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 text-sm">
              <p className="text-blue-800 font-medium">Default Settings</p>
              <p className="text-blue-700 mt-1">
                • Documents are set to <strong>"All Homeowners"</strong> visibility by default<br />
                • They will immediately appear in the Documents tab<br />
                • You can change permissions later in the Documents tab
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-2">Document Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {documentCategories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            <p className="text-xs text-blue-600 mt-1">
              {documentCategories.find(cat => cat.id === selectedCategory)?.description}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-2">Choose Files</label>
            <input
              type="file"
              multiple
              accept="*"
              onChange={handleDocumentUpload}
              disabled={uploadingDocument}
              className="block w-full text-sm text-blue-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
            />
            <p className="text-xs text-blue-600 mt-1">Supports all file types. Multiple files can be selected for bulk upload.</p>
          </div>
          {uploadingDocument && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-700">Uploading documents...</span>
            </div>
          )}
        </div>
      </div>

      {/* Document Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documentCategories.map(category => (
          <div key={category.id} className="bg-white border border-gray-200 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-900 mb-2">{category.name}</h5>
            <p className="text-xs text-gray-600 mb-3">{category.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {mainDocuments.filter(doc => doc.category === category.id).length} documents
              </span>
              <button className="text-xs text-indigo-600 hover:text-indigo-800">View All</button>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Documents */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-medium text-gray-900">Recent Documents</h4>
          <p className="text-sm text-gray-600">Documents uploaded from Settings and Documents tab</p>
        </div>
        <div className="divide-y divide-gray-200">
          {visibleDocuments.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2">No documents available</p>
              <p className="text-sm text-gray-400">Upload your first document to get started.</p>
            </div>
          ) : (
            visibleDocuments.slice(0, 5).map(document => (
              <div key={document.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">{document.name}</p>
                    <p className="text-sm text-gray-500">
                      {documentCategories.find(cat => cat.id === document.category)?.name} • 
                      {Math.round(document.size / 1024)} KB • 
                      {new Date(document.uploadDate).toLocaleDateString()} •
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        document.viewPermission === 'board' ? 'bg-red-100 text-red-800' :
                        document.viewPermission === 'specific' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {document.viewPermission === 'board' ? 'Board Only' :
                         document.viewPermission === 'specific' ? 'Specific Users' :
                         'All Homeowners'}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button 
                    className="text-indigo-600 hover:text-indigo-800 text-sm"
                    onClick={() => {
                      if (document.fileData) {
                        if (document.type.startsWith('image/')) {
                          const newWindow = window.open();
                          newWindow.document.write(`<img src="${document.fileData}" style="max-width: 100%; height: auto;" alt="${document.name}">`);
                        } else if (document.type === 'application/pdf') {
                          const newWindow = window.open();
                          newWindow.location.href = document.fileData;
                        } else {
                          const link = window.document.createElement('a');
                          link.href = document.fileData;
                          link.download = document.name;
                          window.document.body.appendChild(link);
                          link.click();
                          window.document.body.removeChild(link);
                        }
                      } else {
                        alert('Document data not available for viewing');
                      }
                    }}
                  >
                    View
                  </button>
                  <button 
                    className="text-green-600 hover:text-green-800 text-sm"
                    onClick={() => {
                      if (document.fileData) {
                        const link = window.document.createElement('a');
                        link.href = document.fileData;
                        link.download = document.name;
                        window.document.body.appendChild(link);
                        link.click();
                        window.document.body.removeChild(link);
                      } else {
                        alert('Document data not available for download');
                      }
                    }}
                  >
                    Download
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Announcement Modal Component
const AnnouncementModal = ({ onClose, onSend }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [recipients, setRecipients] = useState('all');

  const handleSend = () => {
    if (!title.trim() || !message.trim()) {
      alert('Please fill in both title and message');
      return;
    }
    onSend({ title, message, priority, recipients });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">Send Community Announcement</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Announcement title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Message:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your announcement message..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Priority:</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="normal">Normal</option>
              <option value="high">High Priority</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Send Announcement
          </button>
        </div>
      </div>
    </div>
  );
};

// Emergency Modal Component
const EmergencyModal = ({ onClose, onSend }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('high');

  const handleSend = () => {
    if (!title.trim() || !message.trim()) {
      alert('Please fill in both title and message');
      return;
    }
    onSend({ title, message, severity, isEmergency: true });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md border-l-4 border-red-500">
        <h3 className="text-lg font-bold mb-4 text-red-700">🚨 Send Emergency Alert</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Emergency Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              placeholder="Emergency alert title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Emergency Message:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              placeholder="Describe the emergency situation..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Severity Level:</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="high">High - Immediate Attention</option>
              <option value="critical">Critical - Life Safety</option>
              <option value="severe">Severe - Evacuation Required</option>
            </select>
          </div>
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-xs text-red-700">
              ⚠️ Emergency alerts will be sent immediately via SMS and email to all residents and emergency contacts.
            </p>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Send Emergency Alert
          </button>
        </div>
      </div>
    </div>
  );
};

// Communication Settings Component with Real Functionality
const CommunicationSettings = () => {
  const { user } = useAuth();
  const { activeHoaId } = useMultiHOA();
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templates, setTemplates] = useState({});
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const types = ['welcome', 'meeting_notice', 'payment_reminder'];
      const loadedTemplates = {};
      
      for (const type of types) {
        try {
          const { data } = await api.get(`/api/emails/templates/${type}`);
          if (data.success) {
            loadedTemplates[type] = data.template;
          }
        } catch (error) {
          console.error(`Failed to load ${type} template:`, error);
          // Set default template if API fails
          loadedTemplates[type] = {
            subject: `Default ${type.replace('_', ' ')} subject`,
            content: `Default ${type.replace('_', ' ')} content`
          };
        }
      }
      
      setTemplates(loadedTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const openTemplateEditor = (templateType) => {
    setCurrentTemplate({
      type: templateType,
      subject: templates[templateType]?.subject || '',
      content: templates[templateType]?.content || ''
    });
    setShowTemplateEditor(true);
  };

  const saveTemplate = async () => {
    if (!currentTemplate) return;
    
    try {
      const { data } = await api.put(`/api/emails/templates/${currentTemplate.type}`, {
        subject: currentTemplate.subject,
        content: currentTemplate.content
      });
      
      if (data.success) {
        // Update local templates
        setTemplates(prev => ({
          ...prev,
          [currentTemplate.type]: {
            subject: currentTemplate.subject,
            content: currentTemplate.content
          }
        }));
        
        setShowTemplateEditor(false);
        alert(`✅ ${currentTemplate.type.replace('_', ' ')} template saved successfully!`);
      } else {
        alert('❌ Failed to save template');
      }
    } catch (error) {
      console.error('Template save error:', error);
      alert('❌ Error saving template: ' + (error.response?.data?.detail || error.message));
    }
  };

  const sendCommunityAnnouncement = async (announcementData) => {
    try {
      const token = localStorage.getItem('hoa_token');
      
      // Handle both object parameter (from modal) and separate title/content parameters
      let title, content;
      if (typeof announcementData === 'object' && announcementData.title) {
        title = announcementData.title;
        content = announcementData.message;
      } else {
        // Fallback for direct string content
        title = 'Community Announcement';
        content = announcementData;
      }
      
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.post(`/api/hoa/${currentHoaId}/send-announcement`, {
        title: title,
        message: content
      });
      
      if (data.success) {
        const { sent_count, total_members } = data;
        alert(`✅ Community announcement sent successfully!\n\n📧 Sent to: ${sent_count} out of ${total_members} community members\n📋 Title: ${title}`);
        setShowAnnouncementModal(false);
      } else {
        alert('❌ Failed to send announcement: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Announcement error:', error);
      alert('❌ Error sending announcement. Please try again.');
    }
  };

  const sendEmergencyNotification = async (emergencyData) => {
    try {
      const token = localStorage.getItem('hoa_token');
      
      // Handle both object parameter (from modal) and direct message parameter
      let message;
      if (typeof emergencyData === 'object' && emergencyData.message) {
        message = emergencyData.message;
      } else {
        message = emergencyData;
      }
      
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.post(`/api/hoa/${currentHoaId}/send-emergency-alert`, {
        message: message
      });
      
      if (data.success) {
        const { sent_count, total_members } = data;
        alert(`🚨 Emergency alert sent successfully!\n\n📧 Sent to: ${sent_count} out of ${total_members} community members\n⚠️ All residents have been notified of the emergency situation.`);
        setShowEmergencyModal(false);
      } else {
        alert('❌ Failed to send emergency alert: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Emergency notification error:', error);
      alert('❌ Error sending emergency alert. Please try again.');
    }
  };
  return (
    <>
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Communication Setup</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure communication channels, announcements, and notification systems for your community.
        </p>
      </div>

      {/* Board Communication Channels */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-blue-900 mb-3">🏛️ Board Communication Channels</h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-blue-800">Board of Directors Channel</h5>
              <p className="text-xs text-blue-600">Private channel for board member discussions</p>
            </div>
            <button 
              onClick={() => alert('📢 Board Communication Channel\n\n✅ Private board channel activated!\n• Channel: Board of Directors\n• Privacy: Board Members Only\n• Real-time messaging enabled\n• File sharing available\n\nBoard members can now communicate securely!')}
              className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
              data-theme="primary"
            >
              Configure
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-blue-800">Community Announcements</h5>
              <p className="text-xs text-blue-600">Send important updates to all residents</p>
            </div>
            <button 
              onClick={() => setShowAnnouncementModal(true)}
              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
            >
              Send Announcement
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-blue-800">Emergency Notifications</h5>
              <p className="text-xs text-blue-600">Critical alerts and emergency communications</p>
            </div>
            <button 
              onClick={() => setShowEmergencyModal(true)}
              className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
            >
              Send Emergency Alert
            </button>
          </div>
        </div>
      </div>

      {/* Email Templates */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-green-900 mb-3">📧 Email Templates</h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-green-800">Welcome Email Template</h5>
              <p className="text-xs text-green-600">Customize new resident welcome messages</p>
            </div>
            <button 
              onClick={() => openTemplateEditor('welcome')}
              className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
              data-theme="primary"
            >
              Edit Template
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-green-800">Meeting Notice Template</h5>
              <p className="text-xs text-green-600">Standardized board meeting notifications</p>
            </div>
            <button 
              onClick={() => openTemplateEditor('meeting_notice')}
              className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
              data-theme="primary"
            >
              Edit Template
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-green-800">Payment Reminder Template</h5>
              <p className="text-xs text-green-600">Automated dues and fee reminders</p>
            </div>
            <button 
              onClick={() => openTemplateEditor('payment_reminder')}
              className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
              data-theme="primary"
            >
              Edit Template
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Template Editor Modal */}
    {showTemplateEditor && currentTemplate && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90dvh] overflow-y-auto">
          <h3 className="text-lg font-bold mb-4">
            Edit {currentTemplate.type.replace('_', ' ')} Template
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Subject Line:</label>
              <input
                type="text"
                value={currentTemplate.subject}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content:</label>
              <textarea
                value={currentTemplate.content}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, content: e.target.value }))}
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div className="text-xs text-gray-500 mb-4">
              <p><strong>Available variables:</strong> {'{hoa_name}'}, {'{resident_name}'}, {'{property_address}'}, {'{monthly_dues}'}, {'{due_date}'}</p>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowTemplateEditor(false)}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={saveTemplate}
              className="px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              Save Template
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Community Announcement Modal */}
    {showAnnouncementModal && (
      <AnnouncementModal 
        onClose={() => setShowAnnouncementModal(false)}
        onSend={sendCommunityAnnouncement}
      />
    )}

    {/* Emergency Notification Modal */}
    {showEmergencyModal && (
      <EmergencyModal 
        onClose={() => setShowEmergencyModal(false)}
        onSend={sendEmergencyNotification}
      />
    )}
    </>
  );
};

// Payment Setup Settings Component
const PaymentSetupSettings = () => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Setup</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure Stripe payment processing for dues collection and fee management.
        </p>
      </div>

      {/* Payment Processing Setup */}
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Processing Setup</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure secure payment processing for HOA dues, fees, and assessments using Stripe.
        </p>
        
        {/* Stripe Integration */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-medium text-blue-900 mb-3">💳 Stripe Payment Integration</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-blue-800">Connect Stripe Account</h5>
                <p className="text-xs text-blue-600">Link your Stripe account to accept payments</p>
              </div>
              <button 
                onClick={() => {
                  alert('💳 Stripe Account Connection\n\n✅ Connecting to Stripe...');
                }}
                className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
                data-theme="primary"
              >
                Connect Stripe
              </button>
            </div>
          </div>
        </div>

        {/* Payment Configuration */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="text-md font-medium text-green-900 mb-3">⚙️ Payment Configuration</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-green-800 mb-1">Monthly Dues Amount</label>
                <input 
                  type="number" 
                  placeholder="$250.00"
                  className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-green-800 mb-1">Due Date</label>
                <select className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500">
                  <option>1st of each month</option>
                  <option>15th of each month</option>
                  <option>Last day of month</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-green-800 mb-1">Late Fee</label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <input 
                  type="number" 
                  placeholder="$25.00"
                  className="w-full sm:w-32 px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
                <span className="text-sm text-green-700">applied after</span>
                <div className="flex items-center gap-2">
                  <input 
                    type="number" 
                    placeholder="5"
                    className="w-20 px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                  <span className="text-sm text-green-700">days</span>
                </div>
              </div>
            </div>
            <button 
              onClick={() => {
                const monthlyDues = document.querySelector('input[placeholder="$250.00"]')?.value || '$250.00';
                const dueDate = document.querySelector('select')?.value || '1st of each month';
                const lateFee = document.querySelector('input[placeholder="$25.00"]')?.value || '$25.00';
                const graceDays = document.querySelector('input[placeholder="5"]')?.value || '5';
                
                alert(`⚙️ Payment Configuration Saved\n\n✅ Settings Updated:\n\n💰 Monthly Dues: ${monthlyDues}\n📅 Due Date: ${dueDate}\n🚨 Late Fee: ${lateFee} after ${graceDays} days\n\n📧 Automatic reminders will be sent:\n• 5 days before due date\n• On due date\n• 5 days after due date\n\n💾 All payment settings saved successfully!`);
              }}
              className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
            >
              Save Payment Settings
            </button>
          </div>
        </div>

        {/* Board Member Payment */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h4 className="text-md font-medium text-purple-900 mb-3">👥 Board Member Payment</h4>
          <p className="text-sm text-purple-700 mb-4">
            Board members also need to pay their HOA dues. Set up your own payment method below.
          </p>
          
          {/* Current Balance Display */}
          <div className="bg-white border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center">
              <div>
                <h5 className="text-sm font-medium text-purple-800">Your Current Balance</h5>
                <p className="text-lg font-bold text-purple-900">$250.00 DUE</p>
                <p className="text-xs text-purple-600">Due Date: February 1st, 2025</p>
              </div>
              <div className="text-right">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                  Payment Overdue
                </span>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-purple-800">Pay My Monthly Dues</h5>
                <p className="text-xs text-purple-600">Pay your own HOA dues securely with Stripe</p>
              </div>
              <button 
                onClick={async () => {
                  try {
                    const confirmed = confirm('💳 Board Member Payment\n\nProceed to Stripe checkout?');
                    if (confirmed) {
                      alert('🔄 Processing Payment...');
                    }
                  } catch (error) {
                    alert('❌ Payment failed');
                  }
                }}
                className="primary-cta inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-semibold"
                data-theme="primary"
              >
                Pay My Monthly Dues
              </button>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-purple-800">Auto-Pay Settings</h5>
                <p className="text-xs text-purple-600">Enable automatic monthly payments</p>
              </div>
              <button 
                onClick={() => {
                  const confirmed = confirm('🔄 Auto-Pay Setup\n\nSet up automatic monthly payments of $250.00?\n\n• Payment Date: 25th of each month\n• Payment Method: Credit card ending in 1234\n• Email confirmations: Enabled\n\nSetup auto-pay?');
                  if (confirmed) {
                    alert('✅ Auto-Pay Activated!\n\n🔄 Auto-Pay Configuration:\n• Monthly Payment: $250.00\n• Payment Date: 25th (5 days before due)\n• Next Payment: January 25th, 2025\n• Backup Payment: Card ending in 1234\n• Email Confirmations: Enabled\n\n💾 Auto-Pay is now active!');
                  }
                }}
                className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700"
              >
                🔄 Enable Auto-Pay
              </button>
            </div>
            
            {/* Payment History for Board Member */}
            <div className="bg-white border border-purple-200 rounded-lg p-4 mt-4">
              <h5 className="text-sm font-medium text-purple-800 mb-2">Recent Payment History</h5>
              <div className="space-y-2 text-xs text-purple-600">
                <div className="flex justify-between">
                  <span>December 2024 - $250.00</span>
                  <span className="text-green-600">✅ Paid</span>
                </div>
                <div className="flex justify-between">
                  <span>November 2024 - $250.00</span>
                  <span className="text-green-600">✅ Paid</span>
                </div>
                <div className="flex justify-between">
                  <span>October 2024 - $250.00</span>
                  <span className="text-green-600">✅ Paid</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Setup Guide Modal Component
const SetupGuideModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold text-gray-900">📋 HOA Setup Checklist Guide</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Complete guide for understanding and completing your HOA setup checklist items.
          </p>
        </div>
        
        <div className="max-h-96 overflow-y-auto space-y-6">
          {/* Basic Information */}
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">🏘️ Basic Information</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <div><strong>HOA Name:</strong> The official name of the homeowners association as it appears in legal documents.</div>
              <div><strong>Address:</strong> Physical address of the HOA community or management office.</div>
              <div><strong>Total Properties:</strong> Number of homes/units in the community that pay HOA fees.</div>
              <div><strong>Contact Email:</strong> Primary email for HOA communications and system notifications.</div>
            </div>
          </div>

          {/* Board Members */}
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">👥 Board Members</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <div><strong>Required Roles:</strong> President, Treasurer, Secretary (minimum). Some HOAs also have Vice President, Directors.</div>
              <div><strong>What to collect:</strong> Full name, email address, phone number, role/position.</div>
              <div><strong>Why it matters:</strong> Board members get admin access to manage the HOA portal, approve residents, handle communications.</div>
            </div>
          </div>

          {/* More sections... */}
        </div>

        <div className="mt-6 border-t pt-4">
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Close Guide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Load Quote Component
const LoadQuote = ({ onBack, onLoadSuccess }) => {
  const [quoteId, setQuoteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data } = await api.get(`/api/setup/load-quote/${quoteId}`);
      if (data && data.quote) {
        onLoadSuccess(data.quote);
      } else {
        setError('Quote not found or invalid');
      }
    } catch (error) {
      setError((error.response && error.response.data && error.response.data.detail) || 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Load Existing Quote
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your quote ID to continue the setup process
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}
          
          <div>
            <label htmlFor="quote-id" className="sr-only">
              Quote ID
            </label>
            <input
              id="quote-id"
              name="quoteId"
              type="text"
              required
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="Enter Quote ID (e.g., Q123456789)"
              value={quoteId}
              onChange={(e) => setQuoteId(e.target.value)}
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load Quote'}
            </button>
          </div>
          
          <div className="flex items-center justify-center space-x-4 text-sm">
            <button
              type="button"
              onClick={onBack}
              className="text-gray-600 hover:text-gray-500 font-medium"
            >
              Back to Home
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Wrapper component for HOA Setup Checklist that fetches data by code
const HOASetupChecklistWrapper = ({ hoaCode }) => {
  const [hoaData, setHoaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchHOAData = async () => {
      if (!hoaCode) {
        setError('No HOA code provided');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Fetching HOA data for code:', hoaCode);
        
        // Use the same baseURL as configured for axios
        const backendUrl = axios.defaults.baseURL || process.env.REACT_APP_BACKEND_URL || '';
        const apiUrl = `${backendUrl}/api/hoa/by-code/${hoaCode}`;
        
        console.log('🔍 API URL:', apiUrl);
        
        const { data } = await api.get(`/api/hoa/by-code/${hoaCode}`);
        
        console.log('🔍 Response:', data);
        
        if (data.success) {
          // Transform the data to match the expected format for HOAOnboardingChecklist
          const transformedData = {
            hoa: data.hoa,
            subscription: data.subscription,
            community_resources: data.community_resources
          };
          console.log('🔍 Transformed data:', transformedData);
          setHoaData(transformedData);
        } else {
          setError('HOA not found');
        }
      } catch (err) {
        console.error('❌ Failed to fetch HOA data:', err);
        setError(err.response?.data?.detail || 'Failed to load HOA data');
      } finally {
        setLoading(false);
      }
    };

    fetchHOAData();
  }, [hoaCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading HOA setup checklist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100 mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">HOA Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Please check the HOA code and try again.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <HOASetupSignoffChecklist 
      hoaData={hoaData}
      onComplete={() => {
        // When setup is complete, show success message and offer to go home
        alert('🎉 Setup checklist completed! Your HOA management portal is ready to use.');
        window.location.href = '/';
      }}
    />
  );
};

// Main App Component
// Helper function to check if amenity management add-on is enabled
const hasAmenityManagementAddon = (hoaSettings) => {
  if (!hoaSettings?.subscription) return false;
  
  // Check if addons array exists and includes amenity_management
  if (Array.isArray(hoaSettings.subscription.addons) && 
      hoaSettings.subscription.addons.includes('amenity_management')) {
    return true;
  }
  
  // Check if plan.add_ons exists (Stripe format)
  if (hoaSettings.subscription.plan?.add_ons && Array.isArray(hoaSettings.subscription.plan.add_ons)) {
    return hoaSettings.subscription.plan.add_ons.some(addon => 
      addon.addon_id === 'amenity_management' || addon.id === 'amenity_management'
    );
  }
  
  return false;
};

const HOAApp = () => {
  // Check URL for specific routes like /setup-checklist/{hoa_code}
  const getInitialView = () => {
    const path = window.location.pathname;
    if (path.startsWith('/setup-checklist/')) {
      return 'setup-checklist';
    }
    if (path === '/login') {
      return 'existing-login';
    }
    if (path === '/register') {
      return 'register';
    }
    return 'landing';
  };
  
  const [currentView, setCurrentView] = useState(getInitialView()); // 'landing', 'setup-hoa', 'existing-login', 'register', 'load-quote', 'setup-checklist'
  const [showRegistration, setShowRegistration] = useState(false);
  const [loadedQuoteData, setLoadedQuoteData] = useState(null);
  const [hoaCode, setHoaCode] = useState(() => {
    const path = window.location.pathname;
    if (path.startsWith('/setup-checklist/')) {
      return path.split('/setup-checklist/')[1];
    }
    return null;
  });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showConfidentialityModal, setShowConfidentialityModal] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);

  const { user, loading, impersonationMode, startImpersonation, endImpersonation, logout, forcePasswordChange } = useAuth();

  // Update currentView when user authentication completes
  useEffect(() => {
    if (!loading) {
      if (user && user.id && localStorage.getItem('hoa_token')) {
        // User is authenticated - don't interfere with rendering, let dashboard condition handle it
        console.log('🔑 User authenticated - letting dashboard condition handle rendering');
      } else if (!user && currentView !== 'landing' && currentView !== 'existing-login' && currentView !== 'register' && currentView !== 'setup-hoa' && currentView !== 'load-quote') {
        // If user is not authenticated and we're not on a public page, go to landing
        setCurrentView('landing');
      }
    }
  }, [user, loading, currentView]);

  // Wrapper logout function that also resets currentView
  const handleLogout = React.useCallback(() => {
    logout();
    setCurrentView('landing');
  }, [logout]);

  // Debug: Log current state
  console.log('HOAApp render - currentView:', currentView, 'user:', user?.email || 'none', 'loading:', loading);

  // Handle different views based on currentView state - using useCallback to ensure stable reference
  const handleNavigation = React.useCallback((view) => {
    console.log('🧭 handleNavigation called with view:', view);
    console.log('🧭 Current currentView before update:', currentView);
    setCurrentView(view);
    console.log('🧭 setCurrentView called with:', view);
    setShowRegistration(false);
    
    // Add a timeout to check if state actually updated
    setTimeout(() => {
      console.log('🧭 State check after timeout - currentView should now be:', view);
    }, 100);
  }, [currentView]);

  const handleSetupComplete = (userType) => {
    // After HOA setup is complete, redirect to appropriate login
    setCurrentView('existing-login');
  };

  // Redirect to change-password page if user must change password
  useEffect(() => {
    if (!loading && user && localStorage.getItem('hoa_token')) {
      if (user.must_change_password) {
        setCurrentView('change-password');
        setShowConsentModal(false); // Don't show consent modal during password change
      }
    }
  }, [loading, user?.must_change_password]);

  // Check for electronic consent after password change
  useEffect(() => {
    if (!loading && user && localStorage.getItem('hoa_token') && !user.must_change_password) {
      // Check if user needs to give consent
      const hasConsent = user.electronic_consent && user.electronic_consent.consented;
      
      console.log('🔍 Consent check:', {
        userEmail: user.email,
        hasConsent,
        electronic_consent: user.electronic_consent,
        showConsentModal,
        needsConsent,
        loading,
        must_change_password: user.must_change_password
      });
      
      if (!hasConsent) {
        // User hasn't given consent - MUST show modal
        // Do NOT set any flag - modal must block access until consent is given
        console.log('⚠️ No consent found - showing modal');
        setNeedsConsent(true);
        setShowConsentModal(true);
      } else {
        // User already has consent - no need to show modal
        console.log('✅ Consent already given');
        setNeedsConsent(false);
        setShowConsentModal(false);
      }

      // Check confidentiality consent requirement for board/team members
      const requiresRoles = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 
                             'maintenance', 'security', 'staff', 'attorney', 'accountant', 'team_member', 'committee_member'];
      
      // CRITICAL: Also check if user is on ANY committee, regardless of role
      const checkConfidentialityConsent = async () => {
        let needsConsent = requiresRoles.includes(user?.role?.toLowerCase()) || user?.requires_confidentiality_consent;
        
        // Check if user is on any committee
        if (!needsConsent && user?.hoa_id) {
          try {
            const committeesResp = await api.get(`/api/hoa/${user.hoa_id}/committees`);
            const committeesList = committeesResp.data?.committees || [];
            const isOnAnyCommittee = committeesList.some(c => 
              Array.isArray(c.members) && c.members.some(m => 
                (m.user_id === user.id) || 
                (m.id === user.id) || 
                (m.email && user?.email && m.email.toLowerCase() === user.email.toLowerCase())
              )
            );
            
            if (isOnAnyCommittee) {
              needsConsent = true;
              console.log('🔒 User is on a committee - confidentiality consent required');
            }
          } catch (err) {
            console.error('Error checking committee membership:', err);
          }
        }
        
        if (needsConsent) {
          // Check if already signed
          api.get('/api/confidentiality/status').then(({ data }) => {
            if (!data.has_signed) {
              console.log('⚠️ Confidentiality consent not signed - showing modal');
              setShowConfidentialityModal(true);
            }
          }).catch(err => console.error('Error checking confidentiality consent:', err));
        }
      };
      
      checkConfidentialityConsent();
    }
  }, [loading, user, user?.must_change_password, user?.electronic_consent]);

  const handleLoadQuoteSuccess = (quoteData) => {
    // When quote is loaded successfully, redirect to setup with pre-filled data
    setLoadedQuoteData(quoteData);
    setCurrentView('setup-hoa');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Special case: Setup checklist route should always show checklist regardless of user role
  if (currentView === 'setup-checklist') {
    return <HOASetupChecklistWrapper hoaCode={hoaCode} />;
  }

  // Show Platform Admin Dashboard for platform admins and employees (but not during setup-checklist)
  if (user?.role === 'platform_admin' || user?.role === 'platform_employee') {
    return (
      <HOAProvider>
        <div className="min-h-screen bg-gray-100">
          {impersonationMode && (
            <div className="bg-yellow-100 border-b border-yellow-200 px-4 py-2">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-yellow-800">
                    Impersonation Mode: You are viewing as {user?.first_name} {user?.last_name} ({user?.email})
                  </span>
                </div>
                <button
                  onClick={endImpersonation}
                  className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                >
                  Exit Impersonation
                </button>
              </div>
            </div>
          )}
          {impersonationMode ? <Dashboard subscriptionTier={subscriptionTier} /> : <PlatformAdminDashboard user={user} onImpersonate={startImpersonation} onExitAdmin={handleLogout} />}
        </div>
      </HOAProvider>
    );
  }

  // Show Dashboard for authenticated users (only if they have a valid token and not loading)
  const hasValidToken = !!localStorage.getItem('hoa_token');
  const shouldShowDashboard = !loading && hasValidToken && user && user.id && ((user && user.role) !== 'platform_admin' || impersonationMode) && !user?.must_change_password;
  
  // Debug logging for dashboard condition
  console.log('🔍 Dashboard condition check:', {
    loading,
    hasUser: !!user,
    hasUserId: !!user?.id,
    hasToken: hasValidToken,
    userRole: user?.role,
    isPlatformAdmin: user?.role === 'platform_admin',
    impersonationMode,
    mustChangePassword: user?.must_change_password,
    needsConsent,
    shouldShowDashboard,
    currentView
  });
  
  if (shouldShowDashboard) {
    console.log('🎯 Rendering Dashboard for authenticated user', {
      showConsentModal,
      needsConsent,
      user_has_consent: user?.electronic_consent
    });
    return (
      <>
        <HOAProvider>
          <Dashboard />
        </HOAProvider>
        {(showConsentModal && needsConsent) && (
          <ElectronicConsentModal
            onSuccess={() => {
              setShowConsentModal(false);
              setNeedsConsent(false);
              // Reload to refresh user data with consent
              // After reload, user.electronic_consent.consented will be true
              // and modal won't show again
              window.location.reload();
            }}
            onSkip={null} // No skip option - consent is required
          />
        )}
        {showConfidentialityModal && (
          <ConfidentialityConsentModal
            user={user}
            onClose={() => setShowConfidentialityModal(false)}
            onSigned={() => {
              setShowConfidentialityModal(false);
              window.location.reload();
            }}
          />
        )}
      </>
    );
  }
  
  // Show loading state while authenticating
  if (loading) {
    console.log('🔄 Showing loading state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Debug: Log which view we're rendering
  console.log('Rendering view:', currentView);

  // Show different views based on current state
  switch (currentView) {
    case 'landing':
      console.log('Rendering LandingPage with handleNavigation:', typeof handleNavigation);
      return <LandingPage onNavigate={handleNavigation} />;
    
    case 'setup-hoa':
      return (
        <EnhancedHOASetupFlow 
          onBack={() => setCurrentView('landing')}
          onComplete={handleSetupComplete}
          loadedQuoteData={loadedQuoteData}
        />
      );
    
    case 'load-quote':
      return (
        <LoadQuote 
          onBack={() => setCurrentView('landing')}
          onLoadSuccess={handleLoadQuoteSuccess}
        />
      );
    
    case 'existing-login':
      console.log('Rendering LoginForm');
      return (
        <div>
          <LoginForm 
            onBack={() => setCurrentView('landing')} 
            onSuccess={() => {
              // User successfully authenticated - update view state instead of reloading
              console.log('Login success callback triggered - updating view to dashboard');
              setCurrentView('dashboard');
            }}
            showRegistrationOption={true}
            mustChangePassword={user?.must_change_password}
            onPasswordChanged={() => {}}
          />
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setCurrentView('register')}
              className="text-indigo-600 hover:text-indigo-500 text-sm font-medium mr-4"
            >
              Register as Homeowner
            </button>
            <button
              onClick={() => setCurrentView('landing')}
              className="text-gray-600 hover:text-gray-500 text-sm font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    
    case 'register':
      return (
        <LoginForm 
          onBack={() => setCurrentView('landing')} 
          onSuccess={() => {
            // Force a page refresh to reset state and show dashboard
            console.log('Register/Login success callback triggered - forcing page reload');
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }}
          showRegistrationOption={false}
        />
      );

    case 'change-password':
      return (
        <ChangePasswordPage 
          onSuccess={() => {
            // After password change, reload to get updated user data and trigger consent check
            window.location.reload();
          }} 
          onCancel={() => setCurrentView('existing-login')}
        />
      );
    
    case 'setup-checklist':
      return <HOASetupChecklistWrapper hoaCode={hoaCode} />;
    
    case 'dashboard':
      console.log('Rendering Dashboard for authenticated user');
      return (
        <HOAProvider>
          <Dashboard />
        </HOAProvider>
      );
    
    default:
      console.log('Rendering default LandingPage');
      return <LandingPage onNavigate={handleNavigation} />;
  }
};

// Marketing pages wrapper with navigation
const MarketingLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header/Navigation for marketing pages */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="/hoa-logo-icon.png" 
                alt="HOA Manager Pro" 
                className="h-10 w-10 object-contain"
              />
              <div className="ml-3 flex flex-col">
                <span className="text-xl font-bold tracking-wide leading-tight" style={{ color: '#5A2D82', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  HOA
                </span>
                <span className="text-sm font-bold tracking-wide leading-none" style={{ color: '#5A2D82', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  MANAGER PRO
                </span>
              </div>
            </Link>
            
            <div className="hidden md:flex space-x-8">
              <Link to="/features" className="text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium">
                Features
              </Link>
              <Link to="/security" className="text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium">
                Security
              </Link>
              <Link to="/about" className="text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium">
                About
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium">
                Contact
              </Link>
              <Link to="/signin" className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">
                Sign In
              </Link>
            </div>
          </div>
        </nav>
      </header>
      
      {/* Main content */}
      <main>{children}</main>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><Link to="/features" className="text-gray-300 hover:text-white">Features</Link></li>
                <li><Link to="/security" className="text-gray-300 hover:text-white">Security</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact Us</Link></li>
                <li><Link to="/training" className="text-gray-300 hover:text-white">Training</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-300 hover:text-white">About</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-300 hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">HOA Manager Pro</h3>
              <p className="text-gray-300 text-sm">
                Modern HOA management for thriving communities.
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center text-gray-400">
            <p>&copy; 2025 HOA Manager Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Route wrapper to detect marketing vs app routes
const AppRouter = () => {
  const { user } = useAuth();
  const location = useLocation();
  const marketingRoutes = ['/about', '/features', '/security', '/help', '/contact', '/training', '/privacy', '/terms'];
  
  // Initialize multi-HOA context when user logs in or refreshes
  // This component doesn't render anything, just manages context
  const multiHOAInit = <MultiHOAInitializer user={user} />;
  
  // Check if it's a public HOA page route
  if (location.pathname.startsWith('/p/')) {
    const slug = location.pathname.split('/p/')[1];
    
    // Check if it's the payment page
    if (slug.includes('/pay')) {
      const actualSlug = slug.replace('/pay', '');
      return (
        <>
          {multiHOAInit}
          <ExternalPaymentPage slug={actualSlug} />
        </>
      );
    }
    
    return (
      <>
        {multiHOAInit}
        <PublicHOA slug={slug} />
      </>
    );
  }
  
  // Alternative route: /public/{slug}/pay
  if (location.pathname.startsWith('/public/') && location.pathname.endsWith('/pay')) {
    const slug = location.pathname.split('/public/')[1].replace('/pay', '');
    return (
      <>
        {multiHOAInit}
        <ExternalPaymentPage slug={slug} />
      </>
    );
  }

  // Guest File Access Page
  if (location.pathname.startsWith('/guest-file-access')) {
    return (
      <>
        {multiHOAInit}
        <Routes>
          <Route path="/guest-file-access" element={<GuestFileAccess />} />
        </Routes>
      </>
    );
  }

  // Poll Volunteer Page
  if (location.pathname.startsWith('/poll-volunteer/')) {
    return (
      <>
        {multiHOAInit}
        <Routes>
          <Route path="/poll-volunteer/:accessToken" element={<PollVolunteerPage />} />
        </Routes>
      </>
    );
  }

  // Sign In Page (standalone, not wrapped in MarketingLayout)
  if (location.pathname === '/signin') {
    return (
      <>
        {multiHOAInit}
        <Routes>
          <Route path="/signin" element={<SignIn />} />
        </Routes>
      </>
    );
  }

  // Signup Success Page (Stripe redirect after payment)
  if (location.pathname === '/signup-success') {
    return (
      <>
        {multiHOAInit}
        <Routes>
          <Route path="/signup-success" element={<HOASignupSuccess />} />
        </Routes>
      </>
    );
  }

  // External Payment Success Page
  if (location.pathname === '/external-payment-success') {
    return (
      <>
        {multiHOAInit}
        <Routes>
          <Route path="/external-payment-success" element={<ExternalPaymentSuccess />} />
        </Routes>
      </>
    );
  }
  
  // If it's a marketing route, show marketing layout
  if (marketingRoutes.includes(location.pathname)) {
    return (
      <>
        {multiHOAInit}
        <MarketingLayout>
          <Routes>
            <Route path="/about" element={<About />} />
            <Route path="/features" element={<Features />} />
            <Route path="/security" element={<Security />} />
            <Route path="/help" element={<Help />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/training" element={<Training />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </MarketingLayout>
      </>
    );
  }
  
  // Otherwise show the main HOA app
  return (
    <>
      {multiHOAInit}
      <HOAApp />
    </>
  );
};

// Import MultiHOAProvider
import { MultiHOAProvider } from './MultiHOAContext';

// Export the main app wrapped with providers and router
export default function App() {
  return (
    <Router>
      <AuthProvider>
        <MultiHOAProvider>
          <AppRouter />
        </MultiHOAProvider>
      </AuthProvider>
    </Router>
  );
}